import {
  getEffectiveHovTargetDirectMpAssignments,
  getEffectiveHovGainUpgradeDirectMpAssignments,
} from "./directMpAssignments";
import {
  collectMpmNumericSettingsChanges,
  type MpmNumericSettingChange,
} from "../mpm/mpmNumericSettingsChanges";
import type { Updatable } from "@/shared/contracts/lifecycle";
import {
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  isNonNegativeHuge,
  maxHuge,
  ceilHugeAtLeast,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";
import { TIM_HOV_ACTIVATION_EFFICIENCY_DEFAULT } from "@/features/achievements/achievementEffects";
import {
  applyAchievementPersistentEffects,
  type HoverIdleAchievementPersistentEffects,
} from "./achievementPersistentEffectApplier";
import { applyHovSkillEffects } from "./hovSkillEffectApplier";
import {
  composeHovGainEffects,
  NEUTRAL_HOV_GAIN_EFFECT_INTENT,
  type HovGainEffectSourceIntent,
} from "./hovGainEffectComposer";
import {
  NOT_FULLY_FREE_HG_COST_DIVISOR_THRESHOLD,
  isMassiveMergeHovRatioMet,
} from "@/features/achievements/normalAchievementDefinitions";
import type { SupportedLocale } from "@/i18n/runtime";
import type { Notation } from "@/shared/format/numberFormat";
import type { HelpTopicId } from "@/features/help/helpTopics";
import {
  resolveEntitlementClaim,
  resolveEntitlementGrant,
  resolvePermanentItemPurchase,
  resolveRewardCodeRedeem,
  withPermanentItemGrantedFromBundle,
} from "@/features/payment/paymentStateCommands";
import {
  cloneEntitlementSnapshot,
  createEntitlementSnapshotFromPaymentState,
  mergeEntitlementSnapshots,
} from "@/features/payment/entitlementSnapshot";
import { DEFAULT_ENTITLEMENTS } from "@/features/payment/types";
import type {
  EntitlementGrantResult,
  BundleEntitlementKey,
  EntitlementSnapshot,
  HoverIdlePaymentState,
  PermanentPurchaseId,
  PermanentPurchaseResult,
  RewardCodeRedeemResult,
} from "@/features/payment/types";
import type {
  HoverIdleConfig,
  HoverIdleCumulativeStatsState,
  HoverIdleGridTier,
  HoverIdleModelState,
  HoverIdleSnapshot,
  HoverIdleSettingsState,
  AchievementNotificationMode,
  WallNotificationMode,
  HoverIdleUnlockState,
  HovGainUpgradeMpTarget,
  HovCellGainUpgradeIndex,
  HoverChargeDuration,
  HoverToggleDuration,
  TimUpgradePurchaseResult,
  HovUpgradePurchaseResult,
  HovAmplifierUpgradePurchaseResult,
  HovGainUpgradeBulkPurchaseCommandResult,
  HovGainUpgradeBulkPurchaseResult,
  HovGainUpgradeOneCostBatchPreview,
  HovGainUpgradeOneCostBatchSummary,
  HovGainUpgradeSimultaneousPurchasePreview,
  AmplifierUpgradeGuardMs,
  MHovExecuteBeforeSnapshot,
  MHovState,
  MHovSnapshot,
  MHovAnimationSpeed,
  MHovChargeDurationMs,
  NHovAnimationSpeed,
  NHovBranchExperienceByBranch,
  NHovBranchId,
  NHovEffectBoundaryCaps,
  NHovBranchLevels,
  NHovChargeDurationMs,
  NHovState,
  OHovChargeDurationMs,
  OHovState,
  HoverIdleResetAutomationState,
} from "../types";
import {
  advanceResetAutomationMinimumGameplayIntervalCarry,
  cloneResetAutomationState,
  consumeResetAutomationMinimumGameplayInterval,
  createDefaultResetAutomationState,
  isResetAutomationMinimumGameplayIntervalSatisfied,
  normalizeResetAutomationState,
  resolveResetAutomationMinimumGameplayIntervalRemainingMs,
} from "./resetAutomationMinimumIntervals";
import {
  isMHovChargeDurationMs,
  isNHovAnimationSpeed,
  isNHovChargeDurationMs,
  isOHovChargeDurationMs,
  isAmplifierUpgradeGuardMs,
  resolveHoverChargeDurationMs,
  resolveHoverToggleDurationMs,
} from "./hoverIdleSettingsValues";
import {
  normalizeNHovThemeHue,
  resolveNHovCandidateOffer,
} from "../nHov/nHovCandidateTheme";
import {
  canExecuteMHov,
  computeMHovExecuteOutcome,
  createDefaultMHovState,
  resolveMHovRewardMultiplier,
  resolveMinRunBaseHovForMHovRewardMultiplier,
  type MHovExecuteOutcome,
  type ResolveMHovRewardMultiplierOptions,
} from "../mHov/mHovLogic";
import { NUMBER_LIMIT_BOUNDARY } from "./numberLimitBoundary";
import {
  canExecuteNHovBranch,
  createDefaultNHovState,
  resolveNHovEffectLevels,
  resolveProspectiveNHovBranchEffects,
} from "../nHov/nHovLogic";
import { resolveHgDiscountBoundaryReexecutionState } from "../nHov/hgDiscountBoundaryReexecution";
import {
  resolveNHovBranchContinuousLevelsFromExperience,
  resolveNHovBranchLevelsFromExperience,
} from "../nHov/nHovGrowthLogic";
import { resolveFreshnessWallNHovStalingExponent } from "@/features/hoverIdle/wall/freshnessWallLogic";
import {
  canExecuteOHov,
  createDefaultOHovState,
  isOHovUnlocked,
  resolveBuiltInOHovAutoExecutionTarget,
  resolveOHovHovCoreAwardedGain,
  resolveOHovHovCoreBaseGainForHov,
  resolveOHovMpAutoExecutionCandidateTargetHov,
  resolveOHovMpAutoExecutionGate,
  type BuiltInOHovAutoExecutionTarget,
  type OHovExecutionSource,
} from "../oHov/oHovLogic";
import {
  captureMpmAssignmentIntent,
  cloneMpmState,
  createDefaultMpmState,
  normalizeMpmState,
  overlayMpmAssignmentIntent,
  reconcileMpmAssignmentsForCapacity,
  withMpmAmplifierPanelsUnlocked,
  withMpmAmplifierUpgradesUnlocked,
  withMpmAssignAllUnlockedLatched,
  withMpmHovGridUnlockLatched,
  withMpmMHovExecutionUnlocked,
  withMpmNHovExecutionUnlocked,
  withMpmOHovExecutionUnlocked,
  normalizeMpmMHovExecutionMinimumResetIntervalMs,
  normalizeMpmMHovExecutionMultiplier,
  normalizeMpmNHovExecutionMinimumEffect,
  normalizeMpmNHovExecutionMinimumTimeSeconds,
  normalizeMpmOHovExecutionMinimumHovCore,
  MPM_PRESET_BASE_SLOT_COUNT,
  MPM_PRESET_MAX_SLOT_COUNT,
  type MpmAssignmentIntent,
  type MpmCapacityReconcileReason,
  type MpmState,
} from "../mpm/mpmState";
import { withSavedMpmPreset } from "../mpm/mpmPresets";
import {
  composeMpmUnlockAdoption,
  createNeutralMpmUnlockIntent,
  type MpmUnlockIntent,
} from "../mpm/mpmUnlockAdoption";
import { shouldLatchMpmAssignAllUnlocked } from "../mpm/mpmAssignAll";
import {
  resolveAssignAllMpmAmplifierPanels,
  resolveAssignAllMpmExploration,
  resolveAssignAllMpmHovGainUpgrades,
  resolveAssignAllMpmHovTargets,
  resolveAssignMpToAmplifierPanel,
  resolveAssignMpToAmplifierUpgrade,
  resolveAssignMpToHovGainUpgrade,
  resolveAssignMpToHovTarget,
  resolveAssignMpToMHovExecution,
  resolveAssignMpToNHovExecution,
  resolveAssignMpToOHovExecution,
  resolveClearMpmAmplifierPanelAssignments,
  resolveClearMpmAmplifierUpgradeAssignments,
  resolveClearMpmExplorationAssignments,
  resolveClearMpmHovGainUpgradeAssignments,
  resolveClearMpmHovTargetAssignments,
  resolveRemoveMpFromAmplifierPanel,
  resolveRemoveMpFromAmplifierUpgrade,
  resolveRemoveMpFromHovGainUpgrade,
  resolveRemoveMpFromHovTarget,
  resolveRemoveMpFromMHovExecution,
  resolveRemoveMpFromNHovExecution,
  resolveRemoveMpFromOHovExecution,
  type MpmAssignmentCommandResult,
} from "../mpm/mpmAssignmentCommands";
import {
  computeMpmAvailableMpFromOccupancy,
  getActiveMpmAmplifierPanelAssignments,
} from "../mpm/mpmCapacity";
import {
  getExplorationAssignedMpByBiome,
  reconcileExplorationAssignmentsToPurchasedSlots,
  resolveAssignExplorationMpSlot,
  resolvePurchaseExplorationMpSlot,
  resolveRemoveExplorationMpSlot,
} from "../exploration/explorationCommands";
import { DEFAULT_EXPLORATION_DEFINITION } from "../exploration/explorationDefinitions";
import {
  createExplorationDetailsSnapshot,
  createExplorationSnapshot,
} from "../exploration/explorationSnapshot";
import {
  advanceExploration,
  resolveNextExplorationCompletionElapsedMs,
} from "../exploration/explorationProgression";
import { resolveExplorationTimAccelerationPlan } from "../exploration/explorationTimAcceleration";
import {
  cloneExplorationState,
  createDefaultExplorationState,
} from "../exploration/explorationState";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationAreaSnapshot,
  type ExplorationBiomeId,
  type ExplorationDefinition,
  type ExplorationDetailsSnapshot,
  type ExplorationModelCommandResult,
  type ExplorationMpSlotId,
  type ExplorationOperationAvailability,
  type ExplorationSnapshot,
  type ExplorationState,
} from "../exploration/explorationTypes";
import {
  DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  UPGRADE_HOVER_REPEATER_MIN_INTERVAL_MS,
} from "../upgrades/upgradeRepeatTiming";
import {
  UPGRADE_HOVER_REPEATER_DEFAULT_DECAY,
  advanceUpgradeRepeatCadenceAggregated,
  advanceUpgradeRepeatCadenceStarved,
  createUpgradeRepeatCadenceState,
  resetUpgradeRepeatCadenceState,
  type UpgradeRepeatCadenceConfig,
  type UpgradeRepeatCadenceState,
} from "../upgrades/upgradeRepeatCadence";
import {
  toHovGainUpgradeMpTargetKey,
  type HovGainUpgradeMpTargetKey,
} from "../mpm/mpmHovGainUpgradeTargets";
import {
  doesNHovHgDiscountCostDivisorReachNumberLimit,
  resolveNHovBranchEffects,
  type NHovBranchEffects,
  type NHovBranchGrowthExponentBonus,
} from "../nHov/nHovBranchEffects";
import {
  isNHovBranchId,
  NHOV_BRANCH_IDS,
  normalizeNHovContinuousEffectProgressByBranch,
  normalizeNHovEffectBoundaryCaps,
} from "../nHov/nHovBranches";
import { calculateNHovHgCostDivisorHuge } from "../nHov/hgDiscountLogic";
import { calculateHovCellRankMultiplierHuge } from "../nHov/hngScaleLogic";
import {
  resolveHovResetBalance,
  type HovResetBalance,
  type HovResetBalanceOverrides,
} from "@/features/hoverIdle/balance/hovResetBalance";
import {
  isHovSkillHoldMs,
  type HovSkillHoldMs,
} from "@/features/hoverIdle/hov/hovSkillDefinitions";
import { getWallFirstClearReward } from "@/features/hoverIdle/wall/wallDefinitions";
import {
  isWallStartChargeMs,
  type WallStartChargeMs,
} from "@/features/hoverIdle/wall/wallStartCharge";
import {
  resolveApExponentBonusBreakdown,
  resolveOHovHovCoreGainMultiplier,
  type HovSkillResolvedEffects,
  type MhovNHovGrowthExponentBonus,
} from "@/features/hoverIdle/hov/hovSkillEffects";
import {
  createDefaultHovTabUnlockState,
  resolveHovTabUnlockState,
} from "@/features/hoverIdle/unlocks/hovTabUnlocks";
import {
  HOV_AMPLIFIER_ENTRY_DEFINITIONS,
  applyHovAmplifierRaPropagationReward,
  applyHovAmplifierEnhancementPurchase,
  createEmptyHovAmplifierGaugeVisualSegments,
  createEmptyHovAmplifierRecentChargeSources,
  createEmptyHovAmplifierRecentDirectChargeAtMs,
  createInitialHovAmplifierState,
  isHovAmplifierEntryPurchasable,
  isHovAmplifierEntryMpAssignable,
  isHovAmplifierEntryPlayerVisible,
  isHovAmplifierEntryUnlocked,
  normalizeHovAmplifierState,
  resolveHovAmplifierHgMultiplier,
  resolveHovAmplifierUpgradeCostForEntry,
  type HovAmplifierEntryId,
  type HovAmplifierEntryAvailabilityContext,
  type HovAmplifierGaugeVisualChargeBatch,
  type HovAmplifierGaugeVisualSegments,
  type HovAmplifierRecentChargeSources,
  type HovAmplifierRecentDirectChargeAtMs,
  type HovAmplifierState,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import { resolveHovAmplifierTick } from "@/features/hoverIdle/hov/hovAmplifierTickResolver";
import {
  applyHoverIdleEffectUnlocks,
  createDefaultHoverIdleEffectUnlockState,
  HOV_GRID_INCREASE_1_TIER,
  HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
  HOV_GRID_INCREASE_2_TIER,
  HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
  resolveHoverIdleEffectUnlockState,
} from "@/features/hoverIdle/unlocks/hoverIdleEffectUnlocks";
import {
  addUnseenClearedWall,
  advanceWallRunElapsedInState,
  beginWallRunInState,
  clearActiveWallRunInState,
  clearUnseenClearedWall,
  cloneWallState,
  createDefaultWallState,
  incrementStockWallNhovUsedCountInState,
  isWallActive,
  normalizeWallState,
  resetFreshnessElapsedInState,
  type WallState,
} from "@/features/hoverIdle/wall/wallState";
import { type WallId } from "@/features/hoverIdle/wall/wallDefinitions";
import type { WallRewardCapLevel } from "@/features/hoverIdle/wall/wallRewardCap";
import {
  getClearedWallKindCount,
  getFreshnessElapsedSinceHovRunMs,
  getFreshnessWallRewardExponentBonus,
  getHgWallRewardExponentBonus,
  getWallChallengePolicy,
  hasAmplificationWallClear,
  hasStockWallFirstNhovBranchSelectionReward,
  isFreshnessWallActive,
  isHgWallActive,
  isHovCellUnlockBlockedByActiveWall,
  isHovGainUpgradeDirectMpBlockedByActiveWall,
  isNHovBranchBlockedByActiveWall,
  isSecondHovGridIncreaseBlockedByActiveWall,
  resolveActiveWallClearTargetHov,
  resolveActiveWallAmplifierChallenge,
  resolveAmplificationWallRaPropagationRewardEffect,
  resolveAggregateWallRewardAddend,
  resolveAggregateWallRewardMultiplier,
  resolveOuterWallBuiltInOHovAutoTargetHov,
} from "@/features/hoverIdle/wall/wallChallengePolicy";
import {
  HG_WALL_CONSTRAINT_HG_DIVISOR,
  HG_WALL_CONSTRAINT_HG_EXPONENT,
} from "@/features/hoverIdle/wall/hgWallLogic";
import {
  DEFAULT_HOV_TIM_BALANCE_TOTAL,
  defaultCumulativeStats,
  defaultHiddenGrowth,
  defaultHoverIdleConfig,
  defaultHovTimBalance,
  defaultPayment,
  defaultPrestige,
  defaultSettings,
} from "./hoverIdleDefaults";
import {
  areEntitlementSnapshotsEqual,
  cloneHovCells,
  cloneNHovState,
  cloneOHovState,
  clonePaymentState,
  cloneSettingsState,
  cloneTimCells,
  withUpdatedPaymentState,
} from "./hoverIdleStateClone";
import {
  assertFiniteNonNegative,
  assertFinitePositive,
  assertHoverIdleConfig,
  assertHoverIdleCumulativeStatsState,
  assertHoverIdleEconomyState,
  assertHoverIdlePaymentState,
  assertHoverIdleSettingsState,
  assertNonNegativeInteger,
} from "./hoverIdleStateValidation";
import {
  HOV_CELL_BASE_COSTS,
  HOV_CELL_COUNT,
  HOV_CELL_GAIN_UPGRADE_COST_MULTIPLIER,
  HOV_CELL_GAIN_MULTIPLIER_INCREMENT,
  HOV_CELL_UNLOCK_INITIAL_MULTIPLIER,
  TIM_CELL_BASE_COSTS,
  TIM_CELL_COUNT,
  TIM_CELL_GAIN_MULTIPLIER_INCREMENT,
  TIM_CELL_UNLOCK_INITIAL_MULTIPLIER,
  TIM_CELL_UPGRADE_COST_MULTIPLIER,
  createDefaultHovGainUnlockCosts,
  createDefaultHovCells,
  createDefaultTimCells,
  type HovCellState,
  type HovTimBalanceState,
  type TimCellState,
} from "./hoverIdleState";
import {
  calculateUpgradeMilestoneCount,
  calculateUpgradeMilestoneMultiplier,
} from "./upgradeMilestones";
import {
  computeActiveTimHoverGain,
  computeHiddenGainGrowthPerSecond,
  computePassiveResourceGain,
  computeTimToHovRefinement,
} from "./hoverIdleTickMath";
import {
  BHG_TO_HG_POWER_EXPONENT_BASE,
  resolveHovGainPipeline as resolvePureHovGainPipeline,
  type HovGainPipelineInput,
  type HovGainPipelineResult,
} from "./hovGainPipeline";
import {
  addUnassignedHovTimBalancePoints,
  advanceHovTimGrowthProgress,
  assignHovTimBalancePoint,
  setHovTimBalancePairRatio,
  unassignHovTimBalancePoint,
  type HovTimBalancePair,
  type HovTimBalanceTarget,
} from "./hovTimBalanceLogic";
import {
  createOneCostBulkPurchaseExactSummary,
  createOneCostBulkPurchaseLightweightSummary,
} from "@/features/hoverIdle/upgrades/oneCostBulkPurchase";
import {
  resolveHovGainUpgradeAutomationSegmentPlan,
  resolveHovGainUpgradeFirstPurchaseThreshold,
  resolveHovGainUpgradePurchasePlan,
  toBulkMilestoneRecords,
  type HovGainUpgradePurchaseMode,
  type HovGainUpgradePurchasePlan,
  type HovGainUpgradePurchasePlannerInput,
} from "@/features/hoverIdle/upgrades/hovGainUpgradePurchasePlanner";
import { resolveSecretProgressTimers } from "./secretProgressLogic";
import {
  resolveMHovResetPlan,
  resolveNHovResetPlan,
  resolveOHovResetPlan,
  type HoverResetPlan,
  type HoverResetProgressScope,
  type MHovResetPlan,
  type NHovResetPlan,
  type OHovResetPlan,
} from "./hoverResetPlans";
import {
  applyHoverResetProgressScope as applyHoverResetProgressScopeToGroups,
  applyMHovResetGrant as applyMHovResetGrantToPatch,
  applyNHovResetGrant as applyNHovResetGrantToPatch,
  applyOHovResetGrant as applyOHovResetGrantToPatch,
  type HoverResetProgressStateGroups,
} from "./hoverResetStateApplier";
import { normalizeHoverIdleModelRestoreState } from "./hoverIdleModelRestore";
import {
  buildHoverIdleMHovSnapshot,
  buildHoverIdleSnapshot,
  type HoverIdleSnapshotBuildState,
  type HoverIdleSnapshotLightweightDerivations,
  type HoverIdleSnapshotPreparation,
  type HoverIdleSnapshotReadHelpers,
  type HoverIdleSnapshotSyncedState,
  type HoverIdleSnapshotTransientState,
} from "./hoverIdleSnapshotBuilder";

export { defaultHoverIdleConfig } from "./hoverIdleDefaults";
export { BHG_TO_HG_POWER_EXPONENT_BASE } from "./hovGainPipeline";
export type {
  HovTimBalancePair,
  HovTimBalanceTarget,
} from "./hovTimBalanceLogic";
export {
  HOV_O_LABEL_HOVER_TARGET_SECONDS,
  OBSERVER_TARGET_SECONDS,
  X_PATTERN_CELL_INDICES,
} from "./secretProgressLogic";

export const HOV_CELL_RAPID_CLICK_WINDOW_MS = 10_000;
export const HOV_CELL_RAPID_CLICK_TARGET_COUNT = 10;

export const PASSIVE_RESOURCE_BALANCE_BASE = DEFAULT_HOV_TIM_BALANCE_TOTAL;
export const HIDDEN_HOV_GAIN_GROWTH_PER_ALLOCATION_PER_SECOND = 0.002;
export const HIDDEN_TIM_GAIN_GROWTH_PER_ALLOCATION_PER_SECOND = 0.0004;
export const HOV_TIM_GAIN_DISPLAY_VALUE = 0.01;
// Temporary tuning: 0.01 HovTimG every 36 seconds totals +1 HovTim per hour.
const HOV_TIM_GAIN_INTERVAL_SECONDS = 36;
export const HOV_TIM_GAIN_PER_SECOND =
  HOV_TIM_GAIN_DISPLAY_VALUE / HOV_TIM_GAIN_INTERVAL_SECONDS;

interface MHovResetReward {
  readonly multiplierBefore: HugeNumber;
  readonly outcome: MHovExecuteOutcome;
}

interface NHovResetReward {
  readonly nextCount: number;
  readonly nextBranchExperienceByBranch: NHovBranchExperienceByBranch;
  readonly nextEffectBoundaryCaps: NHovEffectBoundaryCaps;
  readonly nextContinuousEffectProgressByBranch: NonNullable<
    NHovState["continuousEffectProgressByBranch"]
  >;
}

interface OHovResetReward {
  readonly powerGain: HugeNumber;
  readonly hovCoreGain: HugeNumber;
}

function assertFiniteNonNegativeHugeInput(
  value: HugeNumberInput,
  name: string,
): HugeNumber {
  const normalized = huge(value);
  if (!normalized.isFinite() || !isNonNegativeHuge(normalized)) {
    throw new Error(`${name} must be a finite non-negative HugeNumber`);
  }
  return normalized;
}

function assertFinitePositiveHugeInput(
  value: HugeNumberInput,
  name: string,
): HugeNumber {
  const normalized = huge(value);
  if (!normalized.isFinite() || compareHuge(normalized, 0) <= 0) {
    throw new Error(`${name} must be a finite positive HugeNumber`);
  }
  return normalized;
}

function isPositiveHuge(value: HugeNumber): boolean {
  return compareHuge(value, 0) > 0;
}

// Each NHov branch's MP "minimum metric" input compares against the same
// scalar effect the branch reward preview shows: x for amplify, / for
// discount, x for HG-NHov transfer, x for HnG rank-scale base.
function resolveNHovBranchMetric(
  branchId: NHovBranchId,
  effects: NHovBranchEffects,
): HugeNumber {
  switch (branchId) {
    case "hgAmplify":
      return effects.hgAmplifyMultiplier;
    case "hgDiscount":
      return effects.hgDiscountCostDivisor;
    case "hgNHovEffectsToHng":
      return effects.hgNHovEffectsToHngTransferMultiplier;
    case "hngScale":
      return effects.hngRankScaleBase;
  }
}

// NHov MP `minimumEffect` compares the improvement ratio from preview
// `current -> next`, not the absolute post-execution metric. The ratio
// mirrors UI's `current -> next` column (e.g. `x1 -> x8,212` ⇒ ratio 8212),
// matching MHov's pending-vs-previous multiplier semantic so the player's
// "trigger when this run would gain at least Xx" intent is preserved.
function computeNHovBranchMetricImprovementRatio(
  currentMetric: HugeNumber,
  nextMetric: HugeNumber,
): HugeNumber {
  const currentHuge = currentMetric;
  const nextHuge = nextMetric;
  if (compareHuge(currentHuge, 0) <= 0) {
    // Branches like hgNHovEffectsToHng start at metric 0; once the first
    // execution lifts the effect off zero the improvement is effectively
    // infinite for any positive `minimumEffect` threshold. Falling back to
    // a huge sentinel keeps the gate semantic monotonic (more is better)
    // without dragging division-by-zero into the comparison.
    return compareHuge(nextHuge, currentHuge) > 0
      ? huge(Number.MAX_VALUE)
      : hugeZero();
  }
  return nextHuge.div(currentHuge);
}

export interface HoverIdleModelOptions {
  readonly nowMs?: () => number;
  readonly explorationDefinition?: ExplorationDefinition;
  readonly resetBalance?: HovResetBalanceOverrides;
}

export type HoverIdleAmplifierGainSkillMultipliers = Pick<
  HovSkillResolvedEffects,
  | "amplifierGainFixedMultipliersByEntry"
  | "amplifierGainClearedWallKindMultiplierTargets"
  | "amplifierGainOHovRunElapsedMultiplierTargets"
>;

export type HoverIdleTickContext = {
  readonly dtSeconds: number;
  readonly hovGainAtStart: HugeNumber;
  readonly baseHovGainAtStart: HugeNumber;
  readonly timGainAtStart: HugeNumber;
  readonly hiddenHovGainGrowthPerSecond: HugeNumber;
  readonly hiddenTimGainGrowthPerSecond: HugeNumber;
};

export interface HoverIdleMHovAutomationLogStateBefore {
  readonly oHovCount: number;
  readonly nHovCount: number;
  readonly oHovRunElapsedMs: number;
  readonly effectivePendingMultiplier: HugeNumber;
  readonly hov: HugeNumber;
  readonly hovGainPerSecond: HugeNumber;
  readonly currentLoopHovEarned: HugeNumber;
  readonly mHovMultiplier: HugeNumber;
  readonly mHovCount: number;
}

export interface HoverIdleMHovAutomationLogStateAfter {
  readonly mHovMultiplier: HugeNumber;
  readonly mHovCount: number;
}

export interface HoverIdleNHovAutomationLogStateBefore {
  readonly hov: HugeNumber;
  readonly hovGainPerSecond: HugeNumber;
  readonly currentLoopHovEarned: HugeNumber;
  readonly branchExperience: HugeNumber;
  readonly branchLevel: number;
  readonly nHovCount: number;
}

export interface HoverIdleNHovAutomationLogStateAfter {
  readonly branchExperience: HugeNumber;
  readonly branchLevel: number;
  readonly nHovCount: number;
}

export type HoverIdleAutoResetExecutionEvent =
  | {
      readonly sequence: number;
      readonly kind: "ohov-auto-execution";
      readonly layer: "ohov";
      readonly beforeSnapshot: HoverIdleSnapshot;
      readonly afterSnapshot: HoverIdleSnapshot;
    }
  | {
      readonly sequence: number;
      readonly kind: "ohov-mp-execution";
      readonly layer: "ohov";
      readonly beforeSnapshot: HoverIdleSnapshot;
      readonly afterSnapshot: HoverIdleSnapshot;
    }
  | {
      readonly sequence: number;
      readonly kind: "nhov-mp-execution";
      readonly layer: "nhov";
      readonly branchId: NHovBranchId;
      readonly before: HoverIdleNHovAutomationLogStateBefore;
      readonly after: HoverIdleNHovAutomationLogStateAfter;
    }
  | {
      readonly sequence: number;
      readonly kind: "mhov-mp-execution";
      readonly layer: "mhov";
      readonly before: HoverIdleMHovAutomationLogStateBefore;
      readonly after: HoverIdleMHovAutomationLogStateAfter;
    };

export type HoverIdleAutomationEventKind =
  HoverIdleAutoResetExecutionEvent["kind"];

export interface HoverIdleHovGainUpgradeMpAutomationPurchase {
  readonly target: HovGainUpgradeMpTarget;
  readonly attempts: number;
  readonly purchasesApplied: number;
  readonly hovSpent: HugeNumber;
  readonly unlockedTargetIndexes: readonly HovCellGainUpgradeIndex[];
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter: number;
  readonly milestonePurchaseCount: number;
  readonly lastMilestonePurchase:
    | HovGainUpgradeBulkPurchaseCommandResult["milestonePurchases"][number]
    | null;
}

export interface HoverIdleHovGainUpgradeMpAutomationEvent {
  readonly sequence: number;
  readonly kind: "hov-gain-upgrade-mp-automation";
  readonly elapsedMs: number;
  readonly purchases: readonly HoverIdleHovGainUpgradeMpAutomationPurchase[];
}

export interface HoverIdleResetAutomationDrainDiagnostic {
  readonly reason:
    | "event-cap"
    | "hgu-segment-cap"
    | "no-progress"
    | "hgu-no-progress"
    | "no-ready-at-crossing";
  readonly eventsResolved: number;
  readonly hguSegmentsResolved?: number;
  readonly hguPurchasesResolved?: number;
  readonly remainingMs: number;
  readonly crossingMs?: number;
  readonly attemptedKind?:
    | HoverIdleAutomationEventKind
    | "hov-gain-upgrade-mp-automation";
}

interface GameplayAutomationDrainCaps {
  readonly maxResetEvents: number;
  readonly maxHguSegments: number;
}

interface GameplayElapsedLaneScales {
  readonly timer: number;
  readonly amplifierProgression: number;
}

const DEFAULT_GAMEPLAY_ELAPSED_LANE_SCALES: GameplayElapsedLaneScales = {
  timer: 1,
  amplifierProgression: 1,
};

interface GameplayAutomationDrainCoreResult {
  readonly remainingMs: number;
  readonly resetEventsResolved: number;
  readonly hguSegmentsResolved: number;
  readonly hguPurchasesResolved: number;
  readonly hguBulkFoldHitCount: number;
  readonly hguBulkFoldedCadenceCount: number;
  readonly hguBulkFoldFallbackCount: number;
  readonly hguRunFoldChunkCount: number;
  readonly hguRunFoldFireCount: number;
  readonly hguRunFoldPurchaseCount: number;
  /** `null` once `totalDeltaMs` was fully consumed without hitting a cap or stalling. */
  readonly stopReason: HoverIdleResetAutomationDrainDiagnostic["reason"] | null;
}

interface HovGainUpgradeMpAutomationSegmentResult {
  readonly didProcessCadence: boolean;
  readonly attemptCount: number;
  /** Sum of per-target plan `attemptsApplied` — the purchase-bearing subset of `attemptCount`. */
  readonly appliedAttemptCount: number;
  readonly purchaseCount: number;
}

interface HovGainUpgradeMpReadyCadenceDrainResult {
  readonly didProcessCadence: boolean;
  readonly cadenceCount: number;
  readonly purchaseCount: number;
  readonly bulkHitCount: number;
  readonly bulkFoldedCadenceCount: number;
  readonly fallbackCount: number;
}

interface HovGainUpgradeMpHomogeneousCadenceSpanResult extends HovGainUpgradeMpReadyCadenceDrainResult {
  readonly remainingMs: number;
  readonly runFoldChunkCount: number;
  readonly runFoldFireCount: number;
  readonly runFoldPurchaseCount: number;
  /**
   * True when a chunk's purchases grew the gain rate past
   * {@link HGU_RUN_FOLD_CHUNK_RATE_GROWTH_LIMIT}: the caller should stop
   * chunk-folding for the rest of its drain invocation because the regime's
   * purchase->rate feedback is too fast for deferred-purchase chunks.
   */
  readonly runFoldSuppressedByRateGrowth: boolean;
}

interface ContinuousGameplayHovDeltas {
  readonly earnedHov: HugeNumber;
  readonly netHovDelta: HugeNumber;
}

// Elapsed-invariant inputs of the continuous-gameplay projection, computed once
// per crossing resolution and reused across every readiness probe. The gain
// pipeline, hidden-growth rate, and MP cell gain rates do not depend on the
// projected elapsed, so caching them makes each probe cheap while keeping the
// per-elapsed arithmetic bit-identical to the uncached path. The linear /
// quadratic coefficients express the no-hover Hov delta as
// `netHovDelta(dt) = linearHovDeltaPerSecond * dt + quadraticHovDeltaPerSecondSquared * dt^2`,
// which is inverted directly by the closed-form crossing solver.
export interface ContinuousGameplayProjectionInputs {
  readonly context: HoverIdleTickContext;
  readonly mpCellIndexes: readonly number[];
  readonly mpCellGainPerSecond: readonly HugeNumber[];
  // Closed-form is only valid while neither hover interaction is active, since
  // hover refinement and Hov consumption make the delta piecewise in dt.
  readonly quadraticEligible: boolean;
  // Whether the hovered Hov target is eligible for TimHov activation, i.e. its
  // HnG ties the maximum among eligible targets. Elapsed-invariant like the
  // rates above, so every probe reuses one resolution instead of rescanning the
  // grid.
  readonly timHovActivationEligible: boolean;
  readonly linearHovDeltaPerSecond: HugeNumber;
  readonly quadraticHovDeltaPerSecondSquared: HugeNumber;
}

// Result of deriving "what would the projection substrate be immediately
// after an MHov reset executed from the current state" without executing the
// reset. See HoverIdleModel.derivePostMHovResetProjectionInputs.
export interface PostMHovResetProjectionDerivation {
  readonly inputs: ContinuousGameplayProjectionInputs;
  readonly postMHovState: MHovState;
  readonly postHov: HugeNumber;
  readonly rewardBaseHov: HugeNumber;
  readonly rewardMultiplier: HugeNumber;
}

// Runtime-only crossing-resolution strategy. Production always uses
// "closed-form"; the other modes exist for equivalence/perf measurement and are
// selected only through the test-only setter. None of this is persisted.
type ResetCrossingResolutionMode =
  | "closed-form"
  | "binary-cached"
  | "binary-legacy";

// Non-persisted per-update accounting for crossing resolution, modeled on
// `lastResetAutomationDrainDiagnostic`. Used by perf/equivalence harnesses.
interface ResetCrossingResolutionStats {
  resolutionCalls: number;
  closedFormHits: number;
  binaryFallbacks: number;
  nhovPrunedSkips: number;
  near0Crossings: number;
  binaryIterations: number;
  readinessEvals: number;
  crossingResolutionMs: number;
}

function createResetCrossingResolutionStats(): ResetCrossingResolutionStats {
  return {
    resolutionCalls: 0,
    closedFormHits: 0,
    binaryFallbacks: 0,
    nhovPrunedSkips: 0,
    near0Crossings: 0,
    binaryIterations: 0,
    readinessEvals: 0,
    crossingResolutionMs: 0,
  };
}

interface HovGainUpgradeMpAutomationCadence {
  readonly target: HovGainUpgradeMpTarget;
  config: UpgradeRepeatCadenceConfig;
  readonly state: UpgradeRepeatCadenceState;
}

const RESET_AUTOMATION_DRAIN_MAX_EVENTS_PER_UPDATE = 1024;
// 250ms raw-frame clamp at Admin x1000 can yield 250 gameplay seconds.
// At the 80ms minimum cadence that is ~3,125 segments, so keep the cap above
// that valid worst-case frame while still bounding malformed/stuck drains.
const HOV_GAIN_UPGRADE_MP_DRAIN_MAX_SEGMENTS_PER_UPDATE = 4096;
const RESET_AUTOMATION_DRAIN_EPSILON_MS = 1e-6;
const RESET_AUTOMATION_CROSSING_SEARCH_ITERATIONS = 36;
// Crossings at or below this elapsed are counted as near-0 for perf diagnostics.
const RESET_AUTOMATION_NEAR_ZERO_CROSSING_MS = 1;
// Deliberate under-estimate applied to the closed-form HGU-MP affordability
// crossing before quantizing it onto the starved-fire schedule (see
// resolveHovGainUpgradeMpCadenceBoundaryMs). Bounds any residual HugeNumber
// sqrt/mul rounding on the safe (early) side; far smaller than the cadence
// minimum interval (80ms), so it can never shift the quantized fire onto the
// wrong grid cell, and an early estimate only costs one extra no-op real
// plan-build rather than risking a silently-skipped purchase.
const HGU_AFFORDABILITY_CROSSING_SAFETY_MARGIN_MS = 1;

// Purchase-bearing HGU-MP cadence runs are chunk-folded only when one drain
// invocation covers at least this much elapsed time. Below it (every real
// former shared-elapsed render frames capped a frame at 25s, and
// the x1/x1000 update-split equivalence tests pin single updates up to 30s)
// the drain keeps today's exact per-fire behavior, so short-span online play
// stays byte-identical. At or above it (bounded large-elapsed calls or a
// future raised frame cap) fire runs may be coalesced: continuous
// gain accrues once per chunk and each target buys through one aggregate
// plan with the same progressive-cost affordability rule.
const HGU_RUN_FOLD_MIN_CORE_DELTA_MS = 60_000;
// One chunk never covers more than this much elapsed time, bounding how far
// purchase timing (and therefore gain-pipeline compounding) can be deferred
// inside a fold regardless of how long the reset-free stretch is.
const HGU_RUN_FOLD_MAX_CHUNK_MS = 30_000;
// Chunk sizes ramp geometrically from the next fire boundary so a regime is
// probed with a near-per-fire chunk before larger chunks are attempted; the
// ramp is capped so a mispredicted regime cannot jump straight to the
// maximum chunk.
const HGU_RUN_FOLD_RAMP_MAX = 64;
// Regime detector for the chunked run fold: when one chunk's purchases grow
// the linear Hov gain rate by more than this factor, the stretch is
// purchase->rate compounding (post-reset plateau floods, steady drip
// re-purchase). Deferring purchases to chunk ends slows that compounding —
// measured on wide-hgu, folding drip runs collapsed 24h MHov executions from
// ~910 to ~265 — so the fold suppresses itself for the rest of the drain
// invocation and the exact per-fire path resolves the stretch. Only regimes
// whose purchases barely move the pipeline (below this factor per chunk)
// keep folding; removing the compounding floor itself needs the analytic
// purchase-recurrence fold flagged as follow-up in ADR 0043.
const HGU_RUN_FOLD_CHUNK_RATE_GROWTH_LIMIT = 1.02;

/**
 * Default per-chunk elapsed span for `advanceGameplayElapsedBudgeted`, in
 * milliseconds. One chunk is deliberately sized to match what a legacy
 * single `update()` call under the former one-step-per-frame cap used to
 * consume, and stays below `HGU_RUN_FOLD_MIN_CORE_DELTA_MS` so every chunk
 * (fold eligibility is evaluated per drain-core invocation, i.e. per chunk,
 * not across a whole multi-chunk call) keeps today's exact per-fire x1/x1000
 * behavior. Letting a single chunk reach the HGU run fold is future
 * per-chunk solver work, not part of this entry.
 */
const GAMEPLAY_ADVANCE_DEFAULT_CHUNK_SPAN_MS = 25_000;

/**
 * Default per-call compute-time budget for `advanceGameplayElapsedBudgeted`,
 * in milliseconds. A render frame also has to build and render the scene at
 * 60fps (~16.7ms total), so this leaves the majority of that budget free for
 * everything else. At x1 or the x1000 measurement regime a single chunk
 * resolves in well under this budget, so those lanes are unaffected — only a
 * call whose desired elapsed spans many chunks (very high multipliers) is
 * throttled across frames by it.
 */
const GAMEPLAY_ADVANCE_DEFAULT_BUDGET_MS = 6;

/**
 * Hard ceiling on chunks processed by one `advanceGameplayElapsedBudgeted`
 * call, independent of the compute-time budget. Bounds per-call growth of
 * the aggregated automation-event buffers (reset once for the whole call,
 * see `resetPerGameplayAdvanceBuffers`) even in a regime where individual
 * chunks stay cheap enough to never trip the budget on their own.
 */
const GAMEPLAY_ADVANCE_DEFAULT_MAX_CHUNKS = 64;

export type GameplayElapsedAdvanceStopReason =
  | "completed"
  | "budget"
  | "chunk-limit";

export interface GameplayElapsedAdvanceOptions {
  /** Total desired game elapsed time to process, in milliseconds. */
  readonly elapsedMs: number;
  /** Overrides {@link GAMEPLAY_ADVANCE_DEFAULT_CHUNK_SPAN_MS}. */
  readonly chunkSpanMs?: number;
  /** Overrides {@link GAMEPLAY_ADVANCE_DEFAULT_BUDGET_MS}. */
  readonly budgetMs?: number;
  /** Overrides {@link GAMEPLAY_ADVANCE_DEFAULT_MAX_CHUNKS}. */
  readonly maxChunks?: number;
}

export interface GameplayElapsedAdvanceResult {
  /** Milliseconds actually consumed by chunks this call processed. */
  readonly processedMs: number;
  /** Milliseconds of `elapsedMs` not yet processed; carry into a later call. */
  readonly remainingMs: number;
  readonly chunkCount: number;
  readonly stoppedReason: GameplayElapsedAdvanceStopReason;
}

export interface OnlineGameplayAdvanceOptions {
  readonly continuousElapsedMs: number;
  readonly timerElapsedMs: number;
  readonly amplifierProgressionElapsedMs: number;
  readonly resetAutomationEventBudget: number;
  readonly hguPurchaseEventBudget: number;
}

export interface OnlineGameplayAdvanceResult extends OnlineGameplayAdvanceOptions {
  readonly resetEventsResolved: number;
  readonly hguPurchaseEventsResolved: number;
  readonly hguPurchasesResolved: number;
  readonly stoppedReason:
    | HoverIdleResetAutomationDrainDiagnostic["reason"]
    | null;
}

function toLastBulkMilestoneRecord(
  plan: HovGainUpgradePurchasePlan,
):
  | HovGainUpgradeBulkPurchaseCommandResult["milestonePurchases"][number]
  | null {
  const record = plan.milestonePurchases.at(-1);
  if (record === undefined) {
    return null;
  }
  return {
    enhancementLevelBefore: record.enhancementLevelBefore,
    enhancementLevelAfter: record.enhancementLevelAfter,
  };
}

export class HoverIdleModel implements Updatable {
  private readonly mpmNumericSettingsListeners = new Set<
    (changes: readonly MpmNumericSettingChange[]) => void
  >();
  private readonly resetBalance: HovResetBalance;
  private readonly baseHovGainPerSecond: HugeNumber;
  private readonly baseTimGainPerSecond: HugeNumber;
  private readonly baseUpgradeCost: HugeNumber;
  private readonly baseTimUpgradeCost: HugeNumber;
  private readonly upgradeGainIncrease: HugeNumber;
  private readonly upgradeCostMultiplier: number;
  private readonly timUpgradeGainIncrease: HugeNumber;
  private readonly timUpgradeCostMultiplier: number;

  private hov: HugeNumber = hugeZero();
  private tim: HugeNumber = hugeZero();
  // Hov earned during the in-progress update() tick. Observational and
  // transient (not persisted): recent-rate display reads it to avoid
  // reconstructing the gain from a balance/total delta, which rounds to zero
  // once the running total dwarfs the per-tick increment.
  private hovEarnedThisTick: HugeNumber = hugeZero();
  private fai = 0;
  private hovGainPerSecond: HugeNumber;
  private timGainPerSecond: HugeNumber;
  private skillHovGainPerSecond: HugeNumber = hugeZero();
  private skillTimGainPerSecond: HugeNumber = hugeZero();
  private achievementHovGainIntent: HovGainEffectSourceIntent =
    NEUTRAL_HOV_GAIN_EFFECT_INTENT;
  private hovSkillGainIntent: HovGainEffectSourceIntent =
    NEUTRAL_HOV_GAIN_EFFECT_INTENT;
  private bhgToHgPowerExponentBonus = 0;
  private hovGainCountMultiplier = 1;
  private hovGainMultiplier = 1;
  private h1gExponentBonus = 0;
  private hov1GainMultiplier: HugeNumber = hugeOne();
  // Entries whose source Amplifier Gain is currently multiplied by OHov
  // power. Composed by skills/achievements at sync time; consumed by the
  // drain tick and snapshot path so the multiplier flows through both the
  // actual AP/boost gain and the displayed effective gain.
  private hovAmplifierGainOHovMultiplierTargets: ReadonlySet<HovAmplifierEntryId> =
    new Set();
  private amplifierGainFixedMultipliersByEntry: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  > = new Map();
  private achievementAmplifierGainFixedMultipliersByEntry: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  > = new Map();
  private amplifierGainClearedWallKindMultiplierTargets: ReadonlySet<HovAmplifierEntryId> =
    new Set();
  private amplifierGainOHovRunElapsedMultiplierTargets: ReadonlySet<HovAmplifierEntryId> =
    new Set();
  private skillOHovHovCoreGainMultiplierUnlocked = false;
  private skillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked = false;
  private skillFreeMpCoreOHovHovCoreGainMultiplierUnlocked = false;
  // Hov skill contributions to the effective Amplifier AP exponent. Composed
  // on top of `hovAmplifierState.apExponent` together with dynamic AP Deer /
  // AP Butterfly bonuses at HG-multiplier resolution time.
  private skillApBoarExponentBonus = 0;
  private skillApDeerExponentBonusAcquired = false;
  private skillApButterflyExponentBonusAcquired = false;
  // Additive bonus contributed by effect sources to the HnG rank scale base.
  // Composed inside the base BEFORE the NHov hngScale branch multiplier, so
  // effect text describes "rank scale base + N", not "final HnG xN".
  private hngRankScaleBaseBonus = 0;
  // Authoritative MHov / NHov growth-exponent bonuses from acquired Hov skills.
  // Referenced by the MHov reward exponent and NHov branch-effect resolution so
  // snapshot, preview, and execution share the same value.
  private growthExponentBonus: MhovNHovGrowthExponentBonus = {
    mhov: 0,
    nHovHgAmplify: 0,
    nHovHgNHovEffectsToHng: 0,
    nHovHngScale: 0,
  };
  // Additive bonus contributed by Hov skills (currently HUC+) to the HG / HnG
  // base simultaneous purchase count. Composed inside the base BEFORE Wall
  // reward multipliers at the planner cap boundary.
  private hovSkillSimultaneousPurchaseCountBaseBonus = 0;
  private achievementOHovHovCoreGainScalingUnlocked = false;
  private achievementMpmPresetSlotBonus = 0;
  // True once `outside-mhov` lifts the hard Number-limit cap on the MHov
  // reward. Post-boundary rewards still pass through the shared softcap.
  private achievementMhovRewardUncapped = false;
  // True once `outside-nhov` lifts the Number-limit cap on all NHov branch
  // effects. Gates whether branch-effect resolution clamps at NUMBER_LIMIT_BOUNDARY.
  private achievementNhovBranchEffectUncapped = false;
  private hiddenHovGainBonus: HugeNumber = defaultHiddenGrowth.hovGainBonus;
  private hiddenTimGainBonus: HugeNumber = defaultHiddenGrowth.timGainBonus;
  private hiddenHovTimProgress: number = defaultHiddenGrowth.hovTimProgress;
  private upgradeCost: HugeNumber;
  private timUpgradeCost: HugeNumber;
  private purchasedHovGainUpgrades = 0;
  private purchasedTimGainUpgrades = 0;
  private hovTabUnlocks = createDefaultHovTabUnlockState();
  private hoverIdleEffectUnlocks = createDefaultHoverIdleEffectUnlockState();
  private prestige = { ...defaultPrestige };
  private mHovState: MHovState = createDefaultMHovState();
  private nHovState: NHovState = createDefaultNHovState();
  private oHovState: OHovState;
  private explorationState: ExplorationState = createDefaultExplorationState();
  private explorationPointerBiomeId: ExplorationBiomeId | null = null;
  private explorationTimAccelerationUnlocked = false;
  private readonly explorationDefinition: ExplorationDefinition;
  private lastAnyResetAtMs = 0;
  private gameplayElapsedSinceAnyResetMs = 0;
  private resetAutomationState: HoverIdleResetAutomationState =
    createDefaultResetAutomationState();
  // Runtime-only marker bumped on every Outside-family reset execution.
  // Not persisted: the UI scheduler self-initializes from the first snapshot.
  private outsideResetSerial = 0;
  private lastOHovAutoExecutePreSnapshot: HoverIdleSnapshot | null = null;
  // Source of the most recent OHov execution (manual / mp / auto). Stays at
  // "unknown" until an execution writes it, and is consumed by the runtime
  // when emitting PlayLog so manual / auto / MP executions can be told apart
  // downstream. Not persisted.
  private lastOHovExecutionSource: OHovExecutionSource = "unknown";
  private lastOHovMpExecuteBeforeSnapshot: HoverIdleSnapshot | null = null;
  private lastMHovAutoExecuteBeforeSnapshot: MHovExecuteBeforeSnapshot | null =
    null;
  private lastNHovAutoExecute: {
    readonly branchId: NHovBranchId;
    readonly beforeSnapshot: HoverIdleSnapshot;
  } | null = null;
  // Display-only transient candidates. Kept separate from the PlayLog consume
  // slots so rendering cannot change logging, autosave, or reset semantics.
  private lastAutoResetExecuteBeforeSnapshots: {
    mHov: HoverIdleSnapshot | null;
    nHov: HoverIdleSnapshot | null;
    oHov: HoverIdleSnapshot | null;
  } = {
    mHov: null,
    nHov: null,
    oHov: null,
  };
  private lastAutoResetExecutionEvents: HoverIdleAutoResetExecutionEvent[] = [];
  private lastHovGainUpgradeMpAutomationEvents: HoverIdleHovGainUpgradeMpAutomationEvent[] =
    [];
  private lastResetAutomationDrainDiagnostic: HoverIdleResetAutomationDrainDiagnostic | null =
    null;
  private resetCrossingResolutionMode: ResetCrossingResolutionMode =
    "closed-form";
  private resetCrossingResolutionTimingEnabled = false;
  private lastResetCrossingResolutionStats: ResetCrossingResolutionStats =
    createResetCrossingResolutionStats();
  private gameplayAutomationEventSequence = 0;
  private gameplayElapsedLaneScales: GameplayElapsedLaneScales =
    DEFAULT_GAMEPLAY_ELAPSED_LANE_SCALES;
  private hovAmplifierState: HovAmplifierState =
    createInitialHovAmplifierState();
  private hovAmplifierRecentChargeSources: HovAmplifierRecentChargeSources =
    createEmptyHovAmplifierRecentChargeSources();
  // Per-entry wall-clock timestamps of the last direct/self gauge fill (e.g.
  // hover-induced increase). Used only for snapshot presentation; not
  // persisted across save/restore or OHov reset.
  private hovAmplifierRecentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs =
    createEmptyHovAmplifierRecentDirectChargeAtMs();
  private hovAmplifierGaugeVisualSegments: HovAmplifierGaugeVisualSegments =
    createEmptyHovAmplifierGaugeVisualSegments();
  private hovAmplifierVisualChargeBatch: HovAmplifierGaugeVisualChargeBatch =
    {};
  private hovAmplifierHoveringEntryId: HovAmplifierEntryId | null = null;
  private mpmState: MpmState = createDefaultMpmState();
  private hovGainFamilyUpgradeRepeatInitialIntervalMs =
    DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS;
  private readonly hovGainUpgradeMpAutomationCadences = new Map<
    HovGainUpgradeMpTargetKey,
    HovGainUpgradeMpAutomationCadence
  >();
  // Memo keys for syncHovGainUpgradeMpAutomationCadences. The derived target
  // set and cadence config depend only on these three inputs; mpmState and
  // wallState are replaced atomically on change, so reference equality is a
  // safe invalidation signal (same idiom as the NHov derivation cache).
  private hovGainUpgradeMpCadenceSyncMpmState: MpmState | null = null;
  private hovGainUpgradeMpCadenceSyncWallState: WallState | null = null;
  private hovGainUpgradeMpCadenceSyncInitialIntervalMs = Number.NaN;
  private achievementMpmUnlockIntent: MpmUnlockIntent =
    createNeutralMpmUnlockIntent();
  private hovSkillMpmUnlockIntent: MpmUnlockIntent =
    createNeutralMpmUnlockIntent();
  private wallState: WallState = createDefaultWallState();
  // OHov reset-target cache keyed by wallState reference and reward cap level.
  // The automation gates probe both targets several times per drain iteration;
  // caching avoids re-formatting and re-parsing the target strings into
  // HugeNumber on every probe.
  private oHovTargetCache: {
    readonly wallState: WallState;
    readonly wallRewardCapLevel: WallRewardCapLevel;
    readonly builtInTarget: BuiltInOHovAutoExecutionTarget;
    readonly builtInTargetHov: HugeNumber;
    readonly executionTargetHov: string;
    readonly executionTargetHovHuge: HugeNumber;
  } | null = null;
  // OHov hovCore-gain multiplier memo keyed by the exact resolver inputs.
  // `oHovState.power` is reference-stable across the per-tick records spread
  // (only reset grants replace it), so together with mpmState identity,
  // mpCapacity, and the four unlock flags this compares everything
  // resolveOHovHovCoreGainMultiplier reads. The OHov-MP automation gate
  // re-derives the multiplier on every readiness probe even though it only changes at
  // reset/assignment/skill boundaries.
  private oHovHovCoreGainMultiplierCache: {
    readonly oHovPower: HugeNumber;
    readonly mpmState: MpmState;
    readonly mpCapacity: number;
    readonly skillAcquired: boolean;
    readonly achievementScalingUnlocked: boolean;
    readonly mpCapacityCoreAcquired: boolean;
    readonly freeMpCoreAcquired: boolean;
    readonly multiplier: HugeNumber;
  } | null = null;
  // Elapsed-invariant projection substrate cache, scoped to a single
  // drainGameplayAutomationEvents() invocation (reset unconditionally at
  // drain entry, see getContinuousGameplayProjectionInputs). hiddenHovGainBonus
  // and hovAmplifierState catch time-driven drift (hidden gain growth,
  // amplifier AP) that reset/purchase invalidation cannot see directly since
  // it happens inside advanceContinuousGameplay; mpmState/wallState catch
  // threshold-unlock and wall-driven changes the same way the OHov target
  // cache above does. Reset execution and HGU purchases invalidate the cache
  // explicitly because they mutate hovCells/purchasedHovGainUpgrades in
  // place without necessarily changing any of these four references.
  private continuousGameplayProjectionInputsCache: {
    readonly inputs: ContinuousGameplayProjectionInputs;
    readonly hiddenHovGainBonus: HugeNumber;
    readonly hovAmplifierState: HovAmplifierState;
    readonly mpmState: MpmState;
    readonly wallState: WallState;
  } | null = null;
  // MHov-MP crossing-threshold memo keyed by the projection-substrate inputs
  // instance above. See resolveMHovMpCrossingThresholdRunBaseHov for why
  // inputs identity is a sufficient invalidation key.
  private mHovMpCrossingThresholdCache: {
    readonly inputs: ContinuousGameplayProjectionInputs;
    readonly thresholdRunBaseHov: HugeNumber | null;
  } | null = null;
  // HGU-MP first-purchase-threshold memo. Unlike the single MHov-MP threshold
  // above, several cadence targets (global + Hov cells) can be assigned at
  // once, and a dense purchase-compounding run invalidates the projection
  // substrate on every fire (see continuousGameplayProjectionInputsCache) even
  // though one fire's purchase only ever moves ITS OWN target's
  // currentRawCost. Keying entries by the substrate-inputs instance (as the
  // sibling mHovMpCrossingThresholdCache does) would therefore recompute
  // every assigned target's threshold on every fire regardless of which
  // target purchased. Instead each entry is keyed by that target's own
  // (currentRawCost reference, upgradeAvailable) pair, which is exactly the
  // pair resolveHovGainUpgradeFirstPurchaseThreshold's result depends on (see
  // resolveHovGainUpgradeMpFirstPurchaseThreshold) — so an entry survives any
  // invalidation that isn't its own target's purchase.
  // invalidateHovGainUpgradeMpThresholdCachesHard drops the whole map on the
  // two triggers a per-entry key cannot see: reset execution (hovCells/
  // nHovState/wallState can be replaced wholesale) and drain entry (external
  // mutation between drain() calls) — the same two triggers the substrate
  // cache's own explicit invalidation relies on beyond its 4-reference guard.
  private hovGainUpgradeMpFirstPurchaseThresholdCache: Map<
    HovGainUpgradeMpTargetKey,
    {
      readonly rawCostRef: HugeNumber;
      readonly available: boolean;
      readonly value: HugeNumber | null;
    }
  > | null = null;
  // Sibling cache for the grid-tier availability-crossing threshold (see
  // resolveHovGainUpgradeMpAvailabilityThresholdRunBaseHov), keyed by the
  // target cell's own `unlocked` flag under the same two hard-invalidation
  // triggers as the cache above: reset execution and drain entry. The
  // threshold's other dependency, wallState-gated blocking, is exactly why
  // the drain-entry trigger matters here too — stopActiveWall()/startWall()/
  // setSkillWallUnlocked(false) can mutate activeWallId between drain()
  // invocations without any reset-kind event.
  private hovGainUpgradeMpAvailabilityThresholdCache: Map<
    HovGainUpgradeMpTargetKey,
    { readonly unlocked: boolean; readonly value: HugeNumber | null }
  > | null = null;
  private skillWallUnlocked = false;
  private skillExplorationUnlocked = false;
  private wallRewardCapLevel: WallRewardCapLevel = "base";
  private readonly nowMs: () => number;
  private settings: HoverIdleSettingsState =
    cloneSettingsState(defaultSettings);
  private payment: HoverIdlePaymentState = clonePaymentState(defaultPayment);
  private localEntitlementSnapshot =
    cloneEntitlementSnapshot(DEFAULT_ENTITLEMENTS);
  private ownerEntitlementSnapshot =
    cloneEntitlementSnapshot(DEFAULT_ENTITLEMENTS);
  private cumulativeStats: HoverIdleCumulativeStatsState = {
    ...defaultCumulativeStats,
  };
  private isHoveringHov = false;
  private isHoveringTim = false;
  private hovGridTier: HoverIdleGridTier = 1;
  private hovGridThemeHue: number | null = null;
  private hovHoveringCellIndex: number | null = null;
  private timHovConsumingCellIndex: number | null = null;
  private timHovConsumptionRatePerSecond = 0;
  private hovCells: HovCellState[] = createDefaultHovCells(HOV_CELL_BASE_COSTS);
  private minHovGainUnlockCostsByTargetIndex: Array<HugeNumber | null> =
    createDefaultHovGainUnlockCosts();
  private minHovGainMilestoneTriggerCostsByMilestoneIndex: Array<HugeNumber | null> =
    [];
  private timGridTier: HoverIdleGridTier = 1;
  private timHoveringCellIndex: number | null = null;
  private readonly timCells: TimCellState[] =
    createDefaultTimCells(TIM_CELL_BASE_COSTS);
  private hovTimBalance: HovTimBalanceState = { ...defaultHovTimBalance };
  // Externally-set base capacity (skill + payment + achievement effects).
  // Wall reward addends compose on top via `recomputeEffectiveMpCapacity()` so
  // `mpCapacity` (the cached effective value) is what every internal consumer
  // and the snapshot read.
  private mpCapacityBase = 0;
  private mpCapacity = 0;
  private mpCapacityInitialized = false;
  private continuousPointerSeconds = 0;
  private observerSeconds = 0;
  private xPatternSeconds = 0;
  private hPatternSeconds = 0;
  private isHoveringHovOLabel = false;
  private isHovTargetScreenActive = true;
  private hovOLabelHoverSeconds = 0;
  private hovCellClickTimestamps: number[] = [];
  private achievementHovGainFlatBonus = 0;
  private achievementHovUpgradeCostMultiplier = 1;
  private achievementHovCellUpgradeCostMultiplier = 1;
  private achievementTimGainCountMultiplier = 1;
  private timHovActivationEfficiency = TIM_HOV_ACTIVATION_EFFICIENCY_DEFAULT;
  private oneCostHoverRepeaterBatchEnabled = false;
  private isTimHovActivationEnabled = true;
  private isTimHovActivationUnlocked = false;
  private readonly snapshotReadHelpers: HoverIdleSnapshotReadHelpers = {
    resolveHovUpgradeGainIncrease: () => this.resolveHovUpgradeGainIncrease(),
    resolveEffectiveHovCellUpgradeCost: (cellIndex) =>
      this.getEffectiveHovCellUpgradeCost(cellIndex),
    resolveHovCellGainPerSecond: (index, baseGainPerSecond) =>
      this.resolveCellGainPerSecond(index, baseGainPerSecond),
    resolveHovCellGainPerSecondWithMultiplier: (
      index,
      hovGainMultiplier,
      enhancementLevel,
      baseGainPerSecond,
    ) =>
      this.resolveCellGainPerSecondWithMultiplier(
        index,
        hovGainMultiplier,
        enhancementLevel,
        baseGainPerSecond,
      ),
    resolveHovCellOneCostBatchSummary: (options) =>
      this.createHovCellGainUpgradeOneCostBatchSummary(options),
    resolveHovCellSimultaneousPurchasePreview: (options) =>
      this.createHovCellGainUpgradeSimultaneousPurchasePreview(options),
    resolveTimCellGainPerSecond: (index, baseGainPerSecond) =>
      this.resolveTimCellGainPerSecond(index, baseGainPerSecond),
    resolveEffectiveBhgToHgPowerExponent: () =>
      this.getEffectiveBhgToHgPowerExponent(),
  };

  constructor(
    config: HoverIdleConfig = defaultHoverIdleConfig,
    options: HoverIdleModelOptions = {},
  ) {
    assertHoverIdleConfig(config);
    this.resetBalance = resolveHovResetBalance(options.resetBalance);
    this.nowMs = options.nowMs ?? Date.now;
    this.explorationDefinition =
      options.explorationDefinition ?? DEFAULT_EXPLORATION_DEFINITION;
    this.baseHovGainPerSecond = huge(config.hovGainPerSecond);
    this.baseTimGainPerSecond = huge(config.timGainPerSecond);
    this.baseUpgradeCost = huge(config.upgradeCost);
    this.baseTimUpgradeCost = huge(config.timUpgradeCost);
    this.upgradeGainIncrease = huge(config.upgradeGainIncrease);
    this.upgradeCostMultiplier = config.upgradeCostMultiplier;
    this.timUpgradeGainIncrease = huge(config.timUpgradeGainIncrease);
    this.timUpgradeCostMultiplier = config.timUpgradeCostMultiplier;
    this.hovGainPerSecond = huge(config.hovGainPerSecond);
    this.timGainPerSecond = huge(config.timGainPerSecond);
    this.upgradeCost = huge(config.upgradeCost);
    this.timUpgradeCost = huge(config.timUpgradeCost);
    this.oHovState = createDefaultOHovState(this.nowMs());
  }

  update(dtSeconds: number): void {
    assertFinitePositive(dtSeconds, "dtSeconds");
    this.resetPerGameplayAdvanceBuffers();
    this.drainGameplayAutomationEvents(dtSeconds * 1000);
  }

  advanceOnlineGameplay(
    options: OnlineGameplayAdvanceOptions,
  ): OnlineGameplayAdvanceResult {
    assertFiniteNonNegative(options.continuousElapsedMs, "continuousElapsedMs");
    assertFiniteNonNegative(options.timerElapsedMs, "timerElapsedMs");
    assertFiniteNonNegative(
      options.amplifierProgressionElapsedMs,
      "amplifierProgressionElapsedMs",
    );
    assertNonNegativeInteger(
      options.resetAutomationEventBudget,
      "resetAutomationEventBudget",
    );
    assertNonNegativeInteger(
      options.hguPurchaseEventBudget,
      "hguPurchaseEventBudget",
    );
    if (
      options.resetAutomationEventBudget === 0 ||
      options.hguPurchaseEventBudget === 0
    ) {
      throw new Error("online automation event budgets must be positive");
    }

    this.resetPerGameplayAdvanceBuffers();
    if (options.continuousElapsedMs === 0) {
      if (
        options.timerElapsedMs !== 0 ||
        options.amplifierProgressionElapsedMs !== 0
      ) {
        throw new Error(
          "effective elapsed lanes must all be zero when continuousElapsedMs is zero",
        );
      }
      return {
        ...options,
        resetEventsResolved: 0,
        hguPurchaseEventsResolved: 0,
        hguPurchasesResolved: 0,
        stoppedReason: null,
      };
    }

    this.gameplayElapsedLaneScales = {
      timer: options.timerElapsedMs / options.continuousElapsedMs,
      amplifierProgression:
        options.amplifierProgressionElapsedMs / options.continuousElapsedMs,
    };
    try {
      const result = this.runGameplayAutomationDrainCore(
        options.continuousElapsedMs,
        {
          maxResetEvents: options.resetAutomationEventBudget,
          maxHguSegments: options.hguPurchaseEventBudget,
        },
      );
      return {
        ...options,
        resetEventsResolved: result.resetEventsResolved,
        hguPurchaseEventsResolved: result.hguSegmentsResolved,
        hguPurchasesResolved: result.hguPurchasesResolved,
        stoppedReason: result.stopReason,
      };
    } finally {
      this.gameplayElapsedLaneScales = DEFAULT_GAMEPLAY_ELAPSED_LANE_SCALES;
    }
  }

  /**
   * Budgeted large-elapsed entry point shared substrate for advancing game
   * time far beyond one render frame's worth in a single call. Processes
   * `options.elapsedMs` in chunks sized like a legacy `update()` call (see
   * {@link GAMEPLAY_ADVANCE_DEFAULT_CHUNK_SPAN_MS}) until `elapsedMs` is
   * consumed, the per-call compute-time budget is spent (see
   * {@link GAMEPLAY_ADVANCE_DEFAULT_BUDGET_MS}), or `maxChunks` is reached.
   *
   * One chunk is behaviorally identical to one legacy `update()` call — this
   * method's only new capability is processing several chunks per call so a
   * large desired elapsed (a high admin/system speed multiplier) is not
   * silently flattened to a single frame's worth of progress.
   *
   * Resets the per-advance buffers ONCE for the whole call, not per chunk,
   * so automation event buffers aggregate across every chunk this call
   * processes and the caller consumes them once — exactly like a single
   * `update()` call's buffers describe its one chunk. `lastResetAutomationDrainDiagnostic`
   * is the one exception: it is last-writer-wins per drain-core invocation,
   * so after a multi-chunk call it reflects only the final chunk, not the
   * whole call.
   *
   * Float-precision note: at astronomic `elapsedMs` (e.g. `1e51`),
   * `remainingMs - chunkMs` can round back to `remainingMs` unchanged in
   * double precision, so the loop never relies on `remainingMs` reaching 0
   * to terminate — the budget and `maxChunks` bounds below are the real
   * terminators. `processedMs` accumulates independently of that
   * subtraction (a running sum of chunks actually processed), so it stays
   * exact regardless.
   */
  advanceGameplayElapsedBudgeted(
    options: GameplayElapsedAdvanceOptions,
  ): GameplayElapsedAdvanceResult {
    assertFiniteNonNegative(options.elapsedMs, "elapsedMs");
    const chunkSpanMs =
      options.chunkSpanMs !== undefined &&
      Number.isFinite(options.chunkSpanMs) &&
      options.chunkSpanMs > 0
        ? options.chunkSpanMs
        : GAMEPLAY_ADVANCE_DEFAULT_CHUNK_SPAN_MS;
    const budgetMs =
      options.budgetMs !== undefined &&
      Number.isFinite(options.budgetMs) &&
      options.budgetMs >= 0
        ? options.budgetMs
        : GAMEPLAY_ADVANCE_DEFAULT_BUDGET_MS;
    const maxChunks =
      options.maxChunks !== undefined &&
      Number.isFinite(options.maxChunks) &&
      options.maxChunks > 0
        ? // Floor a fractional override up to at least 1: this call's
          // never-zero-progress guarantee must hold even for a caller-supplied
          // maxChunks between 0 and 1.
          Math.max(1, Math.floor(options.maxChunks))
        : GAMEPLAY_ADVANCE_DEFAULT_MAX_CHUNKS;

    this.resetPerGameplayAdvanceBuffers();

    let remainingMs = options.elapsedMs;
    let processedMs = 0;
    let chunkCount = 0;
    const startedAtMs = this.crossingResolutionNowMs();

    while (remainingMs > RESET_AUTOMATION_DRAIN_EPSILON_MS) {
      if (chunkCount >= maxChunks) {
        return {
          processedMs,
          remainingMs,
          chunkCount,
          stoppedReason: "chunk-limit",
        };
      }
      // A chunk in progress always finishes; the deadline can only stop the
      // loop between chunks, never mid-chunk, and is skipped before the
      // very first chunk so one call always makes at least some progress
      // even when `budgetMs` is already spent (e.g. 0).
      if (
        chunkCount > 0 &&
        this.crossingResolutionNowMs() - startedAtMs >= budgetMs
      ) {
        return {
          processedMs,
          remainingMs,
          chunkCount,
          stoppedReason: "budget",
        };
      }
      const chunkMs = Math.min(remainingMs, chunkSpanMs);
      this.drainGameplayAutomationEvents(chunkMs);
      remainingMs = Math.max(0, remainingMs - chunkMs);
      processedMs += chunkMs;
      chunkCount += 1;
    }

    return { processedMs, remainingMs, chunkCount, stoppedReason: "completed" };
  }

  private resetPerGameplayAdvanceBuffers(): void {
    this.hovEarnedThisTick = hugeZero();
    this.clearTimHovConsumptionObservation();
    this.gameplayAutomationEventSequence = 0;
    this.lastHovGainUpgradeMpAutomationEvents = [];
    this.lastResetAutomationDrainDiagnostic = null;
    this.lastResetCrossingResolutionStats =
      createResetCrossingResolutionStats();
  }

  /**
   * `precomputedContext`, when supplied, must be a
   * `getContinuousGameplayProjectionInputs().context` read with no
   * intervening state mutation (purchase, reset, hover input) since it was
   * built: every field but `dtSeconds` is `dtSeconds`-invariant (see
   * `createTickContext`), so a caller that already holds the current
   * substrate's context can reuse it here instead of paying a second
   * `resolveCurrentHovGainPipeline()` call for a value that cannot have
   * changed. Omit it to build a fresh context, as before.
   */
  private advanceContinuousGameplay(
    dtSeconds: number,
    precomputedContext?: HoverIdleTickContext,
  ): void {
    if (!Number.isFinite(dtSeconds) || dtSeconds <= 0) {
      return;
    }

    let remainingSeconds = dtSeconds;
    let reusableContext = precomputedContext;
    while (remainingSeconds > RESET_AUTOMATION_DRAIN_EPSILON_MS / 1000) {
      const nextExplorationElapsedMs =
        this.resolveNextExplorationBoundaryElapsedMs(remainingSeconds * 1_000);
      if (nextExplorationElapsedMs !== null && nextExplorationElapsedMs === 0) {
        this.advanceExploration(0);
        reusableContext = undefined;
        continue;
      }
      const explorationBoundarySeconds =
        nextExplorationElapsedMs !== null
          ? nextExplorationElapsedMs / 1_000
          : Number.POSITIVE_INFINITY;
      const segmentSeconds = Math.min(
        remainingSeconds,
        explorationBoundarySeconds,
      );
      this.advanceContinuousGameplaySegment(segmentSeconds, reusableContext);
      remainingSeconds = Math.max(0, remainingSeconds - segmentSeconds);
      reusableContext = undefined;
    }
    if (remainingSeconds > 0) {
      this.advanceContinuousGameplaySegment(remainingSeconds);
    }
  }

  private advanceContinuousGameplaySegment(
    dtSeconds: number,
    precomputedContext?: HoverIdleTickContext,
  ): void {
    if (!Number.isFinite(dtSeconds) || dtSeconds <= 0) return;

    const context =
      precomputedContext === undefined
        ? this.createTickContext(dtSeconds)
        : { ...precomputedContext, dtSeconds };
    const currentLoopHovEarnedBeforeTick = this.mHovState.currentLoopHovEarned;
    const timerDtSeconds = dtSeconds * this.gameplayElapsedLaneScales.timer;
    const amplifierProgressionDtSeconds =
      dtSeconds * this.gameplayElapsedLaneScales.amplifierProgression;

    this.applyPassiveResourceTick(context);
    this.applyMpHovTick(context);
    this.applyActiveHovTick(context);
    this.applyActiveTimTick(context);
    this.applyHovAmplifierTick(amplifierProgressionDtSeconds);

    this.applyHiddenGainGrowth(dtSeconds);
    this.applyHovTimGrowth(dtSeconds);
    this.advanceSecretProgressTimers(
      timerDtSeconds,
      compareHuge(
        this.mHovState.currentLoopHovEarned,
        currentLoopHovEarnedBeforeTick,
      ) > 0,
    );
    this.applyPlaytimeAndMaxHovTick(timerDtSeconds);
    this.advanceOHovCurrentRunGameElapsed(timerDtSeconds * 1000);
    this.advanceWallRunElapsed(timerDtSeconds * 1000);
    this.advanceGameplayElapsedSinceAnyReset(timerDtSeconds * 1000);
    this.advanceResetAutomationMinimumGameplayIntervalCarry(
      timerDtSeconds * 1000,
    );
    this.advanceExploration(timerDtSeconds * 1000);
    this.syncMaxHovSinceResets();
  }

  private advanceExploration(gameElapsedMs: number): void {
    if (!this.skillExplorationUnlocked) return;
    const participation = this.getExplorationParticipationByBiome();
    const hasParticipation = EXPLORATION_BIOME_IDS.some(
      (biomeId) => participation[biomeId] > 0,
    );
    const accelerationPlan = resolveExplorationTimAccelerationPlan({
      gameElapsedMs,
      availableTim: this.tim,
      hasParticipation,
      unlocked: this.explorationTimAccelerationUnlocked,
      enabled: this.explorationState.timAccelerationEnabled,
      definition: this.explorationDefinition.timAcceleration,
    });
    if (compareHuge(accelerationPlan.timConsumed, 0) > 0) {
      this.tim = maxHuge(this.tim.sub(accelerationPlan.timConsumed), 0);
      this.cumulativeStats = {
        ...this.cumulativeStats,
        totalTimSpent: this.cumulativeStats.totalTimSpent.add(
          accelerationPlan.timConsumed,
        ),
      };
    }

    if (accelerationPlan.acceleratedGameElapsedMs > 0) {
      this.applyExplorationProgressionResult(
        advanceExploration({
          state: this.explorationState,
          definition: this.explorationDefinition,
          participation,
          gameElapsedMs: accelerationPlan.acceleratedGameElapsedMs,
          speedMultipliers: this.createExplorationSpeedMultipliers(
            this.explorationDefinition.timAcceleration.speedMultiplier,
          ),
        }),
      );
    }
    if (
      accelerationPlan.normalGameElapsedMs > 0 ||
      accelerationPlan.acceleratedGameElapsedMs === 0
    ) {
      this.applyExplorationProgressionResult(
        advanceExploration({
          state: this.explorationState,
          definition: this.explorationDefinition,
          participation,
          gameElapsedMs: accelerationPlan.normalGameElapsedMs,
        }),
      );
    }
  }

  private applyExplorationProgressionResult(
    result: ReturnType<typeof advanceExploration>,
  ): void {
    this.explorationState = result.state;

    let shouldSyncMainResourceGains = false;
    if (result.effects.ohovPowerAwarded > 0) {
      this.oHovState = {
        ...this.oHovState,
        power: this.oHovState.power.add(result.effects.ohovPowerAwarded),
      };
      shouldSyncMainResourceGains = true;
    }
    if (compareHuge(result.effects.hgMultiplier, 1) !== 0) {
      this.mHovState = {
        ...this.mHovState,
        hgMultiplier: this.mHovState.hgMultiplier.mul(
          result.effects.hgMultiplier,
        ),
      };
      shouldSyncMainResourceGains = true;
    }
    if (shouldSyncMainResourceGains) {
      this.syncMainResourceGains();
    }
  }

  private resolveNextExplorationBoundaryElapsedMs(
    remainingContinuousElapsedMs: number,
  ): number | null {
    if (!this.skillExplorationUnlocked) return null;
    const timerScale = this.gameplayElapsedLaneScales.timer;
    if (timerScale <= 0) return null;
    const participation = this.getExplorationParticipationByBiome();
    const remainingGameElapsedMs = remainingContinuousElapsedMs * timerScale;
    const accelerationPlan = resolveExplorationTimAccelerationPlan({
      gameElapsedMs: remainingGameElapsedMs,
      availableTim: this.tim,
      hasParticipation: EXPLORATION_BIOME_IDS.some(
        (biomeId) => participation[biomeId] > 0,
      ),
      unlocked: this.explorationTimAccelerationUnlocked,
      enabled: this.explorationState.timAccelerationEnabled,
      definition: this.explorationDefinition.timAcceleration,
    });
    const acceleratedSpeedMultipliers = this.createExplorationSpeedMultipliers(
      this.explorationDefinition.timAcceleration.speedMultiplier,
    );
    const acceleratedBoundaryMs =
      accelerationPlan.acceleratedGameElapsedMs > 0
        ? resolveNextExplorationCompletionElapsedMs({
            state: this.explorationState,
            definition: this.explorationDefinition,
            participation,
            speedMultipliers: acceleratedSpeedMultipliers,
          })
        : null;
    if (
      acceleratedBoundaryMs !== null &&
      acceleratedBoundaryMs <=
        accelerationPlan.acceleratedGameElapsedMs +
          RESET_AUTOMATION_DRAIN_EPSILON_MS
    ) {
      return acceleratedBoundaryMs / timerScale;
    }

    const stateAfterAcceleration =
      accelerationPlan.acceleratedGameElapsedMs > 0
        ? advanceExploration({
            state: this.explorationState,
            definition: this.explorationDefinition,
            participation,
            gameElapsedMs: accelerationPlan.acceleratedGameElapsedMs,
            speedMultipliers: acceleratedSpeedMultipliers,
          }).state
        : this.explorationState;
    const normalBoundaryMs = resolveNextExplorationCompletionElapsedMs({
      state: stateAfterAcceleration,
      definition: this.explorationDefinition,
      participation,
    });
    const gameElapsedMs =
      normalBoundaryMs === null
        ? null
        : accelerationPlan.acceleratedGameElapsedMs + normalBoundaryMs;
    if (gameElapsedMs === null) return null;
    const continuousElapsedMs = gameElapsedMs / timerScale;
    return continuousElapsedMs <=
      remainingContinuousElapsedMs + RESET_AUTOMATION_DRAIN_EPSILON_MS
      ? Math.max(0, continuousElapsedMs)
      : null;
  }

  private getExplorationParticipationByBiome(): Readonly<
    Record<ExplorationBiomeId, number>
  > {
    const assignedMp = getExplorationAssignedMpByBiome({
      definition: this.explorationDefinition,
      mpmState: this.mpmState,
    });
    if (this.explorationPointerBiomeId === null) return assignedMp;
    return {
      ...assignedMp,
      [this.explorationPointerBiomeId]:
        assignedMp[this.explorationPointerBiomeId] + 1,
    };
  }

  private createExplorationSpeedMultipliers(
    multiplier: number,
  ): Readonly<Record<ExplorationBiomeId, number>> {
    return {
      hov: multiplier,
      tim: multiplier,
      mat: multiplier,
      lev: multiplier,
    };
  }

  private drainGameplayAutomationEvents(totalDeltaMs: number): void {
    this.runGameplayAutomationDrainCore(totalDeltaMs, {
      maxResetEvents: RESET_AUTOMATION_DRAIN_MAX_EVENTS_PER_UPDATE,
      maxHguSegments: HOV_GAIN_UPGRADE_MP_DRAIN_MAX_SEGMENTS_PER_UPDATE,
    });
  }

  /**
   * Event-driven automation drain core for bounded gameplay advancement.
   * Fires ready reset/HGU-MP automation, or otherwise advances continuous
   * gameplay to the next crossing boundary until `totalDeltaMs` is consumed
   * or an event budget is exhausted.
   */
  private runGameplayAutomationDrainCore(
    totalDeltaMs: number,
    caps: GameplayAutomationDrainCaps,
  ): GameplayAutomationDrainCoreResult {
    let remainingMs = totalDeltaMs;
    let resetEventsResolved = 0;
    let hguSegmentsResolved = 0;
    let hguPurchasesResolved = 0;
    let hguBulkFoldHitCount = 0;
    let hguBulkFoldedCadenceCount = 0;
    let hguBulkFoldFallbackCount = 0;
    let hguRunFoldChunkCount = 0;
    let hguRunFoldFireCount = 0;
    let hguRunFoldPurchaseCount = 0;
    const chunkFoldEligible = totalDeltaMs >= HGU_RUN_FOLD_MIN_CORE_DELTA_MS;
    // Once a chunk's purchases outrun the rate-growth limit, the rest of this
    // bounded invocation stays on the exact per-fire path.
    let chunkFoldSuppressed = false;

    const buildResult = (
      stopReason: GameplayAutomationDrainCoreResult["stopReason"],
    ): GameplayAutomationDrainCoreResult => ({
      remainingMs: 0,
      resetEventsResolved,
      hguSegmentsResolved,
      hguPurchasesResolved,
      hguBulkFoldHitCount,
      hguBulkFoldedCadenceCount,
      hguBulkFoldFallbackCount,
      hguRunFoldChunkCount,
      hguRunFoldFireCount,
      hguRunFoldPurchaseCount,
      stopReason,
    });

    this.syncHovGainUpgradeMpAutomationCadences();
    // External mutation surfaces (admin edits, restoreState, skill effects,
    // hover input) between drain() calls make any carried substrate from a
    // prior invocation unsafe to assume valid; drop it unconditionally here
    // so this invocation's cache only ever reflects state it observed itself.
    this.invalidateContinuousGameplayProjectionInputsCache();
    this.invalidateHovGainUpgradeMpThresholdCachesHard();

    // Progress-signature carry-over: an iteration that ends at the signature
    // comparison leaves the state unchanged until the next iteration's work,
    // so its "after" signature doubles as the next "before". Paths that
    // advance gameplay and `continue` early reset this to null to force a
    // fresh signature.
    let progressBefore: string | null = null;

    const flushRemainder = (advanceHguCadence = true): void => {
      this.advanceContinuousGameplay(remainingMs / 1000);
      if (advanceHguCadence) {
        this.advanceHovGainUpgradeMpAutomationCadence(remainingMs);
      }
    };

    while (remainingMs > RESET_AUTOMATION_DRAIN_EPSILON_MS) {
      if (resetEventsResolved >= caps.maxResetEvents) {
        this.lastResetAutomationDrainDiagnostic = {
          reason: "event-cap",
          eventsResolved: resetEventsResolved,
          hguSegmentsResolved,
          hguPurchasesResolved,
          remainingMs,
        };
        flushRemainder(false);
        return buildResult("event-cap");
      }

      if (hguSegmentsResolved >= caps.maxHguSegments) {
        this.lastResetAutomationDrainDiagnostic = {
          reason: "hgu-segment-cap",
          eventsResolved: resetEventsResolved,
          hguSegmentsResolved,
          hguPurchasesResolved,
          remainingMs,
          attemptedKind: "hov-gain-upgrade-mp-automation",
        };
        flushRemainder(false);
        return buildResult("hgu-segment-cap");
      }

      progressBefore ??= this.createGameplayAutomationProgressSignature();
      const readyResetKind = this.selectNextResetAutomationEventKind();
      if (readyResetKind !== null) {
        const eventKind = this.resolveOneRuntimeAutomationEvent();
        if (eventKind === null) {
          this.lastResetAutomationDrainDiagnostic = {
            reason: "no-ready-at-crossing",
            eventsResolved: resetEventsResolved,
            hguSegmentsResolved,
            hguPurchasesResolved,
            remainingMs,
            crossingMs: 0,
            attemptedKind: readyResetKind,
          };
          flushRemainder();
          return buildResult("no-ready-at-crossing");
        }
        resetEventsResolved += 1;
        // A reset can replace hovCells/mHovState/nHovState/hovAmplifierState
        // wholesale (see adoptHoverResetProgressStateGroups) without
        // necessarily changing all four cache-key references, so invalidate
        // explicitly rather than relying on the reference check. The HGU-MP
        // threshold caches key on hovCells/nHovState too and get the same
        // explicit drop for the same reason.
        this.invalidateContinuousGameplayProjectionInputsCache();
        this.invalidateHovGainUpgradeMpThresholdCachesHard();
      } else if (this.hasReadyHovGainUpgradeMpAutomationCadence()) {
        const result = this.executeHovGainUpgradeMpReadyCadenceDrain({
          bulkEligible: false,
          maxCadences: caps.maxHguSegments - hguSegmentsResolved,
        });
        if (!result.didProcessCadence) {
          this.lastResetAutomationDrainDiagnostic = {
            reason: "no-ready-at-crossing",
            eventsResolved: resetEventsResolved,
            hguSegmentsResolved,
            hguPurchasesResolved,
            remainingMs,
            crossingMs: 0,
            attemptedKind: "hov-gain-upgrade-mp-automation",
          };
          flushRemainder();
          return buildResult("no-ready-at-crossing");
        }
        hguSegmentsResolved += result.cadenceCount;
        hguPurchasesResolved += result.purchaseCount;
        hguBulkFoldHitCount += result.bulkHitCount;
        hguBulkFoldedCadenceCount += result.bulkFoldedCadenceCount;
        hguBulkFoldFallbackCount += result.fallbackCount;
        if (result.purchaseCount > 0) {
          // A purchase mutates a HovCellState in place or bumps
          // purchasedHovGainUpgrades, neither of which is one of the cache's
          // four reference keys, so invalidate explicitly.
          this.invalidateContinuousGameplayProjectionInputsCache();
        }
      } else {
        const hguSpanResult =
          this.advanceHovGainUpgradeMpHomogeneousCadenceSpan({
            enabled: chunkFoldEligible,
            chunkFoldEligible: chunkFoldEligible && !chunkFoldSuppressed,
            recordChunkEvents: true,
            bulkDrainEligible: false,
            maxCadences: caps.maxHguSegments - hguSegmentsResolved,
            remainingMs,
          });
        if (hguSpanResult.runFoldSuppressedByRateGrowth) {
          chunkFoldSuppressed = true;
        }
        if (hguSpanResult.didProcessCadence) {
          remainingMs = hguSpanResult.remainingMs;
          hguSegmentsResolved += hguSpanResult.cadenceCount;
          hguPurchasesResolved += hguSpanResult.purchaseCount;
          hguBulkFoldHitCount += hguSpanResult.bulkHitCount;
          hguBulkFoldedCadenceCount += hguSpanResult.bulkFoldedCadenceCount;
          hguBulkFoldFallbackCount += hguSpanResult.fallbackCount;
          hguRunFoldChunkCount += hguSpanResult.runFoldChunkCount;
          hguRunFoldFireCount += hguSpanResult.runFoldFireCount;
          hguRunFoldPurchaseCount += hguSpanResult.runFoldPurchaseCount;
          if (hguSpanResult.purchaseCount > 0) {
            this.invalidateContinuousGameplayProjectionInputsCache();
          }
          progressBefore = null;
          continue;
        }

        // readyResetKind is already known null here (this branch is only
        // reached when the readiness check above found nothing ready), so
        // pass it through instead of re-deriving it inside the resolver.
        const resetCrossingMs = this.resolveNextResetCrossingElapsedMs(
          remainingMs,
          readyResetKind,
        );
        const hguCrossingMs =
          this.resolveNextHovGainUpgradeMpAutomationElapsedMs(remainingMs);
        const explorationCrossingMs =
          this.resolveNextExplorationBoundaryElapsedMs(remainingMs);
        const boundaryMs = Math.min(
          resetCrossingMs ?? remainingMs,
          hguCrossingMs ?? remainingMs,
          explorationCrossingMs ?? remainingMs,
          remainingMs,
        );
        this.advanceContinuousGameplay(boundaryMs / 1000);
        this.advanceHovGainUpgradeMpAutomationCadenceThroughBoundary(
          boundaryMs,
        );
        remainingMs -= boundaryMs;
        progressBefore = null;
        continue;
      }

      const progressAfter = this.createGameplayAutomationProgressSignature();
      const madeProgress = progressAfter !== progressBefore;
      progressBefore = progressAfter;
      if (!madeProgress) {
        if (
          readyResetKind === null &&
          !this.hasReadyHovGainUpgradeMpAutomationCadence()
        ) {
          continue;
        }
        const stopReason =
          readyResetKind === null ? "hgu-no-progress" : "no-progress";
        this.lastResetAutomationDrainDiagnostic = {
          reason: stopReason,
          eventsResolved: resetEventsResolved,
          hguSegmentsResolved,
          hguPurchasesResolved,
          remainingMs,
          crossingMs: 0,
          attemptedKind: readyResetKind ?? "hov-gain-upgrade-mp-automation",
        };
        flushRemainder();
        return buildResult(stopReason);
      }
    }

    if (remainingMs > 0) {
      flushRemainder();
    }
    return buildResult(null);
  }

  private createHovGainUpgradeMpAutomationCadenceConfig(): UpgradeRepeatCadenceConfig {
    const initialIntervalMs =
      Number.isFinite(this.hovGainFamilyUpgradeRepeatInitialIntervalMs) &&
      this.hovGainFamilyUpgradeRepeatInitialIntervalMs > 0
        ? this.hovGainFamilyUpgradeRepeatInitialIntervalMs
        : DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS;
    return {
      initialIntervalMs,
      minIntervalMs: UPGRADE_HOVER_REPEATER_MIN_INTERVAL_MS,
      decay: UPGRADE_HOVER_REPEATER_DEFAULT_DECAY,
    };
  }

  private syncHovGainUpgradeMpAutomationCadences(): void {
    if (
      this.hovGainUpgradeMpCadenceSyncMpmState === this.mpmState &&
      this.hovGainUpgradeMpCadenceSyncWallState === this.wallState &&
      this.hovGainUpgradeMpCadenceSyncInitialIntervalMs ===
        this.hovGainFamilyUpgradeRepeatInitialIntervalMs
    ) {
      return;
    }
    this.hovGainUpgradeMpCadenceSyncMpmState = this.mpmState;
    this.hovGainUpgradeMpCadenceSyncWallState = this.wallState;
    this.hovGainUpgradeMpCadenceSyncInitialIntervalMs =
      this.hovGainFamilyUpgradeRepeatInitialIntervalMs;
    const activeTargets = this.getEffectiveHovGainUpgradeDirectMpAssignments();
    const nextTargets = new Map<
      HovGainUpgradeMpTargetKey,
      HovGainUpgradeMpTarget
    >();
    for (const target of activeTargets) {
      const key = toHovGainUpgradeMpTargetKey(target);
      if (key === null || nextTargets.has(key)) {
        continue;
      }
      nextTargets.set(key, target);
    }

    for (const key of [...this.hovGainUpgradeMpAutomationCadences.keys()]) {
      if (!nextTargets.has(key)) {
        this.hovGainUpgradeMpAutomationCadences.delete(key);
      }
    }

    const config = this.createHovGainUpgradeMpAutomationCadenceConfig();
    for (const [key, target] of nextTargets) {
      const existing = this.hovGainUpgradeMpAutomationCadences.get(key);
      if (existing === undefined) {
        this.hovGainUpgradeMpAutomationCadences.set(key, {
          target,
          config,
          state: createUpgradeRepeatCadenceState(config),
        });
        continue;
      }
      existing.config = config;
    }
  }

  private advanceHovGainUpgradeMpAutomationCadence(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
      return;
    }
    this.syncHovGainUpgradeMpAutomationCadences();
    for (const cadence of this.hovGainUpgradeMpAutomationCadences.values()) {
      if (!cadence.state.hasFiredInitial) {
        continue;
      }
      cadence.state.accumulatedRepeatMs += elapsedMs;
    }
  }

  // Advance-to-boundary counterpart of advanceHovGainUpgradeMpAutomationCadence,
  // used only by the drain loop's no-ready-cadence branch. boundaryMs there is
  // always the min of every cadence's own resolveNextHovGainUpgradeMpAutomationElapsedMs
  // contribution (plus the reset crossing / remaining budget), so boundaryMs is
  // provably <= every cadence's own contribution term. advanceUpgradeRepeatCadenceStarved
  // folds starved fires strictly before boundaryMs and is a no-op-equivalent
  // raw accumulation whenever boundaryMs does not reach a cadence's own first
  // pending fire (the "affordable now" / "unclassifiable" contribution cases,
  // which are exactly readinessMs) — so it is safe to call uniformly here
  // instead of threading a separate per-cadence fold/no-fold classification.
  // Do not reuse this for flushRemainder or other call sites: their elapsedMs
  // is not derived from that per-cadence min, so the safety argument does not
  // carry over there.
  private advanceHovGainUpgradeMpAutomationCadenceThroughBoundary(
    boundaryMs: number,
  ): void {
    if (!Number.isFinite(boundaryMs) || boundaryMs <= 0) {
      return;
    }
    this.syncHovGainUpgradeMpAutomationCadences();
    for (const cadence of this.hovGainUpgradeMpAutomationCadences.values()) {
      advanceUpgradeRepeatCadenceStarved(
        cadence.state,
        cadence.config,
        boundaryMs,
      );
    }
  }

  // Affordability-aware HGU-MP boundary: for a cadence that cannot afford its
  // next fire right now, this projects forward to the first starved-schedule
  // fire time at or after the closed-form affordability crossing instead of
  // the plain next-readiness time, so a long no-purchase span folds instead of
  // visiting every cadence fire. See "No-reset interval substrate carry
  // conditions" in docs/audits/2026-07-segment-solver-journal.md for why this
  // is safe only across no-reset/no-purchase/reference-stable spans (exactly
  // the drain loop's no-ready-cadence branch).
  private resolveNextHovGainUpgradeMpAutomationElapsedMs(
    remainingMs: number,
  ): number | null {
    this.syncHovGainUpgradeMpAutomationCadences();
    const inputs = this.getContinuousGameplayProjectionInputs();
    let bestMs: number | null = null;
    for (const [key, cadence] of this.hovGainUpgradeMpAutomationCadences) {
      const elapsedMs = this.resolveHovGainUpgradeMpCadenceBoundaryMs(
        key,
        cadence,
        remainingMs,
        inputs,
      );
      if (elapsedMs === null) {
        continue;
      }
      if (elapsedMs > remainingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS) {
        continue;
      }
      if (bestMs === null || elapsedMs < bestMs) {
        bestMs = elapsedMs;
      }
    }
    return bestMs;
  }

  // Resolves one cadence's contribution to the boundary above. Returns
  // readinessMs unchanged (today's exact behavior — no fold) whenever the
  // cadence is already affordable, the target is currently unavailable
  // (upgradeAvailable can flip true on its own, see the threshold===null
  // comment below), or the closed-form model cannot be trusted; returns null
  // when the cadence is affordable and provably cannot fire-and-buy before
  // remainingMs (no boundary contribution — every fire in the window folds);
  // otherwise returns the quantized starved-schedule fire time at or after
  // the affordability crossing, per the safety argument on
  // advanceHovGainUpgradeMpAutomationCadenceThroughBoundary above. The
  // crossing estimate is biased HGU_AFFORDABILITY_CROSSING_SAFETY_MARGIN_MS
  // earlier than the closed-form root: an early estimate only costs one extra
  // (correctly no-op) real plan-build at the resulting boundary, while a late
  // estimate would silently fold past a fire that could have purchased
  // something.
  private resolveHovGainUpgradeMpCadenceBoundaryMs(
    key: HovGainUpgradeMpTargetKey,
    cadence: HovGainUpgradeMpAutomationCadence,
    remainingMs: number,
    inputs: ContinuousGameplayProjectionInputs,
  ): number | null {
    const readinessMs = this.resolveHovGainUpgradeMpCadenceElapsedMs(cadence);
    if (readinessMs > remainingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS) {
      return null;
    }
    if (!inputs.quadraticEligible) {
      return readinessMs;
    }
    const threshold = this.resolveHovGainUpgradeMpFirstPurchaseThreshold(
      key,
      cadence.target,
    );
    if (threshold === null) {
      // Unavailable is not span-stable the way currentRawCost/upgradeAvailable's
      // *cost* side is: Hov-cell upgradeAvailable also gates on hovGridTier,
      // which syncThresholdUnlocks() raises continuously from
      // mHovState.currentLoopHovEarned inside advanceContinuousGameplay
      // (applyPassiveResourceTick/applyMpHovTick) — not just on reset/purchase
      // boundaries that invalidate this method's cache. Unlike the cost side,
      // grid-tier availability itself *is* a monotone function of
      // currentLoopHovEarned within this no-reset span (see
      // resolveHovGainUpgradeMpAvailabilityFoldedBoundaryMs), so this folds to
      // the availability crossing instead of falling all the way back to the
      // plain per-fire boundary.
      return this.resolveHovGainUpgradeMpAvailabilityFoldedBoundaryMs(
        key,
        cadence,
        readinessMs,
        remainingMs,
        inputs,
      );
    }
    if (compareHuge(this.hov, threshold) >= 0) {
      return readinessMs;
    }
    const hovCrossingSeconds = this.solveQuadraticCrossingSeconds(
      inputs.linearHovDeltaPerSecond,
      inputs.quadraticHovDeltaPerSecondSquared,
      threshold.sub(this.hov),
    );
    if (hovCrossingSeconds === null) {
      return null;
    }
    const hovCrossingMsHuge = hovCrossingSeconds.mul(1000);
    if (!hovCrossingMsHuge.isFinite()) {
      return readinessMs;
    }
    const affordabilityCrossingMs = Math.max(
      0,
      hovCrossingMsHuge.toNumber() -
        HGU_AFFORDABILITY_CROSSING_SAFETY_MARGIN_MS,
    );
    return this.quantizeStarvedCadenceCrossingMs(
      readinessMs,
      cadence.config.initialIntervalMs,
      affordabilityCrossingMs,
      remainingMs,
    );
  }

  // Grid-tier fold counterpart to the affordability crossing above: a
  // currently-unavailable Hov-cell target's availability is a monotone
  // function of mHovState.currentLoopHovEarned within a no-reset/no-purchase
  // span (see resolveHovCellGridTierAvailabilityThreshold for the exact
  // gating rule and why it is span-stable), so this projects forward to the
  // first starved-schedule fire time at or after that crossing rather than
  // visiting every no-op fire. Returns readinessMs unchanged (today's exact
  // per-fire behavior — no fold) for the "global" target (never grid-gated;
  // a null cost threshold for it means a non-tier gate this method does not
  // model) and for any target whose unavailability is not reducible to a
  // monotone currentLoopHovEarned threshold (active-wall blocks — a wall can
  // be stopped externally mid-span, so "still blocked" is not provable from
  // projected growth). Returns null only when the monotone threshold provably
  // cannot be crossed by any fire before remainingMs (no boundary
  // contribution — every fire in the window folds; the span-ending
  // reset/purchase bounds it instead). Conservative-early is safe for the
  // same reason as the affordability crossing: an early estimate costs one
  // extra no-op real plan-build, and that plan-build's own (uncached)
  // availability check is still the authoritative gate.
  private resolveHovGainUpgradeMpAvailabilityFoldedBoundaryMs(
    key: HovGainUpgradeMpTargetKey,
    cadence: HovGainUpgradeMpAutomationCadence,
    readinessMs: number,
    remainingMs: number,
    inputs: ContinuousGameplayProjectionInputs,
  ): number | null {
    if (cadence.target === "global") {
      return readinessMs;
    }
    const cellIndex = cadence.target.index - 1;
    const availabilityThreshold =
      this.resolveHovGainUpgradeMpAvailabilityThresholdRunBaseHov(
        key,
        cellIndex,
      );
    if (availabilityThreshold === null) {
      return readinessMs;
    }
    if (
      compareHuge(this.mHovState.currentLoopHovEarned, availabilityThreshold) >=
      0
    ) {
      return readinessMs;
    }
    const crossingSeconds = this.solveQuadraticCrossingSeconds(
      inputs.linearHovDeltaPerSecond,
      inputs.quadraticHovDeltaPerSecondSquared,
      availabilityThreshold.sub(this.mHovState.currentLoopHovEarned),
    );
    if (crossingSeconds === null) {
      return null;
    }
    const crossingMsHuge = crossingSeconds.mul(1000);
    if (!crossingMsHuge.isFinite()) {
      return readinessMs;
    }
    const availabilityCrossingMs = Math.max(
      0,
      crossingMsHuge.toNumber() - HGU_AFFORDABILITY_CROSSING_SAFETY_MARGIN_MS,
    );
    return this.quantizeStarvedCadenceCrossingMs(
      readinessMs,
      cadence.config.initialIntervalMs,
      availabilityCrossingMs,
      remainingMs,
    );
  }

  // Quantizes a closed-form Hov-family crossing estimate (affordability or
  // grid-tier availability) onto the starved schedule's fire grid (first
  // fire at readinessMs, then every initialIntervalMs), bounded by
  // remainingMs. Returns readinessMs unchanged when the crossing is already
  // at or before the next fire, and null when the quantized fire would land
  // past remainingMs (no boundary contribution — every fire in the window
  // folds).
  private quantizeStarvedCadenceCrossingMs(
    readinessMs: number,
    initialIntervalMs: number,
    crossingMs: number,
    remainingMs: number,
  ): number | null {
    if (crossingMs <= readinessMs) {
      return readinessMs;
    }
    const intervalsPastReadiness = Math.ceil(
      (crossingMs - readinessMs) / initialIntervalMs,
    );
    const quantizedFireMs =
      readinessMs + intervalsPastReadiness * initialIntervalMs;
    if (quantizedFireMs > remainingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS) {
      return null;
    }
    return quantizedFireMs;
  }

  // First-purchase threshold memo. See the field comment on
  // hovGainUpgradeMpFirstPurchaseThresholdCache for why entries are keyed by
  // the target's own (currentRawCost, upgradeAvailable) pair instead of the
  // substrate-inputs instance: those two are exactly what
  // resolveHovGainUpgradeFirstPurchaseThreshold's result depends on
  // (currentRawCost/effectiveCostMultiplier/upgradeAvailable — never
  // availableHov), and the achievement cost multiplier / nHov cost divisor
  // that feed effectiveCostMultiplier are frozen for the lifetime of one
  // drain invocation (achievement effects only change between frames at the
  // app layer; the nHov divisor only changes via an NHov reset, one of this
  // cache's hard-invalidation triggers) — so they need no key of their own.
  private resolveHovGainUpgradeMpFirstPurchaseThreshold(
    key: HovGainUpgradeMpTargetKey,
    target: HovGainUpgradeMpTarget,
  ): HugeNumber | null {
    const rawCostRef = this.resolveHovGainUpgradeMpTargetRawCost(target);
    const available = this.resolveHovGainUpgradeMpTargetAvailability(target);
    const cache = (this.hovGainUpgradeMpFirstPurchaseThresholdCache ??=
      new Map());
    const cached = cache.get(key);
    if (
      cached !== undefined &&
      cached.rawCostRef === rawCostRef &&
      cached.available === available
    ) {
      return cached.value;
    }
    const plannerInput = this.resolveHovGainUpgradeMpAutomationPlannerInput(
      target,
      "one-cost-bulk",
    );
    const value = resolveHovGainUpgradeFirstPurchaseThreshold(plannerInput);
    cache.set(key, { rawCostRef, available, value });
    return value;
  }

  // Grid-tier availability-crossing threshold memo (see the field comment on
  // hovGainUpgradeMpAvailabilityThresholdCache). Caches the
  // currentLoopHovEarned threshold constant itself, not a live availability
  // boolean, so it stays correct even though hovGridTier can keep rising
  // between lookups; keyed by the cell's own `unlocked` flag since that is
  // this threshold's only purchase-driven input (see
  // resolveHovCellGridTierAvailabilityThreshold).
  private resolveHovGainUpgradeMpAvailabilityThresholdRunBaseHov(
    key: HovGainUpgradeMpTargetKey,
    cellIndex: number,
  ): HugeNumber | null {
    const unlocked = this.hovCells[cellIndex]?.unlocked ?? false;
    const cache = (this.hovGainUpgradeMpAvailabilityThresholdCache ??=
      new Map());
    const cached = cache.get(key);
    if (cached !== undefined && cached.unlocked === unlocked) {
      return cached.value;
    }
    const value = this.resolveHovCellGridTierAvailabilityThreshold(cellIndex);
    cache.set(key, { unlocked, value });
    return value;
  }

  // Drops both HGU-MP threshold caches unconditionally. Call at the same two
  // points the projection substrate cache is explicitly invalidated beyond
  // its 4-reference guard: reset execution (hovCells/mHovState/nHovState/
  // wallState can be replaced wholesale) and drain entry (external mutation
  // between drain() calls). Purchase-only invalidation must NOT call this —
  // that is precisely the case the per-entry keys above already handle
  // without a full drop.
  private invalidateHovGainUpgradeMpThresholdCachesHard(): void {
    this.hovGainUpgradeMpFirstPurchaseThresholdCache = null;
    this.hovGainUpgradeMpAvailabilityThresholdCache = null;
  }

  // Shared target dispatch mirroring resolveHovGainUpgradeMpAutomationPlannerInput,
  // but returning only the cheap (currentRawCost, upgradeAvailable) pair the
  // threshold cache needs to validate an entry — building the full planner
  // input (NHov divisor lookup, HugeNumber division) is exactly the cost a
  // cache hit must avoid.
  private resolveHovGainUpgradeMpTargetRawCost(
    target: HovGainUpgradeMpTarget,
  ): HugeNumber {
    if (target === "global") {
      return this.upgradeCost;
    }
    const cell = this.hovCells[target.index - 1];
    if (cell === undefined) {
      throw new Error(`invalid Hov cell gain upgrade index: ${target.index}`);
    }
    return cell.upgradeCost;
  }

  private resolveHovGainUpgradeMpTargetAvailability(
    target: HovGainUpgradeMpTarget,
  ): boolean {
    if (target === "global") {
      return true;
    }
    const cellIndex = target.index - 1;
    const cell = this.hovCells[cellIndex];
    if (cell === undefined) {
      throw new Error(`invalid Hov cell gain upgrade index: ${target.index}`);
    }
    return this.resolveHovCellGainUpgradeAvailability(cellIndex, cell);
  }

  // Grid-tier availability threshold for a currently-unavailable Hov-cell HGU
  // target, expressed as a mHovState.currentLoopHovEarned value. Returns null
  // when unavailability is NOT reducible to that monotone threshold, so the
  // caller keeps the per-fire readinessMs fallback instead of folding:
  //   - the cell's unlock purchase is blocked by the active wall
  //     (isHovCellUnlockBlockedByActiveWall) — an external stopActiveWall()
  //     can lift the block mid-span without any reset/purchase boundary, so
  //     "still blocked at time t" is not a projectable function of
  //     currentLoopHovEarned, or
  //   - the cell needs the second grid increase (tier 9) and the active wall
  //     suppresses it (isSecondHovGridIncreaseBlockedByActiveWall) — same
  //     external-stop escape hatch.
  // Cells 0..HOV_GRID_INCREASE_1_TIER-1 share the first-increase threshold:
  // isHovCellGainUpgradeAvailable special-cases index 0 against the same
  // HOV_GRID_INCREASE_1_TIER constant that isCellVisibleForTier uses for the
  // other cells in range, and hovGridTier only ever takes the values 1, 4
  // (HOV_GRID_INCREASE_1_TIER), or 9 (HOV_GRID_INCREASE_2_TIER) — the only two
  // setHovGridTier call sites, both in syncThresholdUnlocks — so the two
  // gating paths cross at the same currentLoopHovEarned value.
  private resolveHovCellGridTierAvailabilityThreshold(
    cellIndex: number,
  ): HugeNumber | null {
    const cell = this.hovCells[cellIndex];
    if (cell === undefined) {
      return null;
    }
    if (this.isHovCellUnlockBlockedByActiveWall(cellIndex, cell.unlocked)) {
      return null;
    }
    if (cellIndex < HOV_GRID_INCREASE_1_TIER) {
      return huge(HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD);
    }
    if (cellIndex < HOV_GRID_INCREASE_2_TIER) {
      if (isSecondHovGridIncreaseBlockedByActiveWall(this.wallState)) {
        return null;
      }
      return huge(HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD);
    }
    return null;
  }

  // Shared target dispatch for planner-input construction. Mirrors the
  // global/cell branch in applyHovGainUpgradeMpAutomationPurchaseSegment; kept
  // as a separate small helper rather than folding that method into this one
  // because the apply path also needs the resolved cellIndex/cell for mutation
  // and unlock-index reporting beyond just the planner input.
  private resolveHovGainUpgradeMpAutomationPlannerInput(
    target: HovGainUpgradeMpTarget,
    mode: HovGainUpgradePurchaseMode,
  ): HovGainUpgradePurchasePlannerInput {
    if (target === "global") {
      return this.buildGlobalHovGainPlannerInput(mode);
    }
    const cellIndex = target.index - 1;
    const cell = this.hovCells[cellIndex];
    if (cell === undefined) {
      throw new Error(`invalid Hov cell gain upgrade index: ${target.index}`);
    }
    return this.buildHovCellGainPlannerInput(cellIndex, cell, mode);
  }

  private resolveHovGainUpgradeMpCadenceElapsedMs(
    cadence: HovGainUpgradeMpAutomationCadence,
  ): number {
    if (!cadence.state.hasFiredInitial) {
      return 0;
    }
    return Math.max(
      0,
      cadence.state.currentBaseIntervalMs - cadence.state.accumulatedRepeatMs,
    );
  }

  private hasReadyHovGainUpgradeMpAutomationCadence(): boolean {
    this.syncHovGainUpgradeMpAutomationCadences();
    for (const cadence of this.hovGainUpgradeMpAutomationCadences.values()) {
      if (
        this.resolveHovGainUpgradeMpCadenceElapsedMs(cadence) <=
        RESET_AUTOMATION_DRAIN_EPSILON_MS
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Shared homogeneous HGU-MP cadence span solver used by both drain-core
   * policies. Per-fire mode (always available) visits every purchase-capable
   * fire exactly, folding only outer-iteration bookkeeping. Chunk mode
   * (`chunkFoldEligible`, see {@link HGU_RUN_FOLD_MIN_CORE_DELTA_MS})
   * additionally coalesces fire runs: continuous gain accrues once per
   * chunk, then every target buys through one aggregate plan with the same
   * progressive-cost affordability rule. Because purchases apply only at the
   * chunk end, the projection substrate is stable across the chunk interior
   * and the exact reset-crossing resolver bounds every chunk: a chunk never
   * advances past a reset crossing computed on that stable substrate.
   *
   * Chunk sizes ramp up from the next fire boundary and self-limit through
   * {@link HGU_RUN_FOLD_CHUNK_RATE_GROWTH_LIMIT}: a chunk whose purchases
   * materially move the gain pipeline flags the regime as purchase->rate
   * compounding, and the rest of the stretch runs per-fire so compounding
   * progression (MHov cycling, plateau floods) keeps its exact cadence. Only
   * low-feedback runs — starved schedules, drip regimes whose purchases
   * barely move the pipeline — stay folded.
   */
  private advanceHovGainUpgradeMpHomogeneousCadenceSpan(options: {
    readonly enabled: boolean;
    readonly chunkFoldEligible: boolean;
    readonly recordChunkEvents: boolean;
    readonly bulkDrainEligible: boolean;
    readonly maxCadences: number;
    readonly remainingMs: number;
  }): HovGainUpgradeMpHomogeneousCadenceSpanResult {
    if (!options.enabled || options.maxCadences <= 0) {
      return {
        didProcessCadence: false,
        remainingMs: options.remainingMs,
        cadenceCount: 0,
        purchaseCount: 0,
        bulkHitCount: 0,
        bulkFoldedCadenceCount: 0,
        fallbackCount: 0,
        runFoldChunkCount: 0,
        runFoldFireCount: 0,
        runFoldPurchaseCount: 0,
        runFoldSuppressedByRateGrowth: false,
      };
    }

    let remainingMs = options.remainingMs;
    let cadenceCount = 0;
    let purchaseCount = 0;
    let runFoldChunkCount = 0;
    let runFoldFireCount = 0;
    let runFoldPurchaseCount = 0;
    let runFoldSuppressedByRateGrowth = false;
    let chunkRampMultiplier = 2;

    while (
      remainingMs > RESET_AUTOMATION_DRAIN_EPSILON_MS &&
      cadenceCount < options.maxCadences
    ) {
      const readyResetKind = this.selectNextResetAutomationEventKind();
      if (readyResetKind !== null) {
        break;
      }
      const hguCrossingMs =
        this.resolveNextHovGainUpgradeMpAutomationElapsedMs(remainingMs);
      if (hguCrossingMs === null) {
        break;
      }
      if (hguCrossingMs <= RESET_AUTOMATION_DRAIN_EPSILON_MS) {
        break;
      }
      const explorationCrossingMs =
        this.resolveNextExplorationBoundaryElapsedMs(remainingMs);
      if (
        explorationCrossingMs !== null &&
        explorationCrossingMs <=
          hguCrossingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS
      ) {
        break;
      }

      if (
        options.chunkFoldEligible &&
        !runFoldSuppressedByRateGrowth &&
        this.canChunkFoldHovGainUpgradeMpCadenceRun()
      ) {
        const chunkTargetMs = Math.min(
          remainingMs,
          HGU_RUN_FOLD_MAX_CHUNK_MS,
          hguCrossingMs * chunkRampMultiplier,
          explorationCrossingMs ?? remainingMs,
        );
        if (chunkTargetMs > hguCrossingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS) {
          const resetCrossingMs = this.resolveNextResetCrossingElapsedMs(
            chunkTargetMs,
            readyResetKind,
          );
          const chunkMs =
            resetCrossingMs === null
              ? chunkTargetMs
              : Math.min(chunkTargetMs, resetCrossingMs);
          if (chunkMs > hguCrossingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS) {
            const inputsBeforeChunk =
              this.getContinuousGameplayProjectionInputs();
            const rateBefore = inputsBeforeChunk.linearHovDeltaPerSecond;
            this.advanceContinuousGameplay(
              chunkMs / 1000,
              inputsBeforeChunk.context,
            );
            const chunkResult = this.executeHovGainUpgradeMpAutomationSegment(
              chunkMs,
              { recordEvent: options.recordChunkEvents },
            );
            remainingMs -= chunkMs;
            runFoldChunkCount += 1;
            runFoldFireCount += chunkResult.attemptCount;
            runFoldPurchaseCount += chunkResult.purchaseCount;
            if (chunkResult.didProcessCadence) {
              cadenceCount += Math.max(1, chunkResult.appliedAttemptCount);
              purchaseCount += chunkResult.purchaseCount;
            }
            if (chunkResult.purchaseCount > 0) {
              this.invalidateContinuousGameplayProjectionInputsCache();
            }
            const rateAfter =
              this.getContinuousGameplayProjectionInputs()
                .linearHovDeltaPerSecond;
            if (
              compareHuge(
                rateAfter,
                rateBefore.mul(HGU_RUN_FOLD_CHUNK_RATE_GROWTH_LIMIT),
              ) > 0
            ) {
              // Purchase->rate feedback inside this regime outruns the
              // deferred-purchase chunk model; hand the rest of the stretch
              // to the exact per-fire path (the drain core carries this
              // suppression across its remaining span calls).
              runFoldSuppressedByRateGrowth = true;
            } else {
              chunkRampMultiplier = Math.min(
                chunkRampMultiplier * 2,
                HGU_RUN_FOLD_RAMP_MAX,
              );
            }
            continue;
          }
          if (
            resetCrossingMs !== null &&
            resetCrossingMs <= hguCrossingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS
          ) {
            break;
          }
        }
      }

      if (this.wouldResetAutomationCrossBeforeHguCadence(hguCrossingMs)) {
        break;
      }

      // Cache-hit read: nothing between the boundary resolution above and
      // here mutates hovCells/mpmState/wallState/hiddenHovGainBonus/
      // hovAmplifierState, so this reuses the substrate already built for
      // hguCrossingMs instead of rebuilding it.
      const inputsAtBoundary = this.getContinuousGameplayProjectionInputs();
      this.advanceContinuousGameplay(
        hguCrossingMs / 1000,
        inputsAtBoundary.context,
      );
      this.advanceHovGainUpgradeMpAutomationCadenceThroughBoundary(
        hguCrossingMs,
      );
      remainingMs -= hguCrossingMs;

      const drainResult = this.executeHovGainUpgradeMpReadyCadenceDrain({
        bulkEligible: options.bulkDrainEligible,
        maxCadences: options.maxCadences - cadenceCount,
      });
      if (!drainResult.didProcessCadence) {
        break;
      }
      cadenceCount += drainResult.cadenceCount;
      purchaseCount += drainResult.purchaseCount;
      if (drainResult.purchaseCount > 0) {
        this.invalidateContinuousGameplayProjectionInputsCache();
      }
    }

    const didProcessCadence = cadenceCount > 0 || runFoldChunkCount > 0;
    if (!didProcessCadence) {
      return {
        didProcessCadence: false,
        remainingMs,
        cadenceCount: 0,
        purchaseCount: 0,
        bulkHitCount: 0,
        bulkFoldedCadenceCount: 0,
        fallbackCount: 0,
        runFoldChunkCount: 0,
        runFoldFireCount: 0,
        runFoldPurchaseCount: 0,
        runFoldSuppressedByRateGrowth,
      };
    }

    const didBulkFold = cadenceCount > 1;
    return {
      didProcessCadence: true,
      remainingMs,
      cadenceCount,
      purchaseCount,
      bulkHitCount: didBulkFold ? 1 : 0,
      bulkFoldedCadenceCount: didBulkFold ? cadenceCount : 0,
      fallbackCount: didBulkFold ? 0 : 1,
      runFoldChunkCount,
      runFoldFireCount,
      runFoldPurchaseCount,
      runFoldSuppressedByRateGrowth,
    };
  }

  // Chunk folding trusts the projection substrate and the reset-crossing
  // bound for the whole chunk interior, so it requires the same conditions as
  // the monotone endpoint probe (closed-form mode, no active Freshness Wall,
  // no enabled NHov assignments — NHov readiness is not monotone, so a wide
  // chunk could skip a transient readiness window the per-fire path would
  // catch; measured on all-mp as a ~20% NHov/MHov execution deficit) plus no
  // active hover interaction (`quadraticEligible`).
  private canChunkFoldHovGainUpgradeMpCadenceRun(): boolean {
    if (!this.canUseMonotoneResetEndpointProbeForHguSpan()) {
      return false;
    }
    return this.getContinuousGameplayProjectionInputs().quadraticEligible;
  }

  private wouldResetAutomationCrossBeforeHguCadence(
    elapsedMs: number,
  ): boolean {
    if (this.canUseResetEndpointProbeForHguSpan()) {
      // For the three monotone reset gates, readiness is monotone inside a
      // no-reset, no-purchase interval: a ready endpoint means the exact
      // resolver must handle the reset first; a non-ready endpoint proves the
      // gate cannot cross before this HGU cadence boundary. All checks share
      // the same elapsedMs, so the elapsed-dependent deltas (passive/MP
      // projection) are resolved once instead of once per kind.
      const inputs = this.getContinuousGameplayProjectionInputs();
      const deltas = this.projectContinuousGameplayHovDeltas(
        elapsedMs / 1000,
        inputs,
      );
      if (
        this.isResetAutomationEventReadyAtDeltas(
          "ohov-auto-execution",
          elapsedMs,
          deltas,
        ) ||
        this.isResetAutomationEventReadyAtDeltas(
          "ohov-mp-execution",
          elapsedMs,
          deltas,
        ) ||
        this.isResetAutomationEventReadyAtDeltas(
          "mhov-mp-execution",
          elapsedMs,
          deltas,
        )
      ) {
        return true;
      }
      // NHov readiness is not safely monotone, but the full resolver's own
      // boolean for this call site is also an endpoint readiness check: it
      // probes nhov-mp readiness at min(elapsedMs, best cheap crossing + eps)
      // and any cheap crossing already returns true above, so when the three
      // monotone gates are not ready the resolver's bound degenerates to
      // elapsedMs and this endpoint check returns the identical boolean at a
      // fraction of the closed-form + bisection cost.
      return (
        this.hasActiveNHovMpAutomationAssignments() &&
        this.isResetAutomationEventReadyAtDeltas(
          "nhov-mp-execution",
          elapsedMs,
          deltas,
        )
      );
    }

    const resetCrossingMs = this.resolveNextResetCrossingElapsedMs(
      elapsedMs,
      null,
    );
    return resetCrossingMs !== null;
  }

  // Chunk folding still requires the NHov-free monotone regime: a wide chunk
  // could skip a transient NHov readiness window the per-fire path would
  // catch (measured on all-mp as a ~20% NHov/MHov execution deficit). The
  // endpoint probe above does not share that restriction because it evaluates
  // exactly one boundary, matching the full resolver's endpoint semantics.
  private canUseMonotoneResetEndpointProbeForHguSpan(): boolean {
    return (
      this.canUseResetEndpointProbeForHguSpan() &&
      !this.hasActiveNHovMpAutomationAssignments()
    );
  }

  private canUseResetEndpointProbeForHguSpan(): boolean {
    if (this.resetCrossingResolutionMode !== "closed-form") {
      return false;
    }
    return !isFreshnessWallActive(this.wallState);
  }

  private hasActiveNHovMpAutomationAssignments(): boolean {
    const nhovSection = this.mpmState.nhovExecution;
    return (
      nhovSection.unlocked &&
      nhovSection.enabled &&
      nhovSection.assignments.length > 0
    );
  }

  private executeHovGainUpgradeMpReadyCadenceDrain(options: {
    readonly bulkEligible: boolean;
    readonly maxCadences: number;
  }): HovGainUpgradeMpReadyCadenceDrainResult {
    if (!options.bulkEligible || options.maxCadences <= 1) {
      const result = this.executeHovGainUpgradeMpAutomationSegment(0, {
        recordEvent: !options.bulkEligible,
      });
      return {
        didProcessCadence: result.didProcessCadence,
        cadenceCount: result.didProcessCadence ? 1 : 0,
        purchaseCount: result.purchaseCount,
        bulkHitCount: 0,
        bulkFoldedCadenceCount: 0,
        fallbackCount: options.bulkEligible ? 1 : 0,
      };
    }

    let cadenceCount = 0;
    let purchaseCount = 0;
    while (
      cadenceCount < options.maxCadences &&
      this.hasReadyHovGainUpgradeMpAutomationCadence()
    ) {
      const result = this.executeHovGainUpgradeMpAutomationSegment(0, {
        recordEvent: false,
      });
      if (!result.didProcessCadence) {
        break;
      }
      cadenceCount += 1;
      purchaseCount += result.purchaseCount;
    }

    if (cadenceCount <= 1) {
      return {
        didProcessCadence: cadenceCount === 1,
        cadenceCount,
        purchaseCount,
        bulkHitCount: 0,
        bulkFoldedCadenceCount: 0,
        fallbackCount: 1,
      };
    }

    return {
      didProcessCadence: true,
      cadenceCount,
      purchaseCount,
      bulkHitCount: 1,
      bulkFoldedCadenceCount: cadenceCount,
      fallbackCount: 0,
    };
  }

  private executeHovGainUpgradeMpAutomationSegment(
    elapsedMs: number,
    options: { readonly recordEvent?: boolean } = {},
  ): HovGainUpgradeMpAutomationSegmentResult {
    this.syncHovGainUpgradeMpAutomationCadences();
    let didProcessCadence = false;
    let attemptCount = 0;
    let appliedAttemptCount = 0;
    let purchaseCount = 0;
    const purchases: HoverIdleHovGainUpgradeMpAutomationPurchase[] = [];

    for (const cadence of this.hovGainUpgradeMpAutomationCadences.values()) {
      if (elapsedMs <= RESET_AUTOMATION_DRAIN_EPSILON_MS) {
        this.snapHovGainUpgradeMpCadenceToReadyBoundary(cadence);
      }
      let fireCount = advanceUpgradeRepeatCadenceAggregated(
        cadence.state,
        cadence.config,
        elapsedMs,
      );
      if (
        fireCount === 0 &&
        this.resolveHovGainUpgradeMpCadenceElapsedMs(cadence) <=
          RESET_AUTOMATION_DRAIN_EPSILON_MS
      ) {
        this.snapHovGainUpgradeMpCadenceToReadyBoundary(cadence);
        fireCount = advanceUpgradeRepeatCadenceAggregated(
          cadence.state,
          cadence.config,
          0,
        );
      }
      if (fireCount === 0) {
        continue;
      }
      didProcessCadence = true;
      attemptCount += fireCount;

      const result = this.applyHovGainUpgradeMpAutomationPurchaseSegment(
        cadence.target,
        fireCount,
      );
      if (result.attemptsApplied < fireCount) {
        resetUpgradeRepeatCadenceState(cadence.state, cadence.config);
        cadence.state.hasFiredInitial = true;
      }

      appliedAttemptCount += result.attemptsApplied;
      purchaseCount += result.summary.purchasesApplied;
      purchases.push(result.summary);
    }

    if (
      options.recordEvent !== false &&
      purchases.some((purchase) => purchase.purchasesApplied > 0)
    ) {
      this.lastHovGainUpgradeMpAutomationEvents.push({
        sequence: this.nextGameplayAutomationEventSequence(),
        kind: "hov-gain-upgrade-mp-automation",
        elapsedMs,
        purchases,
      });
    }
    return {
      didProcessCadence,
      attemptCount,
      appliedAttemptCount,
      purchaseCount,
    };
  }

  private snapHovGainUpgradeMpCadenceToReadyBoundary(
    cadence: HovGainUpgradeMpAutomationCadence,
  ): void {
    if (!cadence.state.hasFiredInitial) {
      return;
    }
    if (
      cadence.state.currentBaseIntervalMs - cadence.state.accumulatedRepeatMs >
      RESET_AUTOMATION_DRAIN_EPSILON_MS
    ) {
      return;
    }
    cadence.state.accumulatedRepeatMs = Math.max(
      cadence.state.accumulatedRepeatMs,
      cadence.state.currentBaseIntervalMs,
    );
  }

  private applyHovGainUpgradeMpAutomationPurchaseSegment(
    target: HovGainUpgradeMpTarget,
    attempts: number,
  ): {
    readonly attemptsApplied: number;
    readonly summary: HoverIdleHovGainUpgradeMpAutomationPurchase;
  } {
    if (target === "global") {
      const plan = resolveHovGainUpgradeAutomationSegmentPlan(
        this.buildGlobalHovGainPlannerInput("one-cost-bulk"),
        attempts,
      );
      if (plan.ok) {
        this.applyGlobalHovGainPurchasePlan(plan);
      }
      return {
        attemptsApplied: plan.attemptsApplied,
        summary: {
          target,
          attempts,
          purchasesApplied: plan.purchaseCount,
          hovSpent: plan.spentHov,
          unlockedTargetIndexes: [],
          enhancementLevelBefore: plan.enhancementLevelBefore,
          enhancementLevelAfter: plan.enhancementLevelAfter,
          milestonePurchaseCount: plan.milestonePurchases.length,
          lastMilestonePurchase: toLastBulkMilestoneRecord(plan),
        },
      };
    }

    const cellIndex = target.index - 1;
    const cell = this.hovCells[cellIndex];
    if (cell === undefined) {
      throw new Error(`invalid Hov cell gain upgrade index: ${target.index}`);
    }
    const wasUnlocked = cell.unlocked;
    const plan = resolveHovGainUpgradeAutomationSegmentPlan(
      this.buildHovCellGainPlannerInput(cellIndex, cell, "one-cost-bulk"),
      attempts,
    );
    if (plan.ok) {
      this.applyHovCellGainPurchasePlan(cellIndex, cell, plan);
    }
    return {
      attemptsApplied: plan.attemptsApplied,
      summary: {
        target,
        attempts,
        purchasesApplied: plan.purchaseCount,
        hovSpent: plan.spentHov,
        unlockedTargetIndexes:
          !wasUnlocked && cell.unlocked ? [target.index] : [],
        enhancementLevelBefore: plan.enhancementLevelBefore,
        enhancementLevelAfter: plan.enhancementLevelAfter,
        milestonePurchaseCount: plan.milestonePurchases.length,
        lastMilestonePurchase: toLastBulkMilestoneRecord(plan),
      },
    };
  }

  private resolveNextResetCrossingElapsedMs(
    remainingMs: number,
    readyResetKind: HoverIdleAutomationEventKind | null,
  ): number | null {
    // The caller's own readiness check (selectNextResetAutomationEventKind)
    // already ran this tick with no state mutation in between, so its result
    // is still current; re-deriving it here would be redundant work. This
    // short-circuit only matters if a future caller invokes this resolver
    // without having just confirmed readiness itself.
    if (readyResetKind !== null) {
      return 0;
    }

    const stats = this.lastResetCrossingResolutionStats;
    stats.resolutionCalls += 1;
    const timingEnabled = this.resetCrossingResolutionTimingEnabled;
    const startedAtMs = timingEnabled ? this.crossingResolutionNowMs() : 0;

    const mode = this.resetCrossingResolutionMode;
    const inputs =
      mode === "binary-legacy"
        ? null
        : this.getContinuousGameplayProjectionInputs();

    const candidateCrossings: Array<{
      readonly elapsedMs: number;
      readonly priority: number;
    }> = [];

    // Resolve the closed-form-eligible kinds first, in priority order, so their
    // crossing can bound the more expensive NHov branch scan below.
    const cheapCandidates: ReadonlyArray<{
      readonly kind: HoverIdleAutomationEventKind;
      readonly priority: number;
    }> = [
      { kind: "ohov-auto-execution", priority: 0 },
      { kind: "ohov-mp-execution", priority: 1 },
      { kind: "mhov-mp-execution", priority: 3 },
    ];
    for (const candidate of cheapCandidates) {
      const elapsedMs = this.resolveResetAutomationCrossingElapsedMs(
        candidate.kind,
        remainingMs,
        inputs,
        mode,
      );
      if (elapsedMs !== null) {
        candidateCrossings.push({ elapsedMs, priority: candidate.priority });
      }
    }

    let bestCheapMs: number | null = null;
    for (const candidate of candidateCrossings) {
      if (bestCheapMs === null || candidate.elapsedMs < bestCheapMs) {
        bestCheapMs = candidate.elapsedMs;
      }
    }

    // NHov (priority 2) keeps the binary search because its metric-improvement
    // gate is not safely monotone in runBaseHov. In closed-form mode, prune it
    // when it cannot cross at or before the best cheap crossing: a NHov crossing
    // later than `best + epsilon` can never win the min/priority selection, so
    // skipping it there is result-preserving. The bounded window gates only the
    // skip decision; when NHov can win, the exact crossing is searched over the
    // full window so its resolved float stays identical to the baseline.
    const nhovReadinessBoundMs =
      mode === "closed-form" && bestCheapMs !== null
        ? Math.min(remainingMs, bestCheapMs + RESET_AUTOMATION_DRAIN_EPSILON_MS)
        : remainingMs;
    if (
      this.isProjectedResetAutomationEventReady(
        "nhov-mp-execution",
        nhovReadinessBoundMs,
        inputs,
      )
    ) {
      const elapsedMs = this.findResetAutomationCrossingElapsedMs(
        "nhov-mp-execution",
        remainingMs,
        inputs,
      );
      stats.binaryFallbacks += 1;
      candidateCrossings.push({ elapsedMs, priority: 2 });
    } else if (mode === "closed-form" && bestCheapMs !== null) {
      stats.nhovPrunedSkips += 1;
    }

    let best: { readonly elapsedMs: number; readonly priority: number } | null =
      null;
    for (const candidate of candidateCrossings) {
      if (
        best === null ||
        candidate.elapsedMs <
          best.elapsedMs - RESET_AUTOMATION_DRAIN_EPSILON_MS ||
        (Math.abs(candidate.elapsedMs - best.elapsedMs) <=
          RESET_AUTOMATION_DRAIN_EPSILON_MS &&
          candidate.priority < best.priority)
      ) {
        best = candidate;
      }
    }

    const result = best === null ? null : best.elapsedMs;
    if (result !== null && result <= RESET_AUTOMATION_NEAR_ZERO_CROSSING_MS) {
      stats.near0Crossings += 1;
    }
    if (timingEnabled) {
      stats.crossingResolutionMs +=
        this.crossingResolutionNowMs() - startedAtMs;
    }
    return result;
  }

  // Resolve a single closed-form-eligible kind's crossing. Tries the O(1)
  // closed-form path first (closed-form mode only); otherwise, or when the
  // closed-form declines, falls back to the same guarded binary search the
  // original path used. Returns null when the kind cannot cross in the window.
  private resolveResetAutomationCrossingElapsedMs(
    kind: HoverIdleAutomationEventKind,
    remainingMs: number,
    inputs: ContinuousGameplayProjectionInputs | null,
    mode: ResetCrossingResolutionMode,
  ): number | null {
    if (mode === "closed-form" && inputs !== null) {
      const closed = this.tryClosedFormResetCrossing(kind, remainingMs, inputs);
      if (closed.status === "unreachable") {
        return null;
      }
      if (closed.status === "crossing") {
        // Drive the identical 36-iteration bisection with the analytic crossing
        // as a numeric predicate (`mid >= crossing`) instead of the projection.
        // When the analytic crossing lands in the same final grid cell as the
        // true gate flip, this reproduces the binary search's result byte for
        // byte at O(1) projection cost; the exact-gate check below rejects the
        // rare straddle so the caller can run the exact search.
        const elapsedMs = this.bisectResetCrossingByThreshold(
          remainingMs,
          closed.crossingMs,
        );
        if (
          this.closedFormCrossingMatchesGate(
            kind,
            elapsedMs,
            remainingMs,
            inputs,
          )
        ) {
          this.lastResetCrossingResolutionStats.closedFormHits += 1;
          return elapsedMs;
        }
      }
    }
    if (!this.isProjectedResetAutomationEventReady(kind, remainingMs, inputs)) {
      return null;
    }
    this.lastResetCrossingResolutionStats.binaryFallbacks += 1;
    return this.findResetAutomationCrossingElapsedMs(kind, remainingMs, inputs);
  }

  // Closed-form crossing for the monotone reset gates. Inverts the quadratic
  // Hov projection to the elapsed at which the gate's Hov / runBaseHov threshold
  // is reached and folds in the linear time gates. Returns the analytic crossing
  // (the projected gate flip); the caller reproduces the binary search's grid
  // result from it and confirms with the exact gate. Reports "unreachable" when
  // the gate cannot flip in the window and "fallback" whenever the gate may be
  // non-monotone (hover active, Freshness Wall) so the exact search is used.
  private tryClosedFormResetCrossing(
    kind: HoverIdleAutomationEventKind,
    remainingMs: number,
    inputs: ContinuousGameplayProjectionInputs,
  ):
    | { readonly status: "crossing"; readonly crossingMs: number }
    | { readonly status: "unreachable" }
    | { readonly status: "fallback" } {
    if (!inputs.quadraticEligible) {
      return { status: "fallback" };
    }
    const lin = inputs.linearHovDeltaPerSecond;
    const quad = inputs.quadraticHovDeltaPerSecondSquared;

    let hovDeltaTarget: HugeNumber;
    let timeCrossingMs = 0;

    switch (kind) {
      case "ohov-auto-execution": {
        const target = this.getOHovTargetCache().builtInTargetHov;
        hovDeltaTarget = target.sub(this.hov);
        timeCrossingMs =
          this.resolveResetAutomationMinimumGameplayIntervalRemainingMs("oHov");
        break;
      }
      case "ohov-mp-execution": {
        const section = this.mpmState.ohovExecution;
        if (!section.unlocked || !section.enabled || !section.assigned) {
          return { status: "unreachable" };
        }
        const candidateTargetHov = resolveOHovMpAutoExecutionCandidateTargetHov(
          {
            resetTargetHov: this.resolveOHovExecutionTargetHov(),
            minimumHovCore: section.minimumHovCore,
            hovCoreGainMultiplier: this.computeHovCoreGainMultiplierForOHov(),
          },
        );
        hovDeltaTarget = candidateTargetHov.sub(this.hov);
        timeCrossingMs = Math.max(
          this.resolveResetAutomationMinimumGameplayIntervalRemainingMs("oHov"),
          section.minimumIntervalMs -
            this.oHovState.records.currentRunGameElapsedMs,
        );
        break;
      }
      case "mhov-mp-execution": {
        if (isFreshnessWallActive(this.wallState)) {
          return { status: "fallback" };
        }
        const section = this.mpmState.mhovExecution;
        if (!section.unlocked || !section.enabled || !section.assigned) {
          return { status: "unreachable" };
        }
        const thresholdRunBaseHov =
          this.resolveMHovMpCrossingThresholdRunBaseHov(inputs);
        if (thresholdRunBaseHov === null) {
          return { status: "unreachable" };
        }
        hovDeltaTarget = thresholdRunBaseHov.sub(
          this.mHovState.currentLoopHovEarned,
        );
        timeCrossingMs = Math.max(
          this.resolveResetAutomationMinimumGameplayIntervalRemainingMs("mHov"),
          section.minimumResetIntervalMs - this.gameplayElapsedSinceAnyResetMs,
        );
        break;
      }
      default:
        return { status: "fallback" };
    }

    const hovCrossingSeconds = this.solveQuadraticCrossingSeconds(
      lin,
      quad,
      hovDeltaTarget,
    );
    if (hovCrossingSeconds === null) {
      return { status: "unreachable" };
    }
    const hovCrossingMs = hovCrossingSeconds.mul(1000);
    if (!hovCrossingMs.isFinite()) {
      return { status: "fallback" };
    }
    const crossingMs = Math.max(hovCrossingMs.toNumber(), timeCrossingMs);
    if (
      !Number.isFinite(crossingMs) ||
      crossingMs > remainingMs + RESET_AUTOMATION_DRAIN_EPSILON_MS
    ) {
      return { status: "unreachable" };
    }
    return { status: "crossing", crossingMs };
  }

  // MHov-MP crossing threshold (reward-multiplier inverse x2 + execute floor),
  // memoized on the projection-substrate inputs instance. Every state change
  // that can move the threshold — hgMultiplier via reset adoption or restore,
  // section multiplier/unlock via mpmState, the reward exponent via wallState
  // or skill adoption, the reward-uncap achievement flag — reaches the drain
  // only through paths that also replace the substrate (the four guarded
  // references, the explicit reset/purchase invalidations, or the
  // unconditional drop at drain entry), so inputs identity inherits the
  // substrate cache's invalidation boundary exactly. A cached `null` records
  // an unreachable reward target (softcap ceiling without hardCapLifted),
  // mirroring the inverse resolver's contract.
  private resolveMHovMpCrossingThresholdRunBaseHov(
    inputs: ContinuousGameplayProjectionInputs,
  ): HugeNumber | null {
    const cached = this.mHovMpCrossingThresholdCache;
    if (cached !== null && cached.inputs === inputs) {
      return cached.thresholdRunBaseHov;
    }
    const currentMultiplier = this.mHovState.hgMultiplier;
    const rewardOptions = {
      exponent: this.resolveEffectiveMHovRewardExponent(),
      hardCapLifted: this.achievementMhovRewardUncapped,
      balance: this.resetBalance.mHov,
    };
    const thresholdForRatio = resolveMinRunBaseHovForMHovRewardMultiplier(
      currentMultiplier.mul(this.mpmState.mhovExecution.multiplier),
      rewardOptions,
    );
    const thresholdForStrict = resolveMinRunBaseHovForMHovRewardMultiplier(
      currentMultiplier,
      rewardOptions,
    );
    const thresholdRunBaseHov =
      thresholdForRatio === null || thresholdForStrict === null
        ? null
        : maxHuge(
            maxHuge(thresholdForRatio, thresholdForStrict),
            huge(this.resetBalance.mHov.executeThreshold),
          );
    this.mHovMpCrossingThresholdCache = { inputs, thresholdRunBaseHov };
    return thresholdRunBaseHov;
  }

  // Smallest t >= 0 (seconds) with linear*t + quadratic*t^2 >= deltaTarget for
  // the non-negative growth coefficients of the no-hover projection. Returns 0
  // when the threshold is already met and null when it is unreachable.
  private solveQuadraticCrossingSeconds(
    linearPerSecond: HugeNumber,
    quadraticPerSecondSquared: HugeNumber,
    deltaTarget: HugeNumber,
  ): HugeNumber | null {
    if (compareHuge(deltaTarget, 0) <= 0) {
      return hugeZero();
    }
    const hasQuadratic = compareHuge(quadraticPerSecondSquared, 0) > 0;
    const hasLinear = compareHuge(linearPerSecond, 0) > 0;
    if (!hasQuadratic && !hasLinear) {
      return null;
    }
    if (!hasQuadratic) {
      const t = deltaTarget.div(linearPerSecond);
      return t.isFinite() && compareHuge(t, 0) >= 0 ? t : null;
    }
    const discriminant = linearPerSecond
      .mul(linearPerSecond)
      .add(quadraticPerSecondSquared.mul(4).mul(deltaTarget));
    if (!discriminant.isFinite() || compareHuge(discriminant, 0) < 0) {
      return null;
    }
    const t = discriminant
      .sqrt()
      .sub(linearPerSecond)
      .div(quadraticPerSecondSquared.mul(2));
    return t.isFinite() && compareHuge(t, 0) >= 0 ? t : null;
  }

  // Reproduce the binary search's converged upper bound using the analytic
  // crossing as the predicate. Mirrors findResetAutomationCrossingElapsedMs's
  // bisection arithmetic exactly, so when `mid >= crossingMs` agrees with the
  // real gate at every midpoint the returned float is identical to the exact
  // search's.
  private bisectResetCrossingByThreshold(
    remainingMs: number,
    crossingMs: number,
  ): number {
    let lowMs = 0;
    let highMs = remainingMs;
    for (let i = 0; i < RESET_AUTOMATION_CROSSING_SEARCH_ITERATIONS; i += 1) {
      const midMs = (lowMs + highMs) / 2;
      if (midMs >= crossingMs) {
        highMs = midMs;
      } else {
        lowMs = midMs;
      }
    }
    return highMs;
  }

  // Confirm the reproduced crossing satisfies the exact gate's binary-search
  // postcondition (ready at the crossing, not ready one grid step below), which
  // holds only when the analytic crossing shares the true flip's final grid
  // cell. A false result means a grid straddle: the caller runs the exact
  // search instead so the result stays identical to the binary search.
  private closedFormCrossingMatchesGate(
    kind: HoverIdleAutomationEventKind,
    elapsedMs: number,
    remainingMs: number,
    inputs: ContinuousGameplayProjectionInputs,
  ): boolean {
    if (!this.isProjectedResetAutomationEventReady(kind, elapsedMs, inputs)) {
      return false;
    }
    const stepMs =
      remainingMs / 2 ** RESET_AUTOMATION_CROSSING_SEARCH_ITERATIONS;
    const belowMs = elapsedMs - stepMs;
    if (belowMs <= 0) {
      return true;
    }
    return !this.isProjectedResetAutomationEventReady(kind, belowMs, inputs);
  }

  private crossingResolutionNowMs(): number {
    const perf = (globalThis as { performance?: { now?: () => number } })
      .performance;
    return perf?.now ? perf.now() : this.nowMs();
  }

  private findResetAutomationCrossingElapsedMs(
    kind: HoverIdleAutomationEventKind,
    remainingMs: number,
    inputs: ContinuousGameplayProjectionInputs | null = null,
  ): number {
    let lowMs = 0;
    let highMs = remainingMs;
    for (let i = 0; i < RESET_AUTOMATION_CROSSING_SEARCH_ITERATIONS; i += 1) {
      const midMs = (lowMs + highMs) / 2;
      if (this.isProjectedResetAutomationEventReady(kind, midMs, inputs)) {
        highMs = midMs;
      } else {
        lowMs = midMs;
      }
    }
    this.lastResetCrossingResolutionStats.binaryIterations +=
      RESET_AUTOMATION_CROSSING_SEARCH_ITERATIONS;
    return highMs;
  }

  private isProjectedResetAutomationEventReady(
    kind: HoverIdleAutomationEventKind,
    elapsedMs: number,
    inputs: ContinuousGameplayProjectionInputs | null = null,
  ): boolean {
    const deltas = this.projectContinuousGameplayHovDeltas(
      elapsedMs / 1000,
      inputs,
    );
    return this.isResetAutomationEventReadyAtDeltas(kind, elapsedMs, deltas);
  }

  // Split out of isProjectedResetAutomationEventReady so a caller checking
  // several kinds at the same elapsedMs (wouldResetAutomationCrossBeforeHguCadence)
  // can resolve the elapsed-dependent deltas once and reuse them, instead of
  // recomputing the identical passive/MP projection per kind. Still counts
  // one readinessEvals per kind check, matching the un-deduped call shape.
  private isResetAutomationEventReadyAtDeltas(
    kind: HoverIdleAutomationEventKind,
    elapsedMs: number,
    deltas: ContinuousGameplayHovDeltas,
  ): boolean {
    this.lastResetCrossingResolutionStats.readinessEvals += 1;
    const projectedHov = this.hov.add(deltas.netHovDelta);
    const projectedRunBaseHov = this.mHovState.currentLoopHovEarned.add(
      deltas.earnedHov,
    );

    switch (kind) {
      case "ohov-auto-execution":
        return this.shouldAutoExecuteBuiltInOHovAtHov(projectedHov, elapsedMs);
      case "ohov-mp-execution":
        return this.shouldAutoExecuteOHovMpExecutionAt(projectedHov, elapsedMs);
      case "nhov-mp-execution":
        return (
          this.resolveAutoExecuteNHovBranchIdAt(
            projectedRunBaseHov,
            elapsedMs,
          ) !== null
        );
      case "mhov-mp-execution":
        return this.shouldAutoExecuteMHovMpExecutionAt(
          projectedRunBaseHov,
          elapsedMs,
        );
    }
  }

  // Drain-scoped accessor for the projection substrate: reuses the cached
  // instance while hiddenHovGainBonus/hovAmplifierState/mpmState/wallState
  // are all unchanged, rebuilding otherwise. See
  // continuousGameplayProjectionInputsCache for why those four references are
  // the right invalidation key.
  private getContinuousGameplayProjectionInputs(): ContinuousGameplayProjectionInputs {
    const cache = this.continuousGameplayProjectionInputsCache;
    if (
      cache !== null &&
      cache.hiddenHovGainBonus === this.hiddenHovGainBonus &&
      cache.hovAmplifierState === this.hovAmplifierState &&
      cache.mpmState === this.mpmState &&
      cache.wallState === this.wallState
    ) {
      return cache.inputs;
    }
    const inputs = this.computeContinuousGameplayProjectionInputs();
    this.continuousGameplayProjectionInputsCache = {
      inputs,
      hiddenHovGainBonus: this.hiddenHovGainBonus,
      hovAmplifierState: this.hovAmplifierState,
      mpmState: this.mpmState,
      wallState: this.wallState,
    };
    return inputs;
  }

  private invalidateContinuousGameplayProjectionInputsCache(): void {
    this.continuousGameplayProjectionInputsCache = null;
  }

  // Elapsed-invariant inputs are computed once per crossing resolution and
  // threaded through every readiness probe. The per-elapsed arithmetic below is
  // byte-for-byte the same whether `inputs` is supplied or recomputed live, so
  // the cached and uncached paths produce identical deltas.
  private computeContinuousGameplayProjectionInputs(): ContinuousGameplayProjectionInputs {
    const context = this.createTickContext(0);
    const mpCellIndexes = [...this.getEffectiveHovTargetDirectMpAssignments()];
    const mpCellGainPerSecond = mpCellIndexes.map((cellIndex) =>
      this.resolveCellGainPerSecond(cellIndex, context.hovGainAtStart),
    );
    return this.composeContinuousGameplayProjectionInputs(
      context,
      mpCellIndexes,
      mpCellGainPerSecond,
      !this.isHoveringHov && !this.isHoveringTim,
      this.resolveTimHovActivationEligibility(context.hovGainAtStart),
    );
  }

  private composeContinuousGameplayProjectionInputs(
    context: HoverIdleTickContext,
    mpCellIndexes: readonly number[],
    mpCellGainPerSecond: readonly HugeNumber[],
    quadraticEligible: boolean,
    timHovActivationEligible: boolean,
  ): ContinuousGameplayProjectionInputs {
    let linearHovDeltaPerSecond = hugeZero();
    let quadraticHovDeltaPerSecondSquared = hugeZero();
    const allocation = this.hovTimBalance.hovAllocation;
    if (allocation > 0) {
      const allocationScale = allocation / PASSIVE_RESOURCE_BALANCE_BASE;
      linearHovDeltaPerSecond = context.hovGainAtStart.mul(allocationScale);
      quadraticHovDeltaPerSecondSquared =
        context.hiddenHovGainGrowthPerSecond.mul(0.5 * allocationScale);
    }
    for (const rate of mpCellGainPerSecond) {
      if (isPositiveHuge(rate)) {
        linearHovDeltaPerSecond = linearHovDeltaPerSecond.add(rate);
      }
    }

    return {
      context,
      mpCellIndexes,
      mpCellGainPerSecond,
      quadraticEligible,
      timHovActivationEligible,
      linearHovDeltaPerSecond,
      quadraticHovDeltaPerSecondSquared,
    };
  }

  // Derives the projection substrate the drain would rebuild immediately
  // after an MHov reset executed from the current state, without executing
  // the reset. The reward, plan, and progress-scope transitions reuse the
  // exact helpers the real execute path composes (calculateMHovResetReward /
  // selectMHovResetPlan / applyHoverResetProgressScopeToGroups), and the hov
  // gain pipeline is re-evaluated at the post-reset purchase count with the
  // granted hgMultiplier. This is the post-reset-state-injection
  // precondition for MHov multi-cycle reward recursion folding (see
  // docs/audits/2026-07-segment-solver-journal.md); no runtime path consumes
  // it yet — the equivalence tests in tests/unit/mHovModel.test.ts do.
  //
  // Excluded by design (a folding recurrence must model them separately):
  // execution-time side effects (PlayLog automation events and before/after
  // captures, achievement / cumulativeStats latches, reset cooldown
  // bookkeeping, recordAnyResetNow's freshness-wall elapsed reset) and
  // NHov / OHov resets, whose progress scopes differ.
  derivePostMHovResetProjectionInputs(): PostMHovResetProjectionDerivation | null {
    const rewardBaseHov = this.mHovState.currentLoopHovEarned;
    if (!this.canExecuteMHovReset(rewardBaseHov)) {
      return null;
    }
    const reward = this.calculateMHovResetReward(rewardBaseHov);
    const plan = this.selectMHovResetPlan(rewardBaseHov, reward);
    // Mirror applyMHovResetPlanToState's order: the grant lands first, then
    // the shared progress scope resolves against the granted mHovState.
    const post = applyHoverResetProgressScopeToGroups(
      plan.progress,
      {
        ...this.collectHoverResetProgressStateGroups(),
        mHovState: plan.mHovGrant.nextState,
      },
      {
        baseUpgradeCost: this.baseUpgradeCost,
        baseTimUpgradeCost: this.baseTimUpgradeCost,
      },
    );
    const pipelineInput = this.buildHovGainPipelineInput(
      post.purchasedHovGainUpgrades,
    );
    const postPipeline = resolvePureHovGainPipeline({
      ...pipelineInput,
      raw: {
        ...pipelineInput.raw,
        hiddenHovGainBonus: post.hiddenHovGainBonus,
      },
      bhg: {
        ...pipelineInput.bhg,
        mHovHgMultiplier: post.mHovState.hgMultiplier,
      },
    });
    const context: HoverIdleTickContext = {
      dtSeconds: 0,
      hovGainAtStart: postPipeline.finalHovGainPerSecond,
      baseHovGainAtStart: postPipeline.bhgHovGainPerSecond,
      timGainAtStart: this.computeTimGainPerSecond(),
      hiddenHovGainGrowthPerSecond: this.resolveHiddenHovGainGrowthPerSecond(),
      hiddenTimGainGrowthPerSecond: this.resolveHiddenTimGainGrowthPerSecond(),
    };
    const mpCellIndexes = [...this.getEffectiveHovTargetDirectMpAssignments()];
    const mpCellGainPerSecond = mpCellIndexes.map((cellIndex) =>
      this.resolveCellGainPerSecond(
        cellIndex,
        context.hovGainAtStart,
        post.hovTargetGrid.hovCells,
      ),
    );
    return {
      inputs: this.composeContinuousGameplayProjectionInputs(
        context,
        mpCellIndexes,
        mpCellGainPerSecond,
        !post.hovTargetGrid.isHoveringHov && !this.isHoveringTim,
        this.resolveTimHovActivationEligibility(context.hovGainAtStart, {
          hoveringCellIndex: post.hovTargetGrid.hovHoveringCellIndex,
          hovGridTier: post.hovTargetGrid.hovGridTier,
          cells: post.hovTargetGrid.hovCells,
        }),
      ),
      postMHovState: post.mHovState,
      postHov: post.hov,
      rewardBaseHov,
      rewardMultiplier: reward.outcome.rewardMultiplier,
    };
  }

  private projectContinuousGameplayHovDeltas(
    dtSeconds: number,
    inputs: ContinuousGameplayProjectionInputs | null = null,
  ): ContinuousGameplayHovDeltas {
    if (!Number.isFinite(dtSeconds) || dtSeconds <= 0) {
      return { earnedHov: hugeZero(), netHovDelta: hugeZero() };
    }

    const context =
      inputs !== null
        ? { ...inputs.context, dtSeconds }
        : this.createTickContext(dtSeconds);
    const passiveHovGain = computePassiveResourceGain({
      gainPerSecond: context.hovGainAtStart,
      allocation: this.hovTimBalance.hovAllocation,
      hiddenGrowthPerSecond: context.hiddenHovGainGrowthPerSecond,
      dtSeconds: context.dtSeconds,
      passiveResourceBalanceBase: PASSIVE_RESOURCE_BALANCE_BASE,
    });
    const passiveTimGain = computePassiveResourceGain({
      gainPerSecond: context.timGainAtStart,
      allocation: this.hovTimBalance.timAllocation,
      hiddenGrowthPerSecond: context.hiddenTimGainGrowthPerSecond,
      dtSeconds: context.dtSeconds,
      passiveResourceBalanceBase: PASSIVE_RESOURCE_BALANCE_BASE,
    });

    let earnedHov = passiveHovGain;
    const mpCellIndexes =
      inputs !== null
        ? inputs.mpCellIndexes
        : this.getEffectiveHovTargetDirectMpAssignments();
    for (let i = 0; i < mpCellIndexes.length; i += 1) {
      const mpGainPerSecond =
        inputs !== null
          ? inputs.mpCellGainPerSecond[i]
          : this.resolveCellGainPerSecond(
              mpCellIndexes[i],
              context.hovGainAtStart,
            );
      const mpHovGain = mpGainPerSecond.mul(context.dtSeconds);
      if (isPositiveHuge(mpHovGain)) {
        earnedHov = earnedHov.add(mpHovGain);
      }
    }

    const isPlayerHoverOnMpCell =
      this.isHoveringHov &&
      this.hovHoveringCellIndex !== null &&
      this.getEffectiveHovTargetDirectMpAssignments().includes(
        this.hovHoveringCellIndex,
      );
    if (this.isHoveringHov) {
      const cellGainPerSecond = this.resolveHoveringCellGainPerSecond(
        context.hovGainAtStart,
      );
      const baseHoverHovGain = isPlayerHoverOnMpCell
        ? hugeZero()
        : cellGainPerSecond.mul(context.dtSeconds);
      let refinedHovGain = hugeZero();
      if (
        this.shouldActivateTimHov(
          context.hovGainAtStart,
          inputs === null ? null : inputs.timHovActivationEligible,
        )
      ) {
        const refinement = computeTimToHovRefinement({
          availableTim: this.tim.add(passiveTimGain),
          cellGainPerSecond,
          dtSeconds: context.dtSeconds,
          timHovActivationEfficiency: this.timHovActivationEfficiency,
        });
        refinedHovGain = refinement.refinedHovGain;
      }
      earnedHov = earnedHov.add(baseHoverHovGain).add(refinedHovGain);
    }

    let consumedHov = hugeZero();
    if (this.isHoveringTim) {
      const cellTimGainPerSecond = this.resolveHoveringTimCellGainPerSecond(
        context.timGainAtStart,
      );
      const activeTimGain = computeActiveTimHoverGain({
        availableHov: this.hov.add(earnedHov),
        hovGainAtStart: context.baseHovGainAtStart,
        cellTimGainPerSecond,
        dtSeconds: context.dtSeconds,
      });
      consumedHov = activeTimGain.consumedHov;
    }

    return {
      earnedHov,
      netHovDelta: earnedHov.sub(consumedHov),
    };
  }

  private selectNextResetAutomationEventKind(): HoverIdleAutomationEventKind | null {
    if (this.shouldAutoExecuteBuiltInOHovAtHov(this.hov)) {
      return "ohov-auto-execution";
    }
    if (this.shouldAutoExecuteOHovMpExecution()) {
      return "ohov-mp-execution";
    }
    if (this.resolveAutoExecuteNHovBranchId() !== null) {
      return "nhov-mp-execution";
    }
    if (this.shouldAutoExecuteMHovMpExecution()) {
      return "mhov-mp-execution";
    }
    return null;
  }

  private resolveOneRuntimeAutomationEvent(): HoverIdleAutomationEventKind | null {
    if (this.maybeAutoExecuteOHov()) {
      return "ohov-auto-execution";
    }
    if (this.maybeAutoExecuteOHovMpExecution()) {
      return "ohov-mp-execution";
    }
    if (this.maybeAutoExecuteNHovMpExecution()) {
      return "nhov-mp-execution";
    }
    if (this.maybeAutoExecuteMHovMpExecution()) {
      return "mhov-mp-execution";
    }
    return null;
  }

  private createResetAutomationProgressSignature(): string {
    return [
      this.hov.toString(),
      this.mHovState.currentLoopHovEarned.toString(),
      this.mHovState.count,
      this.nHovState.count,
      this.oHovState.count,
      this.outsideResetSerial,
      this.oHovState.hovCore.toString(),
      this.gameplayElapsedSinceAnyResetMs,
    ].join("|");
  }

  private createGameplayAutomationProgressSignature(): string {
    const hguCadences = [
      ...this.hovGainUpgradeMpAutomationCadences.entries(),
    ].map(([key, cadence]) =>
      [
        key,
        cadence.state.hasFiredInitial ? "1" : "0",
        cadence.state.currentBaseIntervalMs,
        cadence.state.accumulatedRepeatMs,
      ].join(":"),
    );
    return [
      this.createResetAutomationProgressSignature(),
      this.purchasedHovGainUpgrades,
      ...this.hovCells.map((cell) =>
        [
          cell.unlocked ? "1" : "0",
          cell.purchasedUpgrades,
          cell.hovGainMultiplier.toString(),
          cell.upgradeCost.toString(),
        ].join(":"),
      ),
      ...hguCadences,
    ].join("|");
  }

  private nextGameplayAutomationEventSequence(): number {
    const sequence = this.gameplayAutomationEventSequence;
    this.gameplayAutomationEventSequence += 1;
    return sequence;
  }

  consumeLastOHovAutoExecutePreSnapshot(): HoverIdleSnapshot | null {
    const snapshot = this.lastOHovAutoExecutePreSnapshot;
    this.lastOHovAutoExecutePreSnapshot = null;
    return snapshot;
  }

  consumeLastMHovAutoExecuteBeforeSnapshot(): MHovExecuteBeforeSnapshot | null {
    const snapshot = this.lastMHovAutoExecuteBeforeSnapshot;
    this.lastMHovAutoExecuteBeforeSnapshot = null;
    return snapshot;
  }

  consumeLastNHovAutoExecute(): {
    readonly branchId: NHovBranchId;
    readonly beforeSnapshot: HoverIdleSnapshot;
  } | null {
    const value = this.lastNHovAutoExecute;
    this.lastNHovAutoExecute = null;
    return value;
  }

  consumeLastAutoResetExecuteBeforeSnapshots(): {
    readonly mHov: HoverIdleSnapshot | null;
    readonly nHov: HoverIdleSnapshot | null;
    readonly oHov: HoverIdleSnapshot | null;
  } {
    const snapshots = this.lastAutoResetExecuteBeforeSnapshots;
    this.lastAutoResetExecuteBeforeSnapshots = {
      mHov: null,
      nHov: null,
      oHov: null,
    };
    return snapshots;
  }

  consumeLastAutoResetExecutionEvents(): readonly HoverIdleAutoResetExecutionEvent[] {
    const events = this.lastAutoResetExecutionEvents;
    this.lastAutoResetExecutionEvents = [];
    this.lastOHovAutoExecutePreSnapshot = null;
    this.lastOHovMpExecuteBeforeSnapshot = null;
    this.lastMHovAutoExecuteBeforeSnapshot = null;
    this.lastNHovAutoExecute = null;
    this.lastAutoResetExecuteBeforeSnapshots = {
      mHov: null,
      nHov: null,
      oHov: null,
    };
    return [...events];
  }

  consumeLastHovGainUpgradeMpAutomationEvents(): readonly HoverIdleHovGainUpgradeMpAutomationEvent[] {
    const events = this.lastHovGainUpgradeMpAutomationEvents;
    this.lastHovGainUpgradeMpAutomationEvents = [];
    return [...events];
  }

  consumeLastResetAutomationDrainDiagnostic(): HoverIdleResetAutomationDrainDiagnostic | null {
    const diagnostic = this.lastResetAutomationDrainDiagnostic;
    this.lastResetAutomationDrainDiagnostic = null;
    return diagnostic;
  }

  // Test/measurement-only surface for the reset-crossing resolver. Not
  // persisted and never used by production wiring, which always resolves via
  // the default "closed-form" mode.
  setResetCrossingResolutionModeForTest(
    mode: ResetCrossingResolutionMode,
  ): void {
    this.resetCrossingResolutionMode = mode;
  }

  setResetCrossingResolutionTimingEnabledForTest(enabled: boolean): void {
    this.resetCrossingResolutionTimingEnabled = enabled;
  }

  getLastResetCrossingResolutionStats(): ResetCrossingResolutionStats {
    return { ...this.lastResetCrossingResolutionStats };
  }

  /**
   * Advances the in-flight OHov run's game-time accumulator on the same lane as
   * online playtime, so the stats screen and the "fast/quick OHov" achievements
   * stay anchored to in-game pacing when admin/system speed multipliers
   * compress wall time.
   */
  private advanceOHovCurrentRunGameElapsed(gameElapsedMs: number): void {
    if (!Number.isFinite(gameElapsedMs) || gameElapsedMs <= 0) return;
    const records = this.oHovState.records;
    this.oHovState = {
      ...this.oHovState,
      records: {
        ...records,
        currentRunGameElapsedMs:
          records.currentRunGameElapsedMs + gameElapsedMs,
      },
    };
  }

  /**
   * Advances the in-flight Wall run's game-time accumulator. Mirrors
   * `advanceOHovCurrentRunGameElapsed` so Wall elapsed/clear/best-clear all
   * track game time instead of wall-clock time, including admin test-time
   * compression and any separate gameplay system multiplier.
   */
  private advanceWallRunElapsed(gameElapsedMs: number): void {
    const next = advanceWallRunElapsedInState(this.wallState, gameElapsedMs);
    if (next === this.wallState) return;
    this.wallState = next;
  }

  private advanceGameplayElapsedSinceAnyReset(gameElapsedMs: number): void {
    if (!Number.isFinite(gameElapsedMs) || gameElapsedMs <= 0) return;
    this.gameplayElapsedSinceAnyResetMs += gameElapsedMs;
  }

  private advanceResetAutomationMinimumGameplayIntervalCarry(
    gameElapsedMs: number,
  ): void {
    this.resetAutomationState = {
      minimumGameplayIntervalCarryMs:
        advanceResetAutomationMinimumGameplayIntervalCarry(
          this.resetAutomationState.minimumGameplayIntervalCarryMs,
          gameElapsedMs,
        ),
    };
  }

  private isResetAutomationMinimumGameplayIntervalReady(
    family: "mHov" | "nHov" | "oHov",
    elapsedOffsetMs = 0,
    nHovBranchId?: NHovBranchId,
  ): boolean {
    return isResetAutomationMinimumGameplayIntervalSatisfied(
      this.resetAutomationState.minimumGameplayIntervalCarryMs,
      family,
      elapsedOffsetMs,
      nHovBranchId,
    );
  }

  private resolveResetAutomationMinimumGameplayIntervalRemainingMs(
    family: "mHov" | "nHov" | "oHov",
    nHovBranchId?: NHovBranchId,
  ): number {
    return resolveResetAutomationMinimumGameplayIntervalRemainingMs(
      this.resetAutomationState.minimumGameplayIntervalCarryMs,
      family,
      nHovBranchId,
    );
  }

  private consumeResetAutomationMinimumGameplayInterval(
    family: "mHov" | "nHov" | "oHov",
    nHovBranchId?: NHovBranchId,
  ): void {
    this.resetAutomationState = {
      minimumGameplayIntervalCarryMs:
        consumeResetAutomationMinimumGameplayInterval(
          this.resetAutomationState.minimumGameplayIntervalCarryMs,
          family,
          nHovBranchId,
        ),
    };
  }

  /**
   * Built-in OHov auto uses the base threshold before Outer Wall clear and the
   * Wall influence target afterward. Active wall clear targets take priority.
   * The firing target is fully expressed by the resolver; MP assignment-form
   * unlock state is unrelated to this path.
   *
   * `executeOHov` resets Hov to zero, and it is internally gated by
   * `canExecuteOHov`, so racing with the MPM path cannot double-count hovCore
   * at the same crossing.
   */
  private maybeAutoExecuteOHov(): boolean {
    if (!this.shouldAutoExecuteBuiltInOHovAtHov(this.hov)) {
      return false;
    }
    const preSnapshot = this.getSnapshot();
    if (!this.executeOHov("auto")) {
      return false;
    }
    const afterSnapshot = this.getSnapshot();
    this.lastOHovAutoExecutePreSnapshot ??= preSnapshot;
    this.lastAutoResetExecuteBeforeSnapshots.oHov ??= preSnapshot;
    this.lastAutoResetExecutionEvents.push({
      sequence: this.nextGameplayAutomationEventSequence(),
      kind: "ohov-auto-execution",
      layer: "ohov",
      beforeSnapshot: preSnapshot,
      afterSnapshot,
    });
    this.consumeResetAutomationMinimumGameplayInterval("oHov");
    return true;
  }

  private shouldAutoExecuteBuiltInOHovAtHov(
    hov: HugeNumber,
    elapsedOffsetMs = 0,
  ): boolean {
    if (
      !this.isResetAutomationMinimumGameplayIntervalReady(
        "oHov",
        elapsedOffsetMs,
      )
    ) {
      return false;
    }
    // Same rule as shouldAutoExecuteOHov, fed the cached pre-parsed target so
    // per-probe string parsing stays off this hot gate.
    return canExecuteOHov({
      hov,
      targetHov: this.getOHovTargetCache().builtInTargetHov,
    });
  }

  /**
   * MPM-driven OHov execution. Distinct from built-in OHov auto: this fires
   * only after the MP assignment form unlocks, the execution row is assigned,
   * and the player-configured HovCore and elapsed conditions are met.
   * Reuses `executeOHov("mp")` so reset scope, wall clear, run records, and
   * outsideResetSerial share the same mutation as manual and built-in auto.
   */
  private maybeAutoExecuteOHovMpExecution(): boolean {
    if (!this.shouldAutoExecuteOHovMpExecution()) {
      return false;
    }
    const beforeSnapshot = this.getSnapshot();
    if (!this.executeOHov("mp")) {
      return false;
    }
    const afterSnapshot = this.getSnapshot();
    this.lastOHovMpExecuteBeforeSnapshot ??= beforeSnapshot;
    this.lastAutoResetExecuteBeforeSnapshots.oHov ??= beforeSnapshot;
    this.lastAutoResetExecutionEvents.push({
      sequence: this.nextGameplayAutomationEventSequence(),
      kind: "ohov-mp-execution",
      layer: "ohov",
      beforeSnapshot,
      afterSnapshot,
    });
    this.consumeResetAutomationMinimumGameplayInterval("oHov");
    return true;
  }

  private shouldAutoExecuteOHovMpExecution(): boolean {
    return this.shouldAutoExecuteOHovMpExecutionAt(this.hov, 0);
  }

  private shouldAutoExecuteOHovMpExecutionAt(
    hov: HugeNumber,
    elapsedOffsetMs: number,
  ): boolean {
    const section = this.mpmState.ohovExecution;
    if (!section.unlocked || !section.enabled || !section.assigned) {
      return false;
    }
    if (!this.canExecuteOHovResetAtHov(hov)) {
      return false;
    }
    if (
      !this.isResetAutomationMinimumGameplayIntervalReady(
        "oHov",
        elapsedOffsetMs,
      )
    ) {
      return false;
    }
    const hovCoreGainMultiplier = this.computeHovCoreGainMultiplierForOHov();
    return resolveOHovMpAutoExecutionGate({
      hov,
      resetTargetHov: this.resolveOHovExecutionTargetHov(),
      minimumHovCore: section.minimumHovCore,
      hovCoreGainMultiplier,
      hovCoreGainPreview: this.computeHovCoreGainForOHovAtHov(
        hov,
        hovCoreGainMultiplier,
      ),
      minimumElapsedSatisfied: this.isOHovMpMinimumElapsedSatisfied(
        section.minimumIntervalMs,
        elapsedOffsetMs,
      ),
    }).canExecute;
  }

  // Treat the in-flight OHov run's game-time accumulator as authoritative for
  // the MP "minimum elapsed" gate. It already lives on the model, advances
  // through online ticks and bounded large-elapsed advancement, and resets cleanly with each
  // Outside reset, so it matches the player intent of "wait at least N ms
  // since the previous Outside reset before letting MPM fire OHov again".
  private isOHovMpMinimumElapsedSatisfied(
    minimumElapsedMs: number,
    elapsedOffsetMs = 0,
  ): boolean {
    if (minimumElapsedMs <= 0) {
      return true;
    }
    const elapsedMs =
      this.oHovState.records.currentRunGameElapsedMs + elapsedOffsetMs;
    return Number.isFinite(elapsedMs) && elapsedMs >= minimumElapsedMs;
  }

  consumeLastOHovMpExecuteBeforeSnapshot(): HoverIdleSnapshot | null {
    const snapshot = this.lastOHovMpExecuteBeforeSnapshot;
    this.lastOHovMpExecuteBeforeSnapshot = null;
    return snapshot;
  }

  private maybeAutoExecuteMHovMpExecution(): boolean {
    if (!this.shouldAutoExecuteMHovMpExecution()) {
      return false;
    }
    const before = this.captureMHovAutomationLogStateBefore();
    if (this.lastAutoResetExecuteBeforeSnapshots.mHov === null) {
      const readoutBeforeSnapshot = this.getSnapshot();
      this.lastAutoResetExecuteBeforeSnapshots.mHov = readoutBeforeSnapshot;
      this.lastMHovAutoExecuteBeforeSnapshot = readoutBeforeSnapshot;
    }
    if (!this.executeMHovMutation()) {
      return false;
    }
    this.lastAutoResetExecutionEvents.push({
      sequence: this.nextGameplayAutomationEventSequence(),
      kind: "mhov-mp-execution",
      layer: "mhov",
      before,
      after: this.captureMHovAutomationLogStateAfter(),
    });
    this.consumeResetAutomationMinimumGameplayInterval("mHov");
    this.cumulativeStats = {
      ...this.cumulativeStats,
      mhovExecutedByMpCount: this.cumulativeStats.mhovExecutedByMpCount + 1,
    };
    return true;
  }

  private maybeAutoExecuteNHovMpExecution(): boolean {
    const branchId = this.resolveAutoExecuteNHovBranchId();
    if (branchId === null) {
      return false;
    }
    const before = this.captureNHovAutomationLogStateBefore(branchId);
    if (this.lastAutoResetExecuteBeforeSnapshots.nHov === null) {
      const readoutBeforeSnapshot = this.getSnapshot();
      this.lastAutoResetExecuteBeforeSnapshots.nHov = readoutBeforeSnapshot;
      this.lastNHovAutoExecute = {
        branchId,
        beforeSnapshot: readoutBeforeSnapshot,
      };
    }
    if (!this.executeNHov(branchId)) {
      return false;
    }
    this.lastAutoResetExecutionEvents.push({
      sequence: this.nextGameplayAutomationEventSequence(),
      kind: "nhov-mp-execution",
      layer: "nhov",
      branchId,
      before,
      after: this.captureNHovAutomationLogStateAfter(branchId),
    });
    this.consumeResetAutomationMinimumGameplayInterval("nHov", branchId);
    return true;
  }

  private captureMHovAutomationLogStateBefore(): HoverIdleMHovAutomationLogStateBefore {
    return {
      oHovCount: this.oHovState.count,
      nHovCount: this.nHovState.count,
      oHovRunElapsedMs: this.oHovState.records.currentRunGameElapsedMs,
      effectivePendingMultiplier: resolveMHovRewardMultiplier(
        this.mHovState.currentLoopHovEarned,
        this.resolveMHovRewardMultiplierOptions(),
      ).effectiveRewardMultiplier,
      hov: this.hov,
      hovGainPerSecond:
        this.resolveCurrentHovGainPipeline().finalHovGainPerSecond,
      currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
      mHovMultiplier: this.mHovState.hgMultiplier,
      mHovCount: this.mHovState.count,
    };
  }

  private captureMHovAutomationLogStateAfter(): HoverIdleMHovAutomationLogStateAfter {
    return {
      mHovMultiplier: this.mHovState.hgMultiplier,
      mHovCount: this.mHovState.count,
    };
  }

  private captureNHovAutomationLogStateBefore(
    branchId: NHovBranchId,
  ): HoverIdleNHovAutomationLogStateBefore {
    return {
      hov: this.hov,
      hovGainPerSecond:
        this.resolveCurrentHovGainPipeline().finalHovGainPerSecond,
      currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
      branchExperience: this.nHovState.branchExperienceByBranch[branchId],
      branchLevel: resolveNHovBranchLevelsFromExperience(
        this.nHovState.branchExperienceByBranch,
      )[branchId],
      nHovCount: this.nHovState.count,
    };
  }

  private captureNHovAutomationLogStateAfter(
    branchId: NHovBranchId,
  ): HoverIdleNHovAutomationLogStateAfter {
    return {
      branchExperience: this.nHovState.branchExperienceByBranch[branchId],
      branchLevel: resolveNHovBranchLevelsFromExperience(
        this.nHovState.branchExperienceByBranch,
      )[branchId],
      nHovCount: this.nHovState.count,
    };
  }

  // Returns the first `NHOV_BRANCH_IDS`-ordered branch that satisfies the MP
  // execution gate. Fixed-order priority is the contract: the assignment list
  // can mutate, but execution priority is anchored to `NHOV_BRANCH_IDS`.
  //
  // The `minimumEffect` gate compares the `next / current` improvement ratio
  // from the same prospective-effects chain that drives the UI preview's
  // `current -> next` column, so the auto-execute gate and the on-screen
  // reward preview cannot drift. The absolute post-execution metric is not
  // compared directly.
  private resolveAutoExecuteNHovBranchId(): NHovBranchId | null {
    return this.resolveAutoExecuteNHovBranchIdAt(
      this.mHovState.currentLoopHovEarned,
      0,
    );
  }

  private resolveAutoExecuteNHovBranchIdAt(
    runBaseHov: HugeNumber,
    elapsedOffsetMs: number,
  ): NHovBranchId | null {
    const section = this.mpmState.nhovExecution;
    if (!section.unlocked || !section.enabled) {
      return null;
    }
    if (section.assignments.length === 0) {
      return null;
    }
    for (const branchId of NHOV_BRANCH_IDS) {
      if (!section.assignments.includes(branchId)) continue;
      if (
        !this.isResetAutomationMinimumGameplayIntervalReady(
          "nHov",
          elapsedOffsetMs,
          branchId,
        )
      ) {
        continue;
      }
      const condition = section.conditions[branchId];
      if (
        !this.isMinimumNHovTimeSecondsSatisfied(
          condition.minimumTimeSeconds,
          elapsedOffsetMs,
        )
      ) {
        continue;
      }
      const prospective = this.resolveProspectiveNHovReset(
        branchId,
        runBaseHov,
        elapsedOffsetMs,
      );
      if (
        !this.canExecuteNHovReset(
          branchId,
          runBaseHov,
          elapsedOffsetMs,
          prospective,
        )
      ) {
        continue;
      }
      const { currentEffects, nextEffects } = prospective;
      const currentMetric = resolveNHovBranchMetric(branchId, currentEffects);
      const nextMetric = resolveNHovBranchMetric(branchId, nextEffects);
      const metricImprovementRatio = computeNHovBranchMetricImprovementRatio(
        currentMetric,
        nextMetric,
      );
      if (compareHuge(metricImprovementRatio, condition.minimumEffect) < 0) {
        continue;
      }
      return branchId;
    }
    return null;
  }

  private isMinimumNHovTimeSecondsSatisfied(
    minimumTimeSeconds: number,
    elapsedOffsetMs = 0,
  ): boolean {
    if (minimumTimeSeconds <= 0) {
      return true;
    }
    const elapsedMs = this.gameplayElapsedSinceAnyResetMs + elapsedOffsetMs;
    return elapsedMs / 1000 >= minimumTimeSeconds;
  }

  private shouldAutoExecuteMHovMpExecution(): boolean {
    return this.shouldAutoExecuteMHovMpExecutionAt(
      this.mHovState.currentLoopHovEarned,
      0,
    );
  }

  private shouldAutoExecuteMHovMpExecutionAt(
    runBaseHov: HugeNumber,
    elapsedOffsetMs: number,
  ): boolean {
    const section = this.mpmState.mhovExecution;
    if (!section.unlocked || !section.enabled || !section.assigned) {
      return false;
    }
    if (!this.canExecuteMHovResetAtRunBaseHov(runBaseHov, elapsedOffsetMs)) {
      return false;
    }
    if (
      !this.isResetAutomationMinimumGameplayIntervalReady(
        "mHov",
        elapsedOffsetMs,
      )
    ) {
      return false;
    }
    if (
      !this.isMinimumResetIntervalSatisfied(
        section.minimumResetIntervalMs,
        elapsedOffsetMs,
      )
    ) {
      return false;
    }

    // NHov manual availability does not block MHov MP auto. NHov MP automation
    // is prioritized by the caller, so this gate remains only the MHov-specific
    // assignment / interval / multiplier check.

    // Ratio gate: `section.multiplier` is the minimum ratio of
    // effective-pending / current HG that the player wants before
    // auto-executing. Compare against the current `hgMultiplier` so the
    // gate shares the same baseline as `canExecuteMHov` (effective > current)
    // and stays aligned with the on-screen `current → fresh → effective`
    // preview. HugeNumber-throughout so multipliers past Number.MAX_VALUE
    // still compare correctly.
    const currentMultiplier = this.mHovState.hgMultiplier;
    const rewardResolution = resolveMHovRewardMultiplier(
      runBaseHov,
      this.resolveMHovRewardMultiplierOptionsAtElapsed(elapsedOffsetMs),
    );
    const requiredMultiplier = currentMultiplier.mul(section.multiplier);
    return (
      compareHuge(
        rewardResolution.effectiveRewardMultiplier,
        requiredMultiplier,
      ) >= 0
    );
  }

  resolveEffectiveMHovRewardExponent(): number {
    return (
      this.resetBalance.mHov.rewardExponent +
      getFreshnessWallRewardExponentBonus(
        this.wallState,
        this.wallRewardCapLevel,
      ) +
      this.growthExponentBonus.mhov
    );
  }

  private resolveMHovRewardMultiplierOptions(): ResolveMHovRewardMultiplierOptions {
    return this.resolveMHovRewardMultiplierOptionsAtElapsed(0);
  }

  private resolveMHovRewardMultiplierOptionsAtElapsed(
    elapsedOffsetMs: number,
  ): ResolveMHovRewardMultiplierOptions {
    const freshnessElapsedSinceHovRunMs = getFreshnessElapsedSinceHovRunMs(
      this.wallState,
    );
    return {
      exponent: this.resolveEffectiveMHovRewardExponent(),
      balance: this.resetBalance.mHov,
      hardCapLifted: this.achievementMhovRewardUncapped,
      freshnessStaling: isFreshnessWallActive(this.wallState)
        ? {
            currentMultiplier: this.mHovState.hgMultiplier,
            elapsedSinceHovRunMs:
              freshnessElapsedSinceHovRunMs + Math.max(0, elapsedOffsetMs),
          }
        : undefined,
    };
  }

  private isMinimumResetIntervalSatisfied(
    minimumResetIntervalMs: number,
    elapsedOffsetMs = 0,
  ): boolean {
    if (minimumResetIntervalMs <= 0) {
      return true;
    }
    return (
      this.gameplayElapsedSinceAnyResetMs + elapsedOffsetMs >=
      minimumResetIntervalMs
    );
  }

  private applyHovAmplifierTick(
    dtSeconds: number,
    options: {
      readonly hoveringEntryId?: HovAmplifierEntryId | null;
      readonly mpAssignedEntryIds?: readonly HovAmplifierEntryId[];
      readonly directChargeDrainMode?: "immediate" | "after-full";
    } = {},
  ): void {
    const result = resolveHovAmplifierTick({
      state: this.hovAmplifierState,
      visualSegments: this.hovAmplifierGaugeVisualSegments,
      recentChargeSources: this.hovAmplifierRecentChargeSources,
      recentDirectChargeAtMs: this.hovAmplifierRecentDirectChargeAtMs,
      dtSeconds,
      nowMs: this.nowMs(),
      hoveringEntryId:
        options.hoveringEntryId === undefined
          ? this.hovAmplifierHoveringEntryId
          : options.hoveringEntryId,
      mpAssignedEntryIds:
        options.mpAssignedEntryIds ??
        this.getEffectiveMpmAmplifierPanelAssignments(),
      directChargeDrainMode: options.directChargeDrainMode ?? "immediate",
      amplifierGainMultipliersByEntry:
        this.resolveAmplifierGainMultipliersByEntry(),
      availability: this.resolveHovAmplifierEntryAvailabilityContext(),
      amplifierChallenge: resolveActiveWallAmplifierChallenge(this.wallState),
    });
    this.hovAmplifierState = result.gameplay.state;
    if (result.gameplay.fullChargeReachedEntryIds.includes("red")) {
      this.recordRedAmplifierFullChargeReached();
    }
    this.hovAmplifierGaugeVisualSegments = result.display.visualSegments;
    this.hovAmplifierRecentChargeSources = result.display.recentChargeSources;
    this.hovAmplifierRecentDirectChargeAtMs =
      result.display.recentDirectChargeAtMs;
    this.hovAmplifierVisualChargeBatch = {};
  }

  private createTickContext(dtSeconds: number): HoverIdleTickContext {
    const hovGainPipeline = this.resolveCurrentHovGainPipeline();
    const baseHovGainAtStart = hovGainPipeline.bhgHovGainPerSecond;
    const hovGainAtStart = hovGainPipeline.finalHovGainPerSecond;
    const timGainAtStart = this.timGainPerSecond;
    const hiddenHovGainGrowthPerSecond =
      this.resolveHiddenHovGainGrowthPerSecond();
    const hiddenTimGainGrowthPerSecond =
      this.resolveHiddenTimGainGrowthPerSecond();

    return {
      dtSeconds,
      hovGainAtStart,
      baseHovGainAtStart,
      timGainAtStart,
      hiddenHovGainGrowthPerSecond,
      hiddenTimGainGrowthPerSecond,
    };
  }

  private applyPassiveResourceTick(context: HoverIdleTickContext): void {
    const passiveHovGain = computePassiveResourceGain({
      gainPerSecond: context.hovGainAtStart,
      allocation: this.hovTimBalance.hovAllocation,
      hiddenGrowthPerSecond: context.hiddenHovGainGrowthPerSecond,
      dtSeconds: context.dtSeconds,
      passiveResourceBalanceBase: PASSIVE_RESOURCE_BALANCE_BASE,
    });
    const passiveTimGain = computePassiveResourceGain({
      gainPerSecond: context.timGainAtStart,
      allocation: this.hovTimBalance.timAllocation,
      hiddenGrowthPerSecond: context.hiddenTimGainGrowthPerSecond,
      dtSeconds: context.dtSeconds,
      passiveResourceBalanceBase: PASSIVE_RESOURCE_BALANCE_BASE,
    });
    if (isPositiveHuge(passiveHovGain) || isPositiveHuge(passiveTimGain)) {
      this.addEarnedHov(passiveHovGain);
      this.tim = this.tim.add(passiveTimGain);
      this.cumulativeStats = {
        ...this.cumulativeStats,
        totalTimEarned: this.cumulativeStats.totalTimEarned.add(passiveTimGain),
      };
      this.syncThresholdUnlocks();
    }
  }

  private applyMpHovTick(context: HoverIdleTickContext): void {
    let totalMpHovGain = hugeZero();
    for (const cellIndex of this.getEffectiveHovTargetDirectMpAssignments()) {
      const mpGainPerSecond = this.resolveCellGainPerSecond(
        cellIndex,
        context.hovGainAtStart,
      );
      const mpHovGain = mpGainPerSecond.mul(context.dtSeconds);
      if (isPositiveHuge(mpHovGain)) {
        totalMpHovGain = totalMpHovGain.add(mpHovGain);
      }
    }
    if (isPositiveHuge(totalMpHovGain)) {
      this.addEarnedHov(totalMpHovGain);
      this.syncThresholdUnlocks();
    }
  }

  private getEffectiveHovTargetDirectMpAssignments(): readonly number[] {
    return getEffectiveHovTargetDirectMpAssignments(
      this.mpmState,
      this.wallState,
    );
  }

  private getEffectiveHovGainUpgradeDirectMpAssignments(): readonly HovGainUpgradeMpTarget[] {
    return getEffectiveHovGainUpgradeDirectMpAssignments(
      this.mpmState,
      this.wallState,
    );
  }

  private applyActiveHovTick(context: HoverIdleTickContext): void {
    this.clearTimHovConsumptionObservation();
    const isPlayerHoverOnMpCell =
      this.isHoveringHov &&
      this.hovHoveringCellIndex !== null &&
      this.getEffectiveHovTargetDirectMpAssignments().includes(
        this.hovHoveringCellIndex,
      );

    if (this.isHoveringHov) {
      const cellGainPerSecond = this.resolveHoveringCellGainPerSecond(
        context.hovGainAtStart,
      );
      const baseHoverHovGain = isPlayerHoverOnMpCell
        ? hugeZero()
        : cellGainPerSecond.mul(context.dtSeconds);
      let refinedHovGain = hugeZero();
      let consumedTim = hugeZero();
      if (this.shouldActivateTimHov(context.hovGainAtStart)) {
        const refinement = computeTimToHovRefinement({
          availableTim: this.tim,
          cellGainPerSecond,
          dtSeconds: context.dtSeconds,
          timHovActivationEfficiency: this.timHovActivationEfficiency,
        });
        consumedTim = refinement.consumedTim;
        refinedHovGain = refinement.refinedHovGain;
        this.tim = this.tim.sub(consumedTim);
        if (isPositiveHuge(consumedTim) && this.hovHoveringCellIndex !== null) {
          const consumptionRate = consumedTim.div(context.dtSeconds).toNumber();
          this.timHovConsumingCellIndex = this.hovHoveringCellIndex;
          this.timHovConsumptionRatePerSecond = Math.min(
            1,
            Math.max(Number.MIN_VALUE, consumptionRate),
          );
        }
      }
      this.addEarnedHov(baseHoverHovGain.add(refinedHovGain));
      if (isPositiveHuge(consumedTim) || isPositiveHuge(refinedHovGain)) {
        this.cumulativeStats = {
          ...this.cumulativeStats,
          totalTimSpent: this.cumulativeStats.totalTimSpent.add(consumedTim),
          lifetimeHovRefinedFromTim:
            this.cumulativeStats.lifetimeHovRefinedFromTim.add(refinedHovGain),
        };
      }
      this.syncThresholdUnlocks();
    }
  }

  private clearTimHovConsumptionObservation(): void {
    this.timHovConsumingCellIndex = null;
    this.timHovConsumptionRatePerSecond = 0;
  }

  private applyActiveTimTick(context: HoverIdleTickContext): void {
    if (this.isHoveringTim) {
      const cellTimGainPerSecond = this.resolveHoveringTimCellGainPerSecond(
        context.timGainAtStart,
      );
      const activeTimGain = computeActiveTimHoverGain({
        availableHov: this.hov,
        hovGainAtStart: context.baseHovGainAtStart,
        cellTimGainPerSecond,
        dtSeconds: context.dtSeconds,
      });
      this.hov = this.hov.sub(activeTimGain.consumedHov);
      this.tim = this.tim.add(activeTimGain.timGain);
      this.cumulativeStats = {
        ...this.cumulativeStats,
        totalTimEarned: this.cumulativeStats.totalTimEarned.add(
          activeTimGain.timGain,
        ),
      };
      this.syncThresholdUnlocks();
    }
  }

  private applyPlaytimeAndMaxHovTick(dtSeconds: number): void {
    this.cumulativeStats = {
      ...this.cumulativeStats,
      onlinePlaytimeSeconds:
        this.cumulativeStats.onlinePlaytimeSeconds + dtSeconds,
      maxHov: maxHuge(this.cumulativeStats.maxHov, this.hov),
    };
  }

  private syncMaxHovSinceResets(): void {
    this.mHovState = {
      ...this.mHovState,
      maxHovSinceLastMHov: maxHuge(
        this.mHovState.maxHovSinceLastMHov,
        this.hov,
      ),
    };
  }

  private addEarnedHov(amount: HugeNumberInput): void {
    const earned = assertFiniteNonNegativeHugeInput(amount, "amount");

    if (compareHuge(earned, 0) <= 0) {
      return;
    }

    this.hov = this.hov.add(earned);
    this.hovEarnedThisTick = this.hovEarnedThisTick.add(earned);
    this.cumulativeStats = {
      ...this.cumulativeStats,
      totalHovEarned: this.cumulativeStats.totalHovEarned.add(earned),
    };
    this.mHovState = {
      ...this.mHovState,
      currentLoopHovEarned: this.mHovState.currentLoopHovEarned.add(earned),
    };
  }

  private advanceSecretProgressTimers(
    dtSeconds: number,
    didEarnHovThisTick: boolean,
  ): void {
    const timers = resolveSecretProgressTimers({
      current: {
        continuousPointerSeconds: this.continuousPointerSeconds,
        observerSeconds: this.observerSeconds,
        xPatternSeconds: this.xPatternSeconds,
        hPatternSeconds: this.hPatternSeconds,
        hovOLabelHoverSeconds: this.hovOLabelHoverSeconds,
      },
      dtSeconds,
      isHoveringHov: this.isHoveringHov,
      isHovTargetScreenActive: this.isHovTargetScreenActive,
      didEarnHovThisTick,
      activeHovCellIndices: this.getEffectiveHovTargetDirectMpAssignments(),
      hoveringHovCellIndex: this.hovHoveringCellIndex,
      isHoveringHovOLabel: this.isHoveringHovOLabel,
    });
    this.continuousPointerSeconds = timers.continuousPointerSeconds;
    this.observerSeconds = timers.observerSeconds;
    this.xPatternSeconds = timers.xPatternSeconds;
    this.hPatternSeconds = timers.hPatternSeconds;
    this.hovOLabelHoverSeconds = timers.hovOLabelHoverSeconds;
  }

  setHoveringHovOLabel(isHovering: boolean): void {
    this.isHoveringHovOLabel = isHovering;
    if (!isHovering) {
      this.hovOLabelHoverSeconds = 0;
    }
  }

  setHovTargetScreenActive(isActive: boolean): void {
    this.isHovTargetScreenActive = isActive;
    if (!isActive) {
      this.observerSeconds = 0;
    }
  }

  setHoveringHovAmplifierEntry(entryId: HovAmplifierEntryId | null): void {
    if (entryId === null) {
      this.hovAmplifierHoveringEntryId = null;
      return;
    }
    if (
      !isHovAmplifierEntryUnlocked(this.hovAmplifierState, entryId) ||
      !isHovAmplifierEntryPlayerVisible(
        entryId,
        this.resolveHovAmplifierEntryAvailabilityContext(),
      )
    ) {
      this.hovAmplifierHoveringEntryId = null;
      return;
    }
    this.hovAmplifierHoveringEntryId = entryId;
  }

  recordHovCellClick(): void {
    const now = this.nowMs();
    this.pruneHovCellClickTimestamps(now);
    this.hovCellClickTimestamps.push(now);
    const overflow =
      this.hovCellClickTimestamps.length - HOV_CELL_RAPID_CLICK_TARGET_COUNT;
    if (overflow > 0) {
      this.hovCellClickTimestamps.splice(0, overflow);
    }
  }

  private pruneHovCellClickTimestamps(nowMs: number): void {
    const cutoff = nowMs - HOV_CELL_RAPID_CLICK_WINDOW_MS;
    let dropCount = 0;
    while (
      dropCount < this.hovCellClickTimestamps.length &&
      this.hovCellClickTimestamps[dropCount] < cutoff
    ) {
      dropCount += 1;
    }
    if (dropCount > 0) {
      this.hovCellClickTimestamps.splice(0, dropCount);
    }
  }

  private pruneTransientHistoryBeforeSnapshot(): HoverIdleSnapshotTransientState {
    this.pruneHovCellClickTimestamps(this.nowMs());
    return {
      hovCellClickWithinWindowCount: this.hovCellClickTimestamps.length,
    };
  }

  setHoveringHov(isHovering: boolean): void {
    this.isHoveringHov = isHovering;
    if (!isHovering) {
      this.hovHoveringCellIndex = null;
      this.clearTimHovConsumptionObservation();
    }
  }

  setHoveringCellIndex(index: number | null): boolean {
    if (index === null) {
      this.hovHoveringCellIndex = null;
      this.isHoveringHov = false;
      this.clearTimHovConsumptionObservation();
      return true;
    }
    if (!Number.isInteger(index) || index < 0 || index >= HOV_CELL_COUNT) {
      throw new Error(`invalid cell index: ${index}`);
    }
    if (!this.isCellVisibleForTier(index, this.hovGridTier)) {
      return false;
    }
    if (!this.isCellUnlocked(index)) {
      return false;
    }
    if (this.hovHoveringCellIndex !== index) {
      this.clearTimHovConsumptionObservation();
    }
    this.hovHoveringCellIndex = index;
    this.isHoveringHov = true;
    return true;
  }

  getHovGridTier(): HoverIdleGridTier {
    return this.hovGridTier;
  }

  setHovGridTier(tier: HoverIdleGridTier): boolean {
    if (tier === this.hovGridTier) return false;
    if (tier < this.hovGridTier) return false;
    this.hovGridTier = tier;
    if (
      this.hovHoveringCellIndex !== null &&
      !this.isCellVisibleForTier(this.hovHoveringCellIndex, tier)
    ) {
      this.hovHoveringCellIndex = null;
      this.isHoveringHov = false;
      this.clearTimHovConsumptionObservation();
    }
    return true;
  }

  setMpCapacity(capacity: number): void {
    assertNonNegativeInteger(capacity, "capacity");
    this.mpCapacityBase = capacity;
    this.mpCapacityInitialized = true;
    this.recomputeEffectiveMpCapacity();
  }

  getMpCapacity(): number {
    return this.mpCapacity;
  }

  getExplorationSnapshot(): ExplorationSnapshot {
    return createExplorationSnapshot({
      state: this.explorationState,
      definition: this.explorationDefinition,
      mpmState: this.mpmState,
      totalMpCapacity: this.mpCapacity,
      pointerBiomeId: this.explorationPointerBiomeId,
    });
  }

  getExplorationDetailsSnapshot(): ExplorationDetailsSnapshot {
    return createExplorationDetailsSnapshot({
      state: this.explorationState,
      definition: this.explorationDefinition,
      mpmState: this.mpmState,
      totalMpCapacity: this.mpCapacity,
      availableHovCore: this.oHovState.hovCore,
      availableTim: this.tim,
      pointerBiomeId: this.explorationPointerBiomeId,
      timAccelerationUnlocked: this.explorationTimAccelerationUnlocked,
    });
  }

  getExplorationBiomeState(
    biomeId: ExplorationBiomeId,
  ): ExplorationAreaSnapshot {
    return this.getExplorationSnapshot().areas[biomeId];
  }

  setExplorationBiomeHovering(
    biomeId: ExplorationBiomeId,
    isHovering: boolean,
  ): boolean {
    if (isHovering) {
      if (!this.skillExplorationUnlocked) return false;
      if (this.explorationPointerBiomeId === biomeId) return false;
      this.explorationPointerBiomeId = biomeId;
      return true;
    }
    if (this.explorationPointerBiomeId !== biomeId) return false;
    this.explorationPointerBiomeId = null;
    return true;
  }

  clearExplorationPointerParticipation(): boolean {
    if (this.explorationPointerBiomeId === null) return false;
    this.explorationPointerBiomeId = null;
    return true;
  }

  setExplorationTimAccelerationUnlocked(unlocked: boolean): void {
    this.explorationTimAccelerationUnlocked = unlocked;
    if (!unlocked && this.explorationState.timAccelerationEnabled) {
      this.explorationState = {
        ...this.explorationState,
        timAccelerationEnabled: false,
      };
    }
  }

  setExplorationTimAccelerationEnabled(enabled: boolean): boolean {
    if (enabled && !this.skillExplorationUnlocked) return false;
    if (enabled && !this.explorationTimAccelerationUnlocked) return false;
    if (this.explorationState.timAccelerationEnabled === enabled) return false;
    this.explorationState = {
      ...this.explorationState,
      timAccelerationEnabled: enabled,
    };
    return true;
  }

  canPurchaseExplorationMpSlot(): ExplorationOperationAvailability {
    if (!this.skillExplorationUnlocked) {
      return { canExecute: false, reason: "exploration-locked" };
    }
    const result = resolvePurchaseExplorationMpSlot({
      state: this.explorationState,
      definition: this.explorationDefinition,
      availableHovCore: this.oHovState.hovCore,
    });
    return result.ok
      ? { canExecute: true, cost: result.cost }
      : { canExecute: false, reason: result.reason };
  }

  purchaseExplorationMpSlot(): ExplorationModelCommandResult {
    if (!this.skillExplorationUnlocked) {
      return {
        ok: false,
        reason: "exploration-locked",
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    const result = resolvePurchaseExplorationMpSlot({
      state: this.explorationState,
      definition: this.explorationDefinition,
      availableHovCore: this.oHovState.hovCore,
    });
    if (!result.ok) {
      return {
        ok: false,
        reason: result.reason,
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    if (!this.spendHovCore(result.cost)) {
      return {
        ok: false,
        reason: "insufficient-hov-core",
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    this.explorationState = result.state;
    return { ok: true, snapshot: this.getExplorationDetailsSnapshot() };
  }

  canAssignExplorationMpSlot(
    slotId: ExplorationMpSlotId,
  ): ExplorationOperationAvailability {
    if (!this.skillExplorationUnlocked) {
      return { canExecute: false, reason: "exploration-locked" };
    }
    const result = resolveAssignExplorationMpSlot({
      state: this.explorationState,
      definition: this.explorationDefinition,
      mpmState: this.mpmState,
      totalMpCapacity: this.mpCapacity,
      slotId,
    });
    return result.ok
      ? { canExecute: true }
      : { canExecute: false, reason: result.reason };
  }

  assignExplorationMpSlot(
    slotId: ExplorationMpSlotId,
  ): ExplorationModelCommandResult {
    if (!this.skillExplorationUnlocked) {
      return {
        ok: false,
        reason: "exploration-locked",
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    const result = resolveAssignExplorationMpSlot({
      state: this.explorationState,
      definition: this.explorationDefinition,
      mpmState: this.mpmState,
      totalMpCapacity: this.mpCapacity,
      slotId,
    });
    if (!result.ok) {
      return {
        ok: false,
        reason: result.reason,
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    this.mpmState = result.mpmState;
    return { ok: true, snapshot: this.getExplorationDetailsSnapshot() };
  }

  canRemoveExplorationMpSlot(
    slotId: ExplorationMpSlotId,
  ): ExplorationOperationAvailability {
    if (!this.skillExplorationUnlocked) {
      return { canExecute: false, reason: "exploration-locked" };
    }
    const result = resolveRemoveExplorationMpSlot({
      mpmState: this.mpmState,
      slotId,
    });
    return result.ok
      ? { canExecute: true }
      : { canExecute: false, reason: result.reason };
  }

  removeExplorationMpSlot(
    slotId: ExplorationMpSlotId,
  ): ExplorationModelCommandResult {
    if (!this.skillExplorationUnlocked) {
      return {
        ok: false,
        reason: "exploration-locked",
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    const result = resolveRemoveExplorationMpSlot({
      mpmState: this.mpmState,
      slotId,
    });
    if (!result.ok) {
      return {
        ok: false,
        reason: result.reason,
        snapshot: this.getExplorationDetailsSnapshot(),
      };
    }
    this.mpmState = result.mpmState;
    return { ok: true, snapshot: this.getExplorationDetailsSnapshot() };
  }

  clearMpmExplorationAssignments(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveClearMpmExplorationAssignments(this.mpmState),
    );
  }

  assignAllMpmExploration(): boolean {
    if (!this.skillExplorationUnlocked) return false;
    return this.applyMpmAssignmentCommand(
      resolveAssignAllMpmExploration({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        purchasedMpSlots: this.explorationDefinition.mpSlots.slice(
          0,
          this.explorationState.purchasedMpSlotCount,
        ),
      }),
    );
  }

  setMpmExplorationEnabled(enabled: boolean): boolean {
    if (this.mpmState.exploration.enabled === enabled) return false;
    this.patchMpmSection("exploration", { enabled });
    return true;
  }

  getMpmPresetSlotCount(): number {
    return Math.min(
      MPM_PRESET_MAX_SLOT_COUNT,
      MPM_PRESET_BASE_SLOT_COUNT + this.achievementMpmPresetSlotBonus,
    );
  }

  saveMpmPreset(presetIndex: number): boolean {
    if (!this.isAvailableMpmPresetIndex(presetIndex)) return false;
    this.mpmState = withSavedMpmPreset(this.mpmState, presetIndex);
    return true;
  }

  loadMpmPreset(presetIndex: number): boolean {
    if (!this.isAvailableMpmPresetIndex(presetIndex)) return false;
    const preset = this.mpmState.presets[presetIndex];
    if (preset === null || preset === undefined) return false;

    this.setAllMpmPresetSectionsEnabled(true);
    this.clearAllMpmPresetAssignments();

    for (const cellIndex of preset.hovTargets.assignments) {
      this.placeMpAtTargetIndex(cellIndex);
    }
    for (const target of preset.hovGainUpgrades.assignments) {
      this.assignMpToHovGainUpgradeTarget(target);
    }
    if (preset.mhovExecution.assigned) {
      this.assignMpToMHovExecution();
    }
    for (const branchId of preset.nhovExecution.assignments) {
      this.assignMpToNHovExecution(branchId);
    }
    if (preset.ohovExecution.assigned) {
      this.assignMpToOHovExecution();
    }
    for (const entryId of preset.amplifierPanels.assignments) {
      this.assignMpToAmplifierPanel(entryId);
    }
    for (const entryId of preset.amplifierUpgrades.assignments) {
      this.assignMpToAmplifierUpgrade(entryId);
    }
    for (const slotId of preset.exploration.assignedSlotIds) {
      this.assignExplorationMpSlot(slotId);
    }

    const numericSettings = preset.numericSettings;
    if (numericSettings !== null) {
      this.setMpmMHovExecutionMultiplierInput(
        numericSettings.mhovExecution.multiplier,
      );
      this.setMpmMHovExecutionMinimumResetIntervalMs(
        numericSettings.mhovExecution.minimumResetIntervalMs,
      );
      for (const branchId of NHOV_BRANCH_IDS) {
        const condition = numericSettings.nhovExecution.conditions[branchId];
        this.setMpmNHovExecutionMinimumEffectInput(
          branchId,
          condition.minimumEffect,
        );
        this.setMpmNHovExecutionMinimumTimeSeconds(
          branchId,
          condition.minimumTimeSeconds,
        );
      }
      this.setMpmOHovExecutionMinimumHovCoreInput(
        numericSettings.ohovExecution.minimumHovCore,
      );
      this.setMpmOHovExecutionMinimumIntervalMs(
        numericSettings.ohovExecution.minimumIntervalMs,
      );
    }

    this.setMpmHovTargetsEnabled(preset.hovTargets.enabled);
    this.setMpmHovGainUpgradesEnabled(preset.hovGainUpgrades.enabled);
    this.setMpmAmplifierPanelsEnabled(preset.amplifierPanels.enabled);
    this.setMpmAmplifierUpgradesEnabled(preset.amplifierUpgrades.enabled);
    this.setMpmMHovExecutionEnabled(preset.mhovExecution.enabled);
    this.setMpmNHovExecutionEnabled(preset.nhovExecution.enabled);
    this.setMpmOHovExecutionEnabled(preset.ohovExecution.enabled);
    this.setMpmExplorationEnabled(preset.exploration.enabled);
    return true;
  }

  private isAvailableMpmPresetIndex(presetIndex: number): boolean {
    return (
      Number.isInteger(presetIndex) &&
      presetIndex >= 0 &&
      presetIndex < this.getMpmPresetSlotCount()
    );
  }

  private setAllMpmPresetSectionsEnabled(enabled: boolean): void {
    this.setMpmHovTargetsEnabled(enabled);
    this.setMpmHovGainUpgradesEnabled(enabled);
    this.setMpmAmplifierPanelsEnabled(enabled);
    this.setMpmAmplifierUpgradesEnabled(enabled);
    this.setMpmMHovExecutionEnabled(enabled);
    this.setMpmNHovExecutionEnabled(enabled);
    this.setMpmOHovExecutionEnabled(enabled);
    this.setMpmExplorationEnabled(enabled);
  }

  private clearAllMpmPresetAssignments(): void {
    this.clearMpmHovTargetAssignments();
    this.clearMpmHovGainUpgradeAssignments();
    this.removeMpFromMHovExecution();
    for (const branchId of [...this.mpmState.nhovExecution.assignments]) {
      this.removeMpFromNHovExecution(branchId);
    }
    this.removeMpFromOHovExecution();
    this.clearMpmAmplifierPanelAssignments();
    this.clearMpmAmplifierUpgradeAssignments();
    this.clearMpmExplorationAssignments();
  }

  // Authoritative effective MP capacity = externally-set base plus the
  // aggregate `mp-capacity` add reward composed across cleared walls (MP
  // Wall today). Called on base, cap-level, and relevant wallState changes.
  // Trims MPM assignments down to the new cap when the effective value shrinks.
  private recomputeEffectiveMpCapacity(): void {
    if (!this.mpCapacityInitialized) return;
    const wallAddend = resolveAggregateWallRewardAddend(
      this.wallState,
      "mp-capacity",
      this.wallRewardCapLevel,
    );
    this.mpCapacity = this.mpCapacityBase + wallAddend;
    this.trimMpAssignmentsToCapacity();
  }

  getMpPlacedCellIndex(): number | null {
    return this.mpmState.hovTargets.assignments[0] ?? null;
  }

  getMpPlacedCellIndices(): readonly number[] {
    return [...this.mpmState.hovTargets.assignments];
  }

  getMpAssignedHovGainUpgradeTargets(): readonly HovGainUpgradeMpTarget[] {
    return [...this.mpmState.hovGainUpgrades.assignments];
  }

  placeMp(cellIndex: number): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToHovTarget({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        cellIndex,
        requireHovScreenCellVisible: this.isCellVisibleForTier(
          cellIndex,
          this.hovGridTier,
        ),
        requireHovScreenCellUnlocked: this.isCellUnlocked(cellIndex),
      }),
    );
  }

  // MPM operates on the permanent Hov target index, so it must be able to assign
  // MP to slots that are not currently visible or unlocked on the Hov screen
  // (e.g., H4 after MHov resets the grid back to tier 1).
  placeMpAtTargetIndex(cellIndex: number): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToHovTarget({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        cellIndex,
      }),
    );
  }

  retrieveMp(cellIndex: number): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromHovTarget({ state: this.mpmState, cellIndex }),
    );
  }

  clearMpmHovTargetAssignments(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveClearMpmHovTargetAssignments(this.mpmState),
    );
  }

  setMpmHovTargetsEnabled(enabled: boolean): boolean {
    if (this.mpmState.hovTargets.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("hovTargets", { enabled });
    return true;
  }

  assignMpToHovGainUpgradeTarget(target: HovGainUpgradeMpTarget): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToHovGainUpgrade({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        target,
      }),
    );
  }

  removeMpFromHovGainUpgradeTarget(target: HovGainUpgradeMpTarget): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromHovGainUpgrade({ state: this.mpmState, target }),
    );
  }

  clearMpmHovGainUpgradeAssignments(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveClearMpmHovGainUpgradeAssignments(this.mpmState),
    );
  }

  setMpmHovGainUpgradesEnabled(enabled: boolean): boolean {
    if (this.mpmState.hovGainUpgrades.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("hovGainUpgrades", { enabled });
    return true;
  }

  assignMpToMHovExecution(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToMHovExecution({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
      }),
    );
  }

  removeMpFromMHovExecution(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromMHovExecution(this.mpmState),
    );
  }

  assignMpToNHovExecution(branchId: NHovBranchId): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToNHovExecution({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        branchId,
      }),
    );
  }

  removeMpFromNHovExecution(branchId: NHovBranchId): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromNHovExecution({
        state: this.mpmState,
        branchId,
      }),
    );
  }

  assignMpToOHovExecution(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToOHovExecution({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
      }),
    );
  }

  removeMpFromOHovExecution(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromOHovExecution(this.mpmState),
    );
  }

  setMpmMHovExecutionEnabled(enabled: boolean): boolean {
    if (this.mpmState.mhovExecution.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("mhovExecution", {
      enabled,
      runtime: { pending: false },
    });
    return true;
  }

  setMpmNHovExecutionEnabled(enabled: boolean): boolean {
    if (this.mpmState.nhovExecution.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("nhovExecution", { enabled });
    return true;
  }

  setMpmOHovExecutionEnabled(enabled: boolean): boolean {
    if (this.mpmState.ohovExecution.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("ohovExecution", { enabled });
    return true;
  }

  setMpmMHovExecutionMultiplierInput(value: HugeNumberInput): boolean {
    const multiplier = normalizeMpmMHovExecutionMultiplier(
      value,
      this.mpmState.mhovExecution.multiplier,
    );
    if (compareHuge(multiplier, this.mpmState.mhovExecution.multiplier) === 0) {
      return false;
    }
    this.patchMpmSection("mhovExecution", { multiplier });
    return true;
  }

  setMpmMHovExecutionMinimumResetIntervalMs(value: number): boolean {
    const minimumResetIntervalMs =
      normalizeMpmMHovExecutionMinimumResetIntervalMs(
        value,
        this.mpmState.mhovExecution.minimumResetIntervalMs,
      );
    if (
      minimumResetIntervalMs ===
      this.mpmState.mhovExecution.minimumResetIntervalMs
    ) {
      return false;
    }
    this.patchMpmSection("mhovExecution", { minimumResetIntervalMs });
    return true;
  }

  setMpmNHovExecutionMinimumEffectInput(
    branchId: NHovBranchId,
    value: HugeNumberInput,
  ): boolean {
    if (!isNHovBranchId(branchId)) {
      return false;
    }
    const current = this.mpmState.nhovExecution.conditions[branchId];
    const minimumEffect = normalizeMpmNHovExecutionMinimumEffect(
      value,
      current.minimumEffect,
    );
    if (compareHuge(minimumEffect, current.minimumEffect) === 0) {
      return false;
    }
    this.patchMpmSection("nhovExecution", {
      conditions: {
        ...this.mpmState.nhovExecution.conditions,
        [branchId]: { ...current, minimumEffect },
      },
    });
    return true;
  }

  setMpmNHovExecutionMinimumTimeSeconds(
    branchId: NHovBranchId,
    value: number,
  ): boolean {
    if (!isNHovBranchId(branchId)) {
      return false;
    }
    const current = this.mpmState.nhovExecution.conditions[branchId];
    const minimumTimeSeconds = normalizeMpmNHovExecutionMinimumTimeSeconds(
      value,
      current.minimumTimeSeconds,
    );
    if (minimumTimeSeconds === current.minimumTimeSeconds) {
      return false;
    }
    this.patchMpmSection("nhovExecution", {
      conditions: {
        ...this.mpmState.nhovExecution.conditions,
        [branchId]: { ...current, minimumTimeSeconds },
      },
    });
    return true;
  }

  setMpmOHovExecutionMinimumHovCoreInput(value: HugeNumberInput): boolean {
    const minimumHovCore = normalizeMpmOHovExecutionMinimumHovCore(
      value,
      this.mpmState.ohovExecution.minimumHovCore,
    );
    if (
      compareHuge(
        minimumHovCore,
        this.mpmState.ohovExecution.minimumHovCore,
      ) === 0
    ) {
      return false;
    }
    this.patchMpmSection("ohovExecution", { minimumHovCore });
    return true;
  }

  setMpmOHovExecutionMinimumIntervalMs(value: number): boolean {
    const minimumIntervalMs = normalizeMpmMHovExecutionMinimumResetIntervalMs(
      value,
      this.mpmState.ohovExecution.minimumIntervalMs,
    );
    if (minimumIntervalMs === this.mpmState.ohovExecution.minimumIntervalMs) {
      return false;
    }
    this.patchMpmSection("ohovExecution", { minimumIntervalMs });
    return true;
  }

  resetMpmMHovExecutionForPHov1(): void {
    this.patchMpmSection("mhovExecution", {
      assigned: false,
      runtime: { pending: false },
    });
  }

  // Capture the user's MP placement and execution setup so a progress rebuild
  // (Progress Builder apply) can restart progression and then restore the
  // captured layout. See applyMpmAssignmentIntentOverlay for the restore side.
  getMpmAssignmentIntent(): MpmAssignmentIntent {
    return captureMpmAssignmentIntent(this.mpmState);
  }

  // Overlay previously-captured assignment intent onto the current (post-apply)
  // MPM state, keeping the re-derived unlock / capacity / runtime fields, then
  // reconcile to the effective MP capacity. Over-capacity intent is stripped by
  // the shared priority; locked surfaces keep their intent but stay inactive.
  applyMpmAssignmentIntentOverlay(
    intent: MpmAssignmentIntent,
    reason: MpmCapacityReconcileReason,
  ): void {
    const before = this.mpmState;
    this.mpmState = reconcileMpmAssignmentsForCapacity(
      overlayMpmAssignmentIntent(this.mpmState, intent),
      { mpCapacity: this.mpCapacity, reason },
    );
    this.notifyMpmNumericSettingsChanges(before);
  }

  getMpAssignedAmplifierPanelEntries(): readonly HovAmplifierEntryId[] {
    return [...this.mpmState.amplifierPanels.assignments];
  }

  getMpAssignedAmplifierUpgradeEntries(): readonly HovAmplifierEntryId[] {
    return [...this.mpmState.amplifierUpgrades.assignments];
  }

  assignMpToAmplifierUpgrade(entryId: HovAmplifierEntryId): boolean {
    const availability = this.resolveHovAmplifierEntryAvailabilityContext();
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToAmplifierUpgrade({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        entryId,
        entryUnlocked:
          isHovAmplifierEntryUnlocked(this.hovAmplifierState, entryId) &&
          isHovAmplifierEntryMpAssignable(entryId, availability),
      }),
    );
  }

  removeMpFromAmplifierUpgrade(entryId: HovAmplifierEntryId): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromAmplifierUpgrade({
        state: this.mpmState,
        entryId,
      }),
    );
  }

  clearMpmAmplifierUpgradeAssignments(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveClearMpmAmplifierUpgradeAssignments(this.mpmState),
    );
  }

  assignMpToAmplifierPanel(entryId: HovAmplifierEntryId): boolean {
    const availability = this.resolveHovAmplifierEntryAvailabilityContext();
    return this.applyMpmAssignmentCommand(
      resolveAssignMpToAmplifierPanel({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        entryId,
        entryUnlocked:
          isHovAmplifierEntryUnlocked(this.hovAmplifierState, entryId) &&
          isHovAmplifierEntryMpAssignable(entryId, availability),
      }),
    );
  }

  removeMpFromAmplifierPanel(entryId: HovAmplifierEntryId): boolean {
    return this.applyMpmAssignmentCommand(
      resolveRemoveMpFromAmplifierPanel({ state: this.mpmState, entryId }),
    );
  }

  clearMpmAmplifierPanelAssignments(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveClearMpmAmplifierPanelAssignments(this.mpmState),
    );
  }

  assignAllMpmAmplifierPanels(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignAllMpmAmplifierPanels({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
        hovAmplifierEntries: this.collectHovAmplifierAssignAllEntries(),
      }),
    );
  }

  assignAllMpmHovTargets(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignAllMpmHovTargets({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
      }),
    );
  }

  assignAllMpmHovGainUpgrades(): boolean {
    return this.applyMpmAssignmentCommand(
      resolveAssignAllMpmHovGainUpgrades({
        state: this.mpmState,
        mpCapacity: this.mpCapacity,
      }),
    );
  }

  setMpmAmplifierPanelsEnabled(enabled: boolean): boolean {
    if (this.mpmState.amplifierPanels.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("amplifierPanels", { enabled });
    return true;
  }

  setMpmAmplifierPanelsUnlocked(unlocked: boolean): void {
    this.adoptMpmStateWithCapacityTrim(
      withMpmAmplifierPanelsUnlocked(this.mpmState, unlocked),
    );
  }

  setMpmAmplifierUpgradesEnabled(enabled: boolean): boolean {
    if (this.mpmState.amplifierUpgrades.enabled === enabled) {
      return false;
    }
    this.patchMpmSection("amplifierUpgrades", { enabled });
    return true;
  }

  setMpmAmplifierUpgradesUnlocked(unlocked: boolean): void {
    this.adoptMpmStateWithCapacityTrim(
      withMpmAmplifierUpgradesUnlocked(this.mpmState, unlocked),
    );
  }

  setMpmMHovExecutionUnlocked(unlocked: boolean): void {
    this.adoptMpmStateWithCapacityTrim(
      withMpmMHovExecutionUnlocked(this.mpmState, unlocked),
    );
  }

  setMpmNHovExecutionUnlocked(unlocked: boolean): void {
    this.adoptMpmStateWithCapacityTrim(
      withMpmNHovExecutionUnlocked(this.mpmState, unlocked),
    );
  }

  setMpmOHovExecutionUnlocked(unlocked: boolean): void {
    this.adoptMpmStateWithCapacityTrim(
      withMpmOHovExecutionUnlocked(this.mpmState, unlocked),
    );
  }

  private trimMpAssignmentsToCapacity(): void {
    this.mpmState = reconcileMpmAssignmentsForCapacity(this.mpmState, {
      mpCapacity: this.mpCapacity,
      reason: "capacity-change",
    });
  }

  subscribeMpmNumericSettingsChanges(
    listener: (changes: readonly MpmNumericSettingChange[]) => void,
  ): () => void {
    this.mpmNumericSettingsListeners.add(listener);
    return () => {
      this.mpmNumericSettingsListeners.delete(listener);
    };
  }

  // Numeric policy mutations notify only after normalization / restore completes.
  private notifyMpmNumericSettingsChanges(before: MpmState): void {
    if (this.mpmNumericSettingsListeners.size === 0) return;
    const changes = collectMpmNumericSettingsChanges(before, this.mpmState);
    if (changes.length === 0) return;
    for (const listener of this.mpmNumericSettingsListeners) listener(changes);
  }

  private patchMpmSection<
    K extends
      | "hovTargets"
      | "hovGainUpgrades"
      | "amplifierPanels"
      | "amplifierUpgrades"
      | "mhovExecution"
      | "nhovExecution"
      | "ohovExecution"
      | "exploration",
  >(section: K, patch: Partial<MpmState[K]>): void {
    const before = this.mpmState;
    this.mpmState = normalizeMpmState({
      ...this.mpmState,
      [section]: { ...this.mpmState[section], ...patch },
    });
    this.notifyMpmNumericSettingsChanges(before);
  }

  private adoptMpmStateWithCapacityTrim(next: MpmState): void {
    if (next === this.mpmState) {
      return;
    }
    this.mpmState = next;
    if (this.mpCapacityInitialized) {
      this.trimMpAssignmentsToCapacity();
    }
  }

  private applyMpmAssignmentCommand(
    result: MpmAssignmentCommandResult,
  ): boolean {
    if (!result.ok) return false;
    this.mpmState = result.state;
    return true;
  }

  private collectHovAmplifierAssignAllEntries(): readonly {
    readonly id: HovAmplifierEntryId;
    readonly unlocked: boolean;
    readonly mpAssignable: boolean;
  }[] {
    const availability = this.resolveHovAmplifierEntryAvailabilityContext();
    return HOV_AMPLIFIER_ENTRY_DEFINITIONS.map((definition) => ({
      id: definition.id,
      unlocked: isHovAmplifierEntryUnlocked(
        this.hovAmplifierState,
        definition.id,
      ),
      mpAssignable: isHovAmplifierEntryMpAssignable(
        definition.id,
        availability,
      ),
    }));
  }

  private resolveHovAmplifierEntryAvailabilityContext(): HovAmplifierEntryAvailabilityContext {
    return {
      amplificationWallCleared: hasAmplificationWallClear(this.wallState),
    };
  }

  private getEffectiveMpmAmplifierPanelAssignments(): readonly HovAmplifierEntryId[] {
    const availability = this.resolveHovAmplifierEntryAvailabilityContext();
    return getActiveMpmAmplifierPanelAssignments(this.mpmState).filter(
      (entryId) =>
        isHovAmplifierEntryUnlocked(this.hovAmplifierState, entryId) &&
        isHovAmplifierEntryMpAssignable(entryId, availability),
    );
  }

  private refreshMpmAssignAllLatch(): void {
    if (this.mpmState.assignAllUnlocked) return;
    const shouldLatch = shouldLatchMpmAssignAllUnlocked({
      mpCapacity: this.mpCapacity,
      mpm: this.mpmState,
      hovAmplifier: { entries: this.collectHovAmplifierAssignAllEntries() },
    });
    if (!shouldLatch) return;
    this.mpmState = withMpmAssignAllUnlockedLatched(this.mpmState, true);
  }

  private adoptMpmUnlocksFromEffectSources(): void {
    this.mpmState = composeMpmUnlockAdoption({
      state: this.mpmState,
      sources: [
        this.achievementMpmUnlockIntent,
        this.hovSkillMpmUnlockIntent,
        this.resolveWallMpmUnlockIntent(),
      ],
      mpCapacity: this.mpCapacity,
      mpCapacityInitialized: this.mpCapacityInitialized,
    });
  }

  private resolveWallMpmUnlockIntent(): MpmUnlockIntent {
    return {
      ...createNeutralMpmUnlockIntent(),
      ohovExecutionUnlocked: getWallChallengePolicy(
        "outer-wall",
      ).hasClearRecord(this.wallState),
    };
  }

  private latchWallMpmUnlocks(): void {
    if (!getWallChallengePolicy("outer-wall").hasClearRecord(this.wallState)) {
      return;
    }
    this.adoptMpmStateWithCapacityTrim(
      withMpmOHovExecutionUnlocked(this.mpmState, true),
    );
  }

  private adoptHovGainEffectsFromSources(): void {
    const adoption = composeHovGainEffects([
      this.achievementHovGainIntent,
      this.hovSkillGainIntent,
    ]);
    this.bhgToHgPowerExponentBonus = adoption.bhgToHgPowerExponentBonus;
    this.hovGainCountMultiplier = adoption.hovGainCountMultiplier;
    this.hovGainMultiplier = adoption.hovGainMultiplier;
    this.h1gExponentBonus = adoption.h1gExponentBonus;
    this.hov1GainMultiplier = adoption.hov1GainMultiplier;
    if (this.hngRankScaleBaseBonus !== adoption.hngRankScaleBaseBonus) {
      this.hngRankScaleBaseBonus = adoption.hngRankScaleBaseBonus;
      this.invalidateNHovBranchDerivationCache();
    }
  }

  private adoptGrowthExponentBonus(bonus: MhovNHovGrowthExponentBonus): void {
    const previous = this.growthExponentBonus;
    this.growthExponentBonus = bonus;
    if (
      previous.nHovHgAmplify !== bonus.nHovHgAmplify ||
      previous.nHovHgNHovEffectsToHng !== bonus.nHovHgNHovEffectsToHng ||
      previous.nHovHngScale !== bonus.nHovHngScale
    ) {
      this.invalidateNHovBranchDerivationCache();
    }
  }

  private resolveNHovGrowthExponentBonus(): NHovBranchGrowthExponentBonus {
    return {
      hgAmplify: this.growthExponentBonus.nHovHgAmplify,
      hgNHovEffectsToHng: this.growthExponentBonus.nHovHgNHovEffectsToHng,
      hngScale: this.growthExponentBonus.nHovHngScale,
    };
  }

  setHovSkillEffects(effects: HovSkillResolvedEffects): void {
    const adoption = applyHovSkillEffects({
      effects,
      mpmState: this.mpmState,
      mpCapacity: this.mpCapacity,
      mpCapacityInitialized: this.mpCapacityInitialized,
      wallState: this.wallState,
    });

    this.skillHovGainPerSecond = adoption.gain.hovGainPerSecond;
    this.skillTimGainPerSecond = adoption.gain.timGainPerSecond;
    this.hovSkillGainIntent = adoption.hovGainIntent;
    this.adoptHovGainEffectsFromSources();
    this.hovAmplifierGainOHovMultiplierTargets =
      adoption.amplifier.oHovMultiplierTargets;
    this.amplifierGainFixedMultipliersByEntry =
      adoption.amplifier.fixedMultipliersByEntry;
    this.amplifierGainClearedWallKindMultiplierTargets =
      adoption.amplifier.clearedWallKindMultiplierTargets;
    this.amplifierGainOHovRunElapsedMultiplierTargets =
      adoption.amplifier.oHovRunElapsedMultiplierTargets;
    this.skillOHovHovCoreGainMultiplierUnlocked =
      adoption.ohovHovCoreGainMultiplierUnlocked;
    this.skillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked =
      adoption.mpCapacityCoreOHovHovCoreGainMultiplierUnlocked;
    this.skillFreeMpCoreOHovHovCoreGainMultiplierUnlocked =
      adoption.freeMpCoreOHovHovCoreGainMultiplierUnlocked;
    this.adoptGrowthExponentBonus(adoption.growthExponentBonus);
    this.hovSkillSimultaneousPurchaseCountBaseBonus =
      adoption.hgHngSimultaneousPurchaseCountBaseBonus;
    this.skillApBoarExponentBonus = adoption.apExponent.apBoarExponentBonus;
    this.skillApDeerExponentBonusAcquired =
      adoption.apExponent.apDeerExponentBonusAcquired;
    this.skillApButterflyExponentBonusAcquired =
      adoption.apExponent.apButterflyExponentBonusAcquired;

    this.skillWallUnlocked = adoption.wallUnlocked;
    this.setSkillExplorationUnlocked(adoption.explorationUnlocked);
    this.wallState = adoption.wallState;
    const previousWallRewardCapLevel = this.wallRewardCapLevel;
    this.wallRewardCapLevel = adoption.wallRewardCapRelaxed
      ? "relaxed"
      : "base";
    this.hovSkillMpmUnlockIntent = adoption.mpmUnlockIntent;
    this.adoptMpmUnlocksFromEffectSources();
    if (previousWallRewardCapLevel !== this.wallRewardCapLevel) {
      this.recomputeEffectiveMpCapacity();
    }

    this.syncMainResourceGains();
  }

  setSkillGainBonuses(bonuses: {
    readonly hovGainPerSecond: HugeNumberInput;
    readonly timGainPerSecond: HugeNumberInput;
  }): void {
    const hovGainPerSecond = assertFiniteNonNegativeHugeInput(
      bonuses.hovGainPerSecond,
      "bonuses.hovGainPerSecond",
    );
    const timGainPerSecond = assertFiniteNonNegativeHugeInput(
      bonuses.timGainPerSecond,
      "bonuses.timGainPerSecond",
    );

    this.skillHovGainPerSecond = hovGainPerSecond;
    this.skillTimGainPerSecond = timGainPerSecond;
    this.syncMainResourceGains();
  }

  setSkillBhgToHgPowerExponentBonus(bonus: number): void {
    assertFiniteNonNegative(bonus, "bonus");
    this.hovSkillGainIntent = {
      ...this.hovSkillGainIntent,
      bhgToHgPowerExponentBonus: bonus,
    };
    this.adoptHovGainEffectsFromSources();
  }

  setSkillHov1GainMultiplier(multiplier: HugeNumberInput): void {
    this.hovSkillGainIntent = {
      ...this.hovSkillGainIntent,
      hov1GainMultiplier: assertFinitePositiveHugeInput(
        multiplier,
        "multiplier",
      ),
    };
    this.adoptHovGainEffectsFromSources();
  }

  setAmplifierGainOHovMultiplierTargets(
    targets: ReadonlySet<HovAmplifierEntryId>,
  ): void {
    this.hovAmplifierGainOHovMultiplierTargets = new Set(targets);
  }

  setAmplifierGainSkillMultipliers(
    multipliers: HoverIdleAmplifierGainSkillMultipliers,
  ): void {
    this.amplifierGainFixedMultipliersByEntry = new Map(
      multipliers.amplifierGainFixedMultipliersByEntry,
    );
    this.amplifierGainClearedWallKindMultiplierTargets = new Set(
      multipliers.amplifierGainClearedWallKindMultiplierTargets,
    );
    this.amplifierGainOHovRunElapsedMultiplierTargets = new Set(
      multipliers.amplifierGainOHovRunElapsedMultiplierTargets,
    );
  }

  setSkillOHovHovCoreGainMultiplierUnlocked(unlocked: boolean): void {
    this.skillOHovHovCoreGainMultiplierUnlocked = unlocked;
  }

  setSkillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked(
    unlocked: boolean,
  ): void {
    this.skillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked = unlocked;
  }

  setSkillFreeMpCoreOHovHovCoreGainMultiplierUnlocked(unlocked: boolean): void {
    this.skillFreeMpCoreOHovHovCoreGainMultiplierUnlocked = unlocked;
  }

  setSkillHngRankScaleBaseBonus(bonus: number): void {
    assertFiniteNonNegative(bonus, "bonus");
    this.hovSkillGainIntent = {
      ...this.hovSkillGainIntent,
      hngRankScaleBaseBonus: bonus,
    };
    this.adoptHovGainEffectsFromSources();
  }

  setSkillWallUnlocked(unlocked: boolean): void {
    if (this.skillWallUnlocked === unlocked) return;
    this.skillWallUnlocked = unlocked;
    if (!unlocked) {
      this.wallState = clearActiveWallRunInState(this.wallState);
    }
  }

  isWallUnlocked(): boolean {
    return this.skillWallUnlocked;
  }

  private setSkillExplorationUnlocked(unlocked: boolean): void {
    this.skillExplorationUnlocked = unlocked;
    if (!unlocked) {
      this.explorationPointerBiomeId = null;
    }
  }

  isExplorationUnlocked(): boolean {
    return this.skillExplorationUnlocked;
  }

  isStepWallActive(): boolean {
    return isWallActive(this.wallState, "step-wall");
  }

  getStepWallBestClearMs(): number | null {
    return getWallChallengePolicy("step-wall").resolveBestClearMs(
      this.wallState,
    );
  }

  getStepWallRewardMultiplier(): number {
    return getWallChallengePolicy("step-wall").resolveRewardMultiplier(
      this.wallState,
      this.wallRewardCapLevel,
    );
  }

  isPriceWallActive(): boolean {
    return isWallActive(this.wallState, "price-wall");
  }

  getPriceWallBestClearMs(): number | null {
    return getWallChallengePolicy("price-wall").resolveBestClearMs(
      this.wallState,
    );
  }

  isMpWallActive(): boolean {
    return isWallActive(this.wallState, "mp-wall");
  }

  assignMpWallHovTarget(cellIndex: number): boolean {
    const assignments = this.wallState.mpWallAssignments;
    if (
      !this.isMpWallActive() ||
      assignments === null ||
      (assignments.kind === "hov-target" && assignments.index === cellIndex) ||
      !Number.isInteger(cellIndex) ||
      !this.isCellVisibleForTier(cellIndex, this.hovGridTier) ||
      !this.isCellUnlocked(cellIndex)
    )
      return false;
    this.wallState = {
      ...this.wallState,
      mpWallAssignments: { kind: "hov-target", index: cellIndex },
    };
    return true;
  }

  removeMpWallHovTarget(cellIndex: number): boolean {
    const assignments = this.wallState.mpWallAssignments;
    if (
      !this.isMpWallActive() ||
      assignments === null ||
      assignments.kind !== "hov-target" ||
      assignments.index !== cellIndex
    )
      return false;
    this.wallState = {
      ...this.wallState,
      mpWallAssignments: { kind: "unassigned" },
    };
    return true;
  }

  assignMpWallHovGainUpgrade(target: HovGainUpgradeMpTarget): boolean {
    const assignments = this.wallState.mpWallAssignments;
    const key = toHovGainUpgradeMpTargetKey(target);
    if (
      !this.isMpWallActive() ||
      assignments === null ||
      key === null ||
      (assignments.kind === "hov-gain-upgrade" &&
        toHovGainUpgradeMpTargetKey(assignments.target) === key) ||
      !this.resolveHovGainUpgradeMpTargetAvailability(target)
    )
      return false;
    this.wallState = {
      ...this.wallState,
      mpWallAssignments: {
        kind: "hov-gain-upgrade",
        target: target === "global" ? target : { ...target },
      },
    };
    return true;
  }

  removeMpWallHovGainUpgrade(target: HovGainUpgradeMpTarget): boolean {
    const assignments = this.wallState.mpWallAssignments;
    if (
      !this.isMpWallActive() ||
      assignments === null ||
      assignments.kind !== "hov-gain-upgrade" ||
      toHovGainUpgradeMpTargetKey(assignments.target) !==
        toHovGainUpgradeMpTargetKey(target)
    )
      return false;
    this.wallState = {
      ...this.wallState,
      mpWallAssignments: { kind: "unassigned" },
    };
    return true;
  }

  isHovGainUpgradeDirectMpBlocked(): boolean {
    return isHovGainUpgradeDirectMpBlockedByActiveWall(this.wallState);
  }

  getMpWallBestClearMs(): number | null {
    return getWallChallengePolicy("mp-wall").resolveBestClearMs(this.wallState);
  }

  isFreshnessWallActive(): boolean {
    return isWallActive(this.wallState, "freshness-wall");
  }

  getFreshnessWallBestClearMs(): number | null {
    return getWallChallengePolicy("freshness-wall").resolveBestClearMs(
      this.wallState,
    );
  }

  startStepWall(): boolean {
    return this.startWall("step-wall");
  }

  stopActiveWall(): boolean {
    if (this.wallState.activeWallId === null) return false;
    this.wallState = clearActiveWallRunInState(this.wallState);
    return true;
  }

  startWall(wallId: WallId): boolean {
    if (!this.skillWallUnlocked) return false;
    if (this.wallState.activeWallId !== null) return false;

    const policy = getWallChallengePolicy(wallId);
    if (!policy.isChallengeAvailable(this.wallState)) return false;

    // Treat wall start as an OHov-run boundary in both cases below. The
    // difference is whether Hov was already past the OHov threshold, which
    // determines whether OHov rewards (HovCore, power,
    // count, records) should be granted before activating the wall.
    if (this.canExecuteOHovReset()) {
      // Hov is at or above the OHov threshold — run the full OHov reward
      // plan so HovCore / power / count / records / outsideResetSerial all
      // settle BEFORE the wall activates. finishActiveWallOnOHovClear is
      // intentionally skipped here: activeWallId is still null at this point
      // so this counts as a fresh OHov-run boundary, not a wall clear.
      this.applyWallStartOHovBoundary();
    } else {
      // Hov is below the threshold — no reward grant, but still align the
      // OHov-run boundary so wall-clock elapsed, game elapsed, and
      // lastAnyResetAtMs all reset to fresh values.
      this.applyWallStartUnrewardedOHovBoundary(policy.startResetScope);
    }

    this.wallState = beginWallRunInState(this.wallState, wallId);
    return true;
  }

  // Wall-start OHov boundary path used when Hov has already reached the OHov
  // threshold. Shares the reward / plan / state-apply pipeline with
  // executeOHov but skips finishActiveWallOnOHovClear so this preparatory
  // reset cannot be mistaken for a wall clear.
  private applyWallStartOHovBoundary(): void {
    const reward = this.calculateOHovResetReward();
    const plan = this.selectOHovResetPlan(reward);
    this.applyOHovResetPlanToState(plan);
    this.recordAnyResetNow();
    this.lastOHovExecutionSource = "wall-start";
  }

  private applyWallStartUnrewardedOHovBoundary(
    startResetScope: HoverResetProgressScope,
  ): void {
    this.applyHoverResetProgressScope(startResetScope);
    const boundaryStartedAtMs = this.recordAnyResetNow();
    this.resetOHovCurrentRunForBoundary(boundaryStartedAtMs);
    this.syncMainResourceGains();
    this.syncThresholdUnlocks();
    this.lastOHovExecutionSource = "wall-start";
  }

  private resetOHovCurrentRunForBoundary(startedAtMs: number): void {
    this.oHovState = {
      ...this.oHovState,
      records: {
        ...this.oHovState.records,
        currentRunStartedAtMs: startedAtMs,
        currentRunGameElapsedMs: 0,
      },
    };
  }

  private finishActiveWallOnOHovClear(): void {
    const activeWallId = this.wallState.activeWallId;
    if (activeWallId === null) return;

    const policy = getWallChallengePolicy(activeWallId);
    const elapsedMs = this.wallState.wallRunElapsedMs ?? 0;
    let nextState = this.wallState;
    const clearRecord = policy.resolveClearRecord({
      state: nextState,
      elapsedMs,
    });
    const isFirstClear = clearRecord.previousBestClearMs === null;
    nextState = policy.applyClearRecord({ state: nextState, clearRecord });
    nextState = clearActiveWallRunInState(nextState);
    nextState = addUnseenClearedWall(nextState, activeWallId);
    this.wallState = nextState;

    if (isFirstClear) {
      this.grantWallFirstClearReward(activeWallId);
    }
    // MP Wall's clear record changes the `mp-capacity` add reward, so
    // recompose the authoritative MP capacity now rather than waiting for
    // the next external setMpCapacity call.
    this.recomputeEffectiveMpCapacity();
  }

  private grantWallFirstClearReward(wallId: WallId): void {
    const reward = getWallFirstClearReward(wallId);
    if (reward.kind === "hov-core") {
      if (!Number.isFinite(reward.amount) || reward.amount <= 0) return;
      this.oHovState = {
        ...this.oHovState,
        hovCore: this.oHovState.hovCore.add(reward.amount),
      };
      return;
    }
    if (reward.kind === "ohov-mp-control-unlock") {
      this.adoptMpmUnlocksFromEffectSources();
      return;
    }
    if (reward.kind === "nhov-first-branch-selection-unlock") return;
    if (reward.kind === "amplifier-la-da-availability-unlock") return;
  }

  markWallClearSeen(wallId: WallId): boolean {
    const next = clearUnseenClearedWall(this.wallState, wallId);
    if (next === this.wallState) return false;
    this.wallState = next;
    return true;
  }

  getUnseenClearedWallIds(): readonly WallId[] {
    return this.wallState.unseenClearedWallIds;
  }

  // x1 when no skill currently targets this entry and when OHov power is
  // below 1; otherwise max(1, ohov.power). Skills compose the target set; the
  // multiplier value is always derived from current ohov.power so changes
  // after OHov execution flow through without an extra sync.
  private resolveAmplifierGainOHovMultiplierForEntry(
    entryId: HovAmplifierEntryId,
  ): HugeNumber {
    if (!this.hovAmplifierGainOHovMultiplierTargets.has(entryId)) {
      return hugeOne();
    }
    return maxHuge(hugeOne(), this.oHovState.power);
  }

  private resolveAmplifierGainSkillMultiplierForEntry(
    entryId: HovAmplifierEntryId,
  ): HugeNumber {
    let multiplier =
      this.amplifierGainFixedMultipliersByEntry.get(entryId) ?? hugeOne();
    if (this.amplifierGainClearedWallKindMultiplierTargets.has(entryId)) {
      multiplier = multiplier.mul(1 + getClearedWallKindCount(this.wallState));
    }
    if (this.amplifierGainOHovRunElapsedMultiplierTargets.has(entryId)) {
      const elapsedSeconds =
        this.oHovState.records.currentRunGameElapsedMs / 1000;
      const elapsedMultiplier =
        Number.isFinite(elapsedSeconds) && elapsedSeconds > 0
          ? 1 + elapsedSeconds ** 0.8 / 100
          : 1;
      multiplier = multiplier.mul(elapsedMultiplier);
    }
    return multiplier;
  }

  private resolveAmplifierGainAchievementMultiplierForEntry(
    entryId: HovAmplifierEntryId,
  ): HugeNumber {
    return (
      this.achievementAmplifierGainFixedMultipliersByEntry.get(entryId) ??
      hugeOne()
    );
  }

  private resolveAmplifierGainMultipliersByEntry(): Partial<
    Record<HovAmplifierEntryId, HugeNumber>
  > {
    if (
      this.hovAmplifierGainOHovMultiplierTargets.size === 0 &&
      this.achievementAmplifierGainFixedMultipliersByEntry.size === 0 &&
      this.amplifierGainFixedMultipliersByEntry.size === 0 &&
      this.amplifierGainClearedWallKindMultiplierTargets.size === 0 &&
      this.amplifierGainOHovRunElapsedMultiplierTargets.size === 0
    ) {
      return {};
    }
    const result: Partial<Record<HovAmplifierEntryId, HugeNumber>> = {};
    for (const definition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
      const multiplier = this.resolveAmplifierGainOHovMultiplierForEntry(
        definition.id,
      )
        .mul(
          this.resolveAmplifierGainAchievementMultiplierForEntry(definition.id),
        )
        .mul(this.resolveAmplifierGainSkillMultiplierForEntry(definition.id));
      if (compareHuge(multiplier, 1) > 0) {
        result[definition.id] = multiplier;
      }
    }
    return applyHovAmplifierRaPropagationReward(
      result,
      resolveAmplificationWallRaPropagationRewardEffect(this.wallState),
    );
  }

  setAchievementPersistentEffects(
    effects: HoverIdleAchievementPersistentEffects,
  ): void {
    const adoption = applyAchievementPersistentEffects({
      effects,
      mpmState: this.mpmState,
      mpCapacity: this.mpCapacity,
      mpCapacityInitialized: this.mpCapacityInitialized,
    });
    const { bonuses } = adoption;
    this.achievementHovGainFlatBonus = bonuses.hovGainFlatBonus;
    this.achievementHovUpgradeCostMultiplier = bonuses.hovUpgradeCostMultiplier;
    this.achievementHovCellUpgradeCostMultiplier =
      bonuses.hovCellUpgradeCostMultiplier;
    this.achievementTimGainCountMultiplier = bonuses.timGainCountMultiplier;
    this.achievementHovGainIntent = adoption.hovGainIntent;
    this.adoptHovGainEffectsFromSources();
    this.achievementAmplifierGainFixedMultipliersByEntry =
      adoption.amplifier.fixedMultipliersByEntry;
    this.timHovActivationEfficiency = bonuses.timHovActivationEfficiency;
    this.oneCostHoverRepeaterBatchEnabled =
      bonuses.oneCostHoverRepeaterBatchEnabled;
    this.hovGainFamilyUpgradeRepeatInitialIntervalMs =
      effects.hovGainFamilyUpgradeRepeatInitialIntervalMs ??
      DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS;
    this.achievementMpmUnlockIntent = adoption.mpmUnlockIntent;
    this.adoptMpmUnlocksFromEffectSources();
    this.achievementOHovHovCoreGainScalingUnlocked =
      adoption.ohovHovCoreGainScalingUnlocked;
    this.achievementMpmPresetSlotBonus = adoption.mpmPresetSlotBonus;
    this.achievementMhovRewardUncapped = adoption.mhovRewardUncapped;
    this.setExplorationTimAccelerationUnlocked(
      adoption.explorationTimAccelerationUnlocked,
    );
    if (
      this.achievementNhovBranchEffectUncapped !==
      adoption.nhovBranchEffectUncapped
    ) {
      this.achievementNhovBranchEffectUncapped =
        adoption.nhovBranchEffectUncapped;
      // The cached branch effects clamp at NUMBER_LIMIT_BOUNDARY based on this
      // flag, so flipping it must invalidate the cache to recompute uncapped.
      this.invalidateNHovBranchDerivationCache();
    }
    this.syncMainResourceGains();
  }

  private getEffectiveUpgradeCost(): HugeNumber {
    const baseCost = this.upgradeCost
      .mul(this.achievementHovUpgradeCostMultiplier)
      .div(this.getNHovBranchEffects().hgDiscountCostDivisor);
    return ceilHugeAtLeast(baseCost, 1);
  }

  private getEffectiveHovCellUpgradeCost(cellIndex: number): HugeNumber {
    const cell = this.hovCells[cellIndex];
    if (!cell) {
      throw new Error(`invalid Hov cell index: ${cellIndex}`);
    }
    const divisor = this.getNHovBranchEffects().hgDiscountToHngCostDivisor;
    return ceilHugeAtLeast(
      cell.upgradeCost
        .mul(this.achievementHovCellUpgradeCostMultiplier)
        .div(divisor),
      1,
    );
  }

  private incrementHovGainFamilyUpgradeStats(count: number): void {
    if (count <= 0) return;
    this.cumulativeStats = {
      ...this.cumulativeStats,
      upgradesPurchased: this.cumulativeStats.upgradesPurchased + count,
      hovGainFamilyUpgradesPurchased:
        this.cumulativeStats.hovGainFamilyUpgradesPurchased + count,
    };
  }

  private resolveEffectiveUpgradeGainIncrease(): HugeNumber {
    return this.upgradeGainIncrease.mul(
      this.getNHovBranchEffects().hgAmplifyMultiplier,
    );
  }

  private resolveHovUpgradeMilestoneMultiplier(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
  ): number {
    return calculateUpgradeMilestoneMultiplier(purchasedUpgrades);
  }

  private resolveHovUpgradeMilestoneAdjustedPurchaseCount(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
  ): number {
    return Math.max(
      0,
      purchasedUpgrades - calculateUpgradeMilestoneCount(purchasedUpgrades),
    );
  }

  private resolveHovUpgradeGainIncrease(): HugeNumber {
    return this.resolveBaseHovGainPerSecond(
      this.purchasedHovGainUpgrades + 1,
    ).sub(this.resolveBaseHovGainPerSecond());
  }

  private buildHovGainPipelineInput(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
    overrides: {
      readonly skillHovGainPerSecond?: HugeNumber;
    } = {},
  ): HovGainPipelineInput {
    return {
      raw: {
        baseHovGainPerSecond: this.baseHovGainPerSecond,
        upgradeGainIncreasePerPurchase:
          this.resolveEffectiveUpgradeGainIncrease(),
        milestoneAdjustedPurchaseCount:
          this.resolveHovUpgradeMilestoneAdjustedPurchaseCount(
            purchasedUpgrades,
          ),
        hiddenHovGainBonus: this.hiddenHovGainBonus,
        achievementFlatHovGainBonus: this.achievementHovGainFlatBonus,
      },
      base: {
        skillHovGainPerSecond:
          overrides.skillHovGainPerSecond ?? this.skillHovGainPerSecond,
        upgradeMilestoneMultiplier:
          this.resolveHovUpgradeMilestoneMultiplier(purchasedUpgrades),
      },
      bhg: {
        entitlementHovIncomeMultiplier: this.resolveHovIncomeMultiplier(),
        mHovHgMultiplier: this.mHovState.hgMultiplier,
        hovGainCountMultiplier: this.hovGainCountMultiplier,
        hovGainMultiplier: this.hovGainMultiplier,
      },
      hg: {
        bhgToHgPowerExponent: this.getEffectiveBhgToHgPowerExponent(),
        activeConstraint: isHgWallActive(this.wallState)
          ? {
              divisor: HG_WALL_CONSTRAINT_HG_DIVISOR,
              exponent: HG_WALL_CONSTRAINT_HG_EXPONENT,
            }
          : undefined,
      },
      ehg: {
        amplifierHgMultiplier: this.getHovAmplifierHgMultiplier(),
        wallRewardMultiplier: huge(
          resolveAggregateWallRewardMultiplier(
            this.wallState,
            "hov-gain",
            this.wallRewardCapLevel,
          ),
        ),
      },
    };
  }

  private resolveCurrentHovGainPipeline(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
    overrides: {
      readonly skillHovGainPerSecond?: HugeNumber;
    } = {},
  ): HovGainPipelineResult {
    return resolvePureHovGainPipeline(
      this.buildHovGainPipelineInput(purchasedUpgrades, overrides),
    );
  }

  private resolveKnownBaseHovGainPerSecond(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
  ): HugeNumber {
    return this.resolveCurrentHovGainPipeline(purchasedUpgrades, {
      skillHovGainPerSecond: hugeZero(),
    }).baseHovGainPerSecond;
  }

  private resolveBaseHovGainPerSecond(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
  ): HugeNumber {
    return this.resolveCurrentHovGainPipeline(purchasedUpgrades)
      .baseHovGainPerSecond;
  }

  // Memoized derivations keyed by the current branchExperienceByBranch object
  // reference. NHov reset always replaces this object atomically, so identity
  // equality is a safe invalidation signal. getSnapshot() and the resource
  // pipeline call these many times per call; caching collapses that fan-out.
  private nHovBranchDerivationsKey: NHovBranchExperienceByBranch | null = null;
  private nHovEffectBoundaryCapsKey: NHovEffectBoundaryCaps | null = null;
  private nHovContinuousEffectProgressKey:
    | NHovState["continuousEffectProgressByBranch"]
    | null = null;
  private nHovBranchLevelsCache: NHovBranchLevels | null = null;
  private nHovBranchEffectsCache: NHovBranchEffects | null = null;

  private getNHovBranchEffects(): NHovBranchEffects {
    this.refreshNHovBranchDerivationCache();
    return this.nHovBranchEffectsCache!;
  }

  private getDerivedNHovBranchLevels(): NHovBranchLevels {
    this.refreshNHovBranchDerivationCache();
    return this.nHovBranchLevelsCache!;
  }

  private invalidateNHovBranchDerivationCache(): void {
    this.nHovBranchDerivationsKey = null;
    this.nHovEffectBoundaryCapsKey = null;
    this.nHovContinuousEffectProgressKey = null;
    this.nHovBranchLevelsCache = null;
    this.nHovBranchEffectsCache = null;
  }

  private refreshNHovBranchDerivationCache(): void {
    const key = this.nHovState.branchExperienceByBranch;
    const effectBoundaryCapsKey = this.nHovState.effectBoundaryCaps ?? null;
    const continuousEffectProgressKey =
      this.nHovState.continuousEffectProgressByBranch ?? null;
    const effectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
      this.nHovState.effectBoundaryCaps,
    );
    if (
      this.nHovBranchDerivationsKey === key &&
      this.nHovEffectBoundaryCapsKey === effectBoundaryCapsKey &&
      this.nHovContinuousEffectProgressKey === continuousEffectProgressKey &&
      this.nHovBranchLevelsCache !== null &&
      this.nHovBranchEffectsCache !== null
    ) {
      return;
    }
    const branchLevels = resolveNHovBranchLevelsFromExperience(key);
    const numberLimitCandidateLevels =
      resolveNHovBranchContinuousLevelsFromExperience(key);
    const effectLevels = resolveNHovEffectLevels(
      this.nHovState,
      branchLevels,
      numberLimitCandidateLevels,
    );
    const effects = resolveNHovBranchEffects({
      branches: branchLevels,
      balance: this.resetBalance.nHov,
      hngRankScaleBaseBonus: this.hngRankScaleBaseBonus,
      growthExponentBonus: this.resolveNHovGrowthExponentBonus(),
      uncapped: this.achievementNhovBranchEffectUncapped,
      numberLimitCandidateLevels,
      effectLevels,
      effectBoundaryCaps,
    });
    this.nHovBranchDerivationsKey = key;
    this.nHovEffectBoundaryCapsKey = effectBoundaryCapsKey;
    this.nHovContinuousEffectProgressKey = continuousEffectProgressKey;
    this.nHovBranchLevelsCache = branchLevels;
    this.nHovBranchEffectsCache = effects;
  }

  purchaseGlobalHovGainUpgrade(): HovUpgradePurchaseResult {
    this.syncThresholdUnlocks();
    const plan = resolveHovGainUpgradePurchasePlan(
      this.buildGlobalHovGainPlannerInput("single"),
    );
    if (!plan.ok) {
      return {
        ok: false,
        reason: "insufficient-hov",
        snapshot: this.getSnapshot(),
      };
    }
    this.applyGlobalHovGainPurchasePlan(plan);
    return { ok: true, snapshot: this.getSnapshot() };
  }

  purchaseHovCellGainUpgrade(
    index: HovCellGainUpgradeIndex,
  ): HovUpgradePurchaseResult {
    this.syncThresholdUnlocks();

    if (!Number.isInteger(index) || index < 1 || index > 9) {
      throw new Error(`invalid Hov cell gain upgrade index: ${index}`);
    }

    return this.executeHovCellGainUpgradePurchase(index - 1, "single");
  }

  purchaseGlobalHovGainUpgradeOneCostBatch(options?: {
    readonly maxPurchases?: number;
  }): HovGainUpgradeBulkPurchaseResult {
    const result = this.executeGlobalHovGainUpgradeOneCostBatch(options);
    return { ...result, snapshot: this.getSnapshot() };
  }

  // High-frequency command boundary for simulation. Online callers use the
  // wrapper above so their result keeps the full snapshot contract.
  executeGlobalHovGainUpgradeOneCostBatch(options?: {
    readonly maxPurchases?: number;
  }): HovGainUpgradeBulkPurchaseCommandResult {
    this.syncThresholdUnlocks();
    const enhancementLevelBefore = this.purchasedHovGainUpgrades;
    const plan = resolveHovGainUpgradePurchasePlan(
      this.buildGlobalHovGainPlannerInput(
        "one-cost-bulk",
        options?.maxPurchases,
      ),
    );
    if (!plan.ok) {
      return {
        ok: false,
        purchaseCount: 0,
        enhancementLevelBefore,
        enhancementLevelAfter: enhancementLevelBefore,
        spentHov: hugeZero(),
        finalCost: this.getEffectiveUpgradeCost(),
        milestonePurchases: [],
      };
    }
    this.applyGlobalHovGainPurchasePlan(plan);
    return {
      ok: true,
      purchaseCount: plan.purchaseCount,
      enhancementLevelBefore: plan.enhancementLevelBefore,
      enhancementLevelAfter: plan.enhancementLevelAfter,
      spentHov: plan.spentHov,
      finalCost: this.getEffectiveUpgradeCost(),
      milestonePurchases: toBulkMilestoneRecords(plan),
    };
  }

  purchaseHovCellGainUpgradeOneCostBatch(
    index: HovCellGainUpgradeIndex,
    options?: { readonly maxPurchases?: number },
  ): HovGainUpgradeBulkPurchaseResult {
    const result = this.executeHovCellGainUpgradeOneCostBatch(index, options);
    return { ...result, snapshot: this.getSnapshot() };
  }

  // Cell counterpart to the snapshot-free global command.
  executeHovCellGainUpgradeOneCostBatch(
    index: HovCellGainUpgradeIndex,
    options?: { readonly maxPurchases?: number },
  ): HovGainUpgradeBulkPurchaseCommandResult {
    this.syncThresholdUnlocks();
    if (!Number.isInteger(index) || index < 1 || index > 9) {
      throw new Error(`invalid Hov cell gain upgrade index: ${index}`);
    }
    return this.executeHovCellGainUpgradeBulkPurchaseCommand(
      index - 1,
      options?.maxPurchases,
    );
  }

  private executeHovCellGainUpgradePurchase(
    cellIndex: number,
    mode: HovGainUpgradePurchaseMode,
  ): HovUpgradePurchaseResult {
    const cell = this.hovCells[cellIndex];
    if (!cell) {
      throw new Error(`invalid Hov cell index: ${cellIndex}`);
    }
    const plan = resolveHovGainUpgradePurchasePlan(
      this.buildHovCellGainPlannerInput(cellIndex, cell, mode),
    );
    if (!plan.ok) {
      return {
        ok: false,
        reason: "insufficient-hov",
        snapshot: this.getSnapshot(),
      };
    }
    this.applyHovCellGainPurchasePlan(cellIndex, cell, plan);
    return { ok: true, snapshot: this.getSnapshot() };
  }

  private executeHovCellGainUpgradeBulkPurchaseCommand(
    cellIndex: number,
    maxPurchases: number | undefined,
  ): HovGainUpgradeBulkPurchaseCommandResult {
    const cell = this.hovCells[cellIndex];
    if (!cell) {
      throw new Error(`invalid Hov cell index: ${cellIndex}`);
    }
    const enhancementLevelBefore = this.resolveHovCellEnhancementLevel(
      cell,
      cellIndex,
    );
    const plan = resolveHovGainUpgradePurchasePlan(
      this.buildHovCellGainPlannerInput(
        cellIndex,
        cell,
        "one-cost-bulk",
        maxPurchases,
      ),
    );
    if (!plan.ok) {
      return {
        ok: false,
        purchaseCount: 0,
        enhancementLevelBefore,
        enhancementLevelAfter: enhancementLevelBefore,
        spentHov: hugeZero(),
        finalCost: this.getEffectiveHovCellUpgradeCost(cellIndex),
        milestonePurchases: [],
      };
    }
    this.applyHovCellGainPurchasePlan(cellIndex, cell, plan);
    return {
      ok: true,
      purchaseCount: plan.purchaseCount,
      enhancementLevelBefore: plan.enhancementLevelBefore,
      enhancementLevelAfter: plan.enhancementLevelAfter,
      spentHov: plan.spentHov,
      finalCost: this.getEffectiveHovCellUpgradeCost(cellIndex),
      milestonePurchases: toBulkMilestoneRecords(plan),
    };
  }

  private buildGlobalHovGainPlannerInput(
    mode: HovGainUpgradePurchaseMode,
    maxPurchases?: number,
  ): HovGainUpgradePurchasePlannerInput {
    const nHovDivisor = this.getNHovBranchEffects().hgDiscountCostDivisor;
    return {
      mode,
      upgradeAvailable: true,
      oneCostBatchEnabled: this.oneCostHoverRepeaterBatchEnabled,
      availableHov: this.hov,
      currentEnhancementLevel: this.purchasedHovGainUpgrades,
      currentRawCost: this.upgradeCost,
      rawCostMultiplier: this.upgradeCostMultiplier,
      effectiveCostMultiplier: huge(
        this.achievementHovUpgradeCostMultiplier,
      ).div(nHovDivisor),
      cap: maxPurchases,
      simultaneousPurchaseCap: this.getSimultaneousPurchaseCap(),
    };
  }

  private buildHovCellGainPlannerInput(
    cellIndex: number,
    cell: HovCellState,
    mode: HovGainUpgradePurchaseMode,
    maxPurchases?: number,
  ): HovGainUpgradePurchasePlannerInput {
    const nHovDivisor = this.getNHovBranchEffects().hgDiscountToHngCostDivisor;
    const upgradeAvailable = this.resolveHovCellGainUpgradeAvailability(
      cellIndex,
      cell,
    );
    return {
      mode,
      upgradeAvailable,
      oneCostBatchEnabled: this.oneCostHoverRepeaterBatchEnabled,
      availableHov: this.hov,
      currentEnhancementLevel: this.resolveHovCellEnhancementLevel(
        cell,
        cellIndex,
      ),
      currentRawCost: cell.upgradeCost,
      rawCostMultiplier: HOV_CELL_GAIN_UPGRADE_COST_MULTIPLIER,
      effectiveCostMultiplier: huge(
        this.achievementHovCellUpgradeCostMultiplier,
      ).div(nHovDivisor),
      cap: maxPurchases,
      simultaneousPurchaseCap: this.getSimultaneousPurchaseCap(),
    };
  }

  // Shared with resolveHovGainUpgradeMpTargetAvailability (the HGU-MP
  // threshold cache's cheap validity check): a Hov cell HGU target is
  // purchasable when its grid tier is unlocked and its own unlock isn't
  // currently blocked by an active wall.
  private resolveHovCellGainUpgradeAvailability(
    cellIndex: number,
    cell: HovCellState,
  ): boolean {
    return (
      this.isHovCellGainUpgradeAvailable(cellIndex) &&
      !this.isHovCellUnlockBlockedByActiveWall(cellIndex, cell.unlocked)
    );
  }

  // Composes the HG / HnG simultaneous purchase cap from two sources:
  //   base = 1 + sum of Hov-skill additive bonuses (HUC+ contributes +1).
  //   wall = aggregate "simultaneous-purchase-count" reward multiplier
  //          across walls (currently Price Wall).
  // The planner cap is `base * wall`, so a skill-derived base of 2 still
  // composes with a cleared Price Wall and vice versa.
  private getSimultaneousPurchaseCap(): number {
    const base = 1 + this.hovSkillSimultaneousPurchaseCountBaseBonus;
    const wallMultiplier = resolveAggregateWallRewardMultiplier(
      this.wallState,
      "simultaneous-purchase-count",
      this.wallRewardCapLevel,
    );
    return Math.max(1, Math.floor(base * wallMultiplier));
  }

  getPriceWallRewardMultiplier(): number {
    return getWallChallengePolicy("price-wall").resolveRewardMultiplier(
      this.wallState,
      this.wallRewardCapLevel,
    );
  }

  private applyGlobalHovGainPurchasePlan(
    plan: HovGainUpgradePurchasePlan,
  ): void {
    if (!plan.ok || plan.purchaseCount <= 0) return;
    this.hov = this.hov.sub(plan.spentHov);
    this.purchasedHovGainUpgrades = plan.enhancementLevelAfter;
    this.upgradeCost = plan.rawCostAfter;
    for (const record of plan.milestonePurchases) {
      this.recordHovGainMilestoneTriggerCost(
        calculateUpgradeMilestoneCount(record.enhancementLevelBefore),
        record.effectiveCostPaid,
      );
    }
    this.incrementHovGainFamilyUpgradeStats(plan.purchaseCount);
    this.syncMainResourceGains();
  }

  private applyHovCellGainPurchasePlan(
    cellIndex: number,
    cell: HovCellState,
    plan: HovGainUpgradePurchasePlan,
  ): void {
    if (!plan.ok || plan.purchaseCount <= 0) return;
    const wasUnlocked = cell.unlocked;

    this.hov = this.hov.sub(plan.spentHov);

    if (!wasUnlocked) {
      cell.unlocked = true;
      cell.hovGainMultiplier = huge(HOV_CELL_UNLOCK_INITIAL_MULTIPLIER);
    }
    const enhanceCount = plan.purchaseCount - (wasUnlocked ? 0 : 1);
    if (enhanceCount > 0) {
      cell.hovGainMultiplier = cell.hovGainMultiplier.add(
        huge(HOV_CELL_GAIN_MULTIPLIER_INCREMENT).mul(enhanceCount),
      );
    }
    cell.upgradeCost = plan.rawCostAfter;
    cell.purchasedUpgrades += plan.purchaseCount;

    if (!wasUnlocked) {
      this.recordHovGainUnlockCost(cellIndex, plan.firstEffectiveCost);
    }
    this.incrementHovGainFamilyUpgradeStats(plan.purchaseCount);
  }

  /**
   * Exact one-cost preview for tooltip/detail surfaces. Regular snapshots use
   * lightweight summaries so `getSnapshot()` stays on the cheap path.
   */
  previewGlobalHovGainUpgradeOneCostBatch():
    | HovGainUpgradeOneCostBatchPreview
    | undefined {
    const effectiveCost = this.getEffectiveUpgradeCost();
    const summary = this.createGlobalHovGainUpgradeOneCostBatchExactSummary({
      effectiveCost,
    });
    if (summary === undefined) {
      return undefined;
    }
    return {
      ...summary,
      valueAfter: this.resolveDisplayedHovGainPerSecond(
        summary.enhancementLevelAfter,
      ),
    };
  }

  /**
   * Cell-scoped exact one-cost preview for hovered rows and detail surfaces.
   * Regular HnG snapshot rows use lightweight summaries.
   */
  previewHovCellGainUpgradeOneCostBatch(
    index: HovCellGainUpgradeIndex,
  ): HovGainUpgradeOneCostBatchPreview | undefined {
    if (!Number.isInteger(index) || index < 1 || index > 9) {
      throw new Error(`invalid Hov cell gain upgrade index: ${index}`);
    }
    const cellIndex = index - 1;
    const cell = this.hovCells[cellIndex];
    if (cell === undefined) {
      return undefined;
    }
    const upgradeAvailable = this.isHovCellGainUpgradeAvailable(cellIndex);
    const effectiveCost = this.getEffectiveHovCellUpgradeCost(cellIndex);
    const summary = this.createHovCellGainUpgradeOneCostBatchExactSummary({
      cellIndex,
      cell,
      upgradeAvailable,
      effectiveCost,
    });
    if (summary === undefined) {
      return undefined;
    }
    return {
      ...summary,
      valueAfter: this.resolveHovCellGainPerSecondAfterPurchases(
        cellIndex,
        cell,
        summary.purchaseCount,
        summary.enhancementLevelAfter,
      ),
    };
  }

  private createGlobalHovGainUpgradeOneCostBatchSummary(options: {
    readonly effectiveCost: HugeNumber;
  }): HovGainUpgradeOneCostBatchSummary | undefined {
    const achievementMultiplier = this.achievementHovUpgradeCostMultiplier;
    const nHovDivisor = this.getNHovBranchEffects().hgDiscountCostDivisor;
    return createOneCostBulkPurchaseLightweightSummary({
      batchEnabled: this.oneCostHoverRepeaterBatchEnabled,
      currentEnhancementLevel: this.purchasedHovGainUpgrades,
      availableHov: this.hov,
      effectiveCost: options.effectiveCost,
      currentRawCost: this.upgradeCost,
      effectiveCostMultiplier: huge(achievementMultiplier).div(nHovDivisor),
      rawCostMultiplier: this.upgradeCostMultiplier,
    });
  }

  private createGlobalHovGainUpgradeOneCostBatchExactSummary(options: {
    readonly effectiveCost: HugeNumber;
  }): HovGainUpgradeOneCostBatchSummary | undefined {
    const achievementMultiplier = this.achievementHovUpgradeCostMultiplier;
    const nHovDivisor = this.getNHovBranchEffects().hgDiscountCostDivisor;
    return createOneCostBulkPurchaseExactSummary({
      batchEnabled: this.oneCostHoverRepeaterBatchEnabled,
      currentEnhancementLevel: this.purchasedHovGainUpgrades,
      availableHov: this.hov,
      effectiveCost: options.effectiveCost,
      currentRawCost: this.upgradeCost,
      effectiveCostMultiplier: huge(achievementMultiplier).div(nHovDivisor),
      rawCostMultiplier: this.upgradeCostMultiplier,
    });
  }

  private createHovCellGainUpgradeOneCostBatchSummary(options: {
    readonly cellIndex: number;
    readonly cell: HovCellState;
    readonly upgradeAvailable: boolean;
    readonly effectiveCost: HugeNumber;
  }): HovGainUpgradeOneCostBatchSummary | undefined {
    const achievementMultiplier = this.achievementHovCellUpgradeCostMultiplier;
    const nHovDivisor = this.getNHovBranchEffects().hgDiscountToHngCostDivisor;
    return createOneCostBulkPurchaseLightweightSummary({
      batchEnabled: this.oneCostHoverRepeaterBatchEnabled,
      upgradeAvailable: options.upgradeAvailable,
      currentEnhancementLevel: this.resolveHovCellEnhancementLevel(
        options.cell,
        options.cellIndex,
      ),
      availableHov: this.hov,
      effectiveCost: options.effectiveCost,
      currentRawCost: options.cell.upgradeCost,
      effectiveCostMultiplier: huge(achievementMultiplier).div(nHovDivisor),
      rawCostMultiplier: HOV_CELL_GAIN_UPGRADE_COST_MULTIPLIER,
    });
  }

  private createHovCellGainUpgradeOneCostBatchExactSummary(options: {
    readonly cellIndex: number;
    readonly cell: HovCellState;
    readonly upgradeAvailable: boolean;
    readonly effectiveCost: HugeNumber;
  }): HovGainUpgradeOneCostBatchSummary | undefined {
    const achievementMultiplier = this.achievementHovCellUpgradeCostMultiplier;
    const nHovDivisor = this.getNHovBranchEffects().hgDiscountToHngCostDivisor;
    return createOneCostBulkPurchaseExactSummary({
      batchEnabled: this.oneCostHoverRepeaterBatchEnabled,
      upgradeAvailable: options.upgradeAvailable,
      currentEnhancementLevel: this.resolveHovCellEnhancementLevel(
        options.cell,
        options.cellIndex,
      ),
      availableHov: this.hov,
      effectiveCost: options.effectiveCost,
      currentRawCost: options.cell.upgradeCost,
      effectiveCostMultiplier: huge(achievementMultiplier).div(nHovDivisor),
      rawCostMultiplier: HOV_CELL_GAIN_UPGRADE_COST_MULTIPLIER,
    });
  }

  /**
   * Preview of the planner-driven simultaneous purchase that a single regular
   * purchase click will execute. Returns undefined when the cap collapses to 1
   * (no Price Wall reward, or available Hov can only afford 1 purchase) so
   * callers can keep the existing single-purchase display.
   */
  private createGlobalHovGainUpgradeSimultaneousPurchasePreview(options: {
    readonly effectiveCost: HugeNumber;
  }): HovGainUpgradeSimultaneousPurchasePreview | undefined {
    const cap = this.getSimultaneousPurchaseCap();
    if (cap <= 1) return undefined;
    if (compareHuge(this.hov, options.effectiveCost) < 0) return undefined;
    const plan = resolveHovGainUpgradePurchasePlan(
      this.buildGlobalHovGainPlannerInput("single"),
    );
    if (!plan.ok || plan.purchaseCount <= 1) return undefined;
    return {
      purchaseCount: plan.purchaseCount,
      enhancementLevelAfter: plan.enhancementLevelAfter,
      spentHov: plan.spentHov,
      valueAfter: this.resolveDisplayedHovGainPerSecond(
        plan.enhancementLevelAfter,
      ),
    };
  }

  private createHovCellGainUpgradeSimultaneousPurchasePreview(options: {
    readonly cellIndex: number;
    readonly cell: HovCellState;
    readonly upgradeAvailable: boolean;
    readonly effectiveCost: HugeNumber;
    readonly baseGainPerSecond?: HugeNumber;
  }): HovGainUpgradeSimultaneousPurchasePreview | undefined {
    if (!options.upgradeAvailable) return undefined;
    const cap = this.getSimultaneousPurchaseCap();
    if (cap <= 1) return undefined;
    if (compareHuge(this.hov, options.effectiveCost) < 0) return undefined;
    const plan = resolveHovGainUpgradePurchasePlan(
      this.buildHovCellGainPlannerInput(
        options.cellIndex,
        options.cell,
        "single",
      ),
    );
    if (!plan.ok || plan.purchaseCount <= 1) return undefined;
    return {
      purchaseCount: plan.purchaseCount,
      enhancementLevelAfter: plan.enhancementLevelAfter,
      spentHov: plan.spentHov,
      valueAfter: this.resolveHovCellGainPerSecondAfterPurchases(
        options.cellIndex,
        options.cell,
        plan.purchaseCount,
        plan.enhancementLevelAfter,
        options.baseGainPerSecond,
      ),
    };
  }

  private resolveHovCellGainPerSecondAfterPurchases(
    cellIndex: number,
    cell: HovCellState,
    purchaseCount: number,
    enhancementLevelAfter: number,
    baseGainPerSecond: HugeNumber = this.resolveEffectiveHovGainBasis(),
  ): HugeNumber {
    const increment = huge(HOV_CELL_GAIN_MULTIPLIER_INCREMENT);
    const multiplierAfter = cell.unlocked
      ? cell.hovGainMultiplier.add(increment.mul(purchaseCount))
      : huge(HOV_CELL_UNLOCK_INITIAL_MULTIPLIER).add(
          increment.mul(Math.max(0, purchaseCount - 1)),
        );
    return this.resolveCellGainPerSecondWithMultiplier(
      cellIndex,
      multiplierAfter,
      enhancementLevelAfter,
      baseGainPerSecond,
    );
  }

  private recordHovGainUnlockCost(
    targetIndex: number,
    effectiveCost: HugeNumber,
  ): void {
    const current = this.minHovGainUnlockCostsByTargetIndex[targetIndex];
    if (current === undefined) {
      throw new Error(
        `invalid Hov Gain unlock cost target index: ${targetIndex}`,
      );
    }
    if (current === null || compareHuge(effectiveCost, current) < 0) {
      this.minHovGainUnlockCostsByTargetIndex[targetIndex] = effectiveCost;
    }
  }

  /**
   * Records the effective cost actually paid on the purchase that triggered an
   * HG milestone, keyed by milestone index. Keeps the lowest cost seen per
   * index so the record stays a lifetime minimum across resets and replays.
   */
  private recordHovGainMilestoneTriggerCost(
    milestoneIndex: number,
    effectiveCost: HugeNumber,
  ): void {
    if (!Number.isInteger(milestoneIndex) || milestoneIndex < 0) {
      throw new Error(`invalid Hov Gain milestone index: ${milestoneIndex}`);
    }
    const costs = this.minHovGainMilestoneTriggerCostsByMilestoneIndex;
    while (costs.length <= milestoneIndex) {
      costs.push(null);
    }
    const current = costs[milestoneIndex];
    if (
      current === null ||
      current === undefined ||
      compareHuge(effectiveCost, current) < 0
    ) {
      costs[milestoneIndex] = effectiveCost;
    }
  }

  // Basis that matches the Hov actually credited per second: BHG after the HG
  // power exponent, active HG wall constraint, Amplifier HG multiplier, and the
  // `hov-gain` wall reward. HnG cell display/preview must scale from this, not
  // the BHG-stage `hovGainPerSecond`, so values and previews match real gain.
  private resolveEffectiveHovGainBasis(): HugeNumber {
    return this.resolveCurrentHovGainPipeline().finalHovGainPerSecond;
  }

  private resolveHoveringCellGainPerSecond(
    baseGainPerSecond = this.hovGainPerSecond,
  ): HugeNumber {
    const index = this.hovHoveringCellIndex;
    if (index === null) {
      return baseGainPerSecond;
    }
    return this.resolveCellGainPerSecond(index, baseGainPerSecond);
  }

  private resolveCellGainPerSecond(
    index: number,
    baseGainPerSecond = this.hovGainPerSecond,
    cells: readonly HovCellState[] = this.hovCells,
  ): HugeNumber {
    const cell = cells[index];
    if (!cell?.unlocked) return hugeZero();
    return this.resolveCellGainPerSecondWithMultiplier(
      index,
      cell.hovGainMultiplier,
      this.resolveHovCellEnhancementLevel(cell, index),
      baseGainPerSecond,
    );
  }

  /**
   * Player-facing HnG enhancement level for a Hov cell. A locked cell is 0; the
   * unlocked state itself is level 1, and every upgrade adds one level. Cell 0
   * (H1G) is unlocked at game start without a purchase, so its unlock is not
   * counted in `purchasedUpgrades` and is added back here; every other cell's
   * unlock is its first purchase. This level is the basis for HnG milestones.
   */
  private resolveHovCellEnhancementLevel(
    cell: HovCellState,
    index: number,
  ): number {
    if (!cell.unlocked) return 0;
    return cell.purchasedUpgrades + (index === 0 ? 1 : 0);
  }

  private resolveCellGainPerSecondWithMultiplier(
    index: number,
    hovGainMultiplier: HugeNumber,
    enhancementLevel: number,
    baseGainPerSecond: HugeNumber = this.hovGainPerSecond,
  ): HugeNumber {
    if (!isPositiveHuge(hovGainMultiplier)) return hugeZero();
    const effects = this.getNHovBranchEffects();
    const cellGain = baseGainPerSecond
      .mul(
        this.resolveEffectiveHovCellGainMultiplier(
          hovGainMultiplier,
          enhancementLevel,
        ),
      )
      .mul(this.resolveHovCellRankMultiplier(index))
      .mul(effects.hgAmplifyToHngGainMultiplier);
    const withExponentBonus = this.applyH1gExponentBonus(index, cellGain);
    return this.applyHov1GainFinalMultiplier(index, withExponentBonus);
  }

  // Final x multiplier on the H1G cell only, so it does not propagate to
  // H2G..H9G or HnG. Composes the hov-1-gain-multiplier-bonus skill multiplier
  // with the Split Wall clear reward (the `hov1-gain` wall reward dimension).
  private applyHov1GainFinalMultiplier(
    index: number,
    cellGain: HugeNumber,
  ): HugeNumber {
    if (index !== 0) return cellGain;
    const wallHov1GainMultiplier = resolveAggregateWallRewardMultiplier(
      this.wallState,
      "hov1-gain",
      this.wallRewardCapLevel,
    );
    return cellGain.mul(this.hov1GainMultiplier).mul(wallHov1GainMultiplier);
  }

  // Raises the H1G cell's effective Hov gain to (1 + bonus). Applied only to
  // index 0 so this bonus does not propagate to H2G..H9G or HnG, and only when
  // the value is finite and >= 1 so log10 / pow stay well-defined.
  private applyH1gExponentBonus(
    index: number,
    cellGain: HugeNumber,
  ): HugeNumber {
    if (index !== 0) return cellGain;
    const bonus = this.h1gExponentBonus;
    if (bonus <= 0 || !Number.isFinite(bonus)) return cellGain;
    if (!cellGain.isFinite()) return cellGain;
    if (compareHuge(cellGain, 1) < 0) return cellGain;
    return cellGain.pow(1 + bonus);
  }

  private resolveEffectiveHovCellGainMultiplier(
    hovGainMultiplier: HugeNumber,
    enhancementLevel: number,
  ): HugeNumber {
    const milestoneCount = calculateUpgradeMilestoneCount(enhancementLevel);
    const milestoneAdjustedMultiplier = maxHuge(
      hovGainMultiplier.sub(
        huge(HOV_CELL_GAIN_MULTIPLIER_INCREMENT).mul(milestoneCount),
      ),
      hugeZero(),
    );
    return milestoneAdjustedMultiplier.mul(
      calculateUpgradeMilestoneMultiplier(enhancementLevel),
    );
  }

  private resolveHovCellRankMultiplier(index: number): HugeNumber {
    const branchLevels = this.getDerivedNHovBranchLevels();
    return calculateHovCellRankMultiplierHuge({
      cellIndex: index,
      hngScaleLevel: branchLevels.hngScale,
      hngRankScaleBaseBonus: this.hngRankScaleBaseBonus,
      exponentBonus: this.growthExponentBonus.nHovHngScale,
      balance: this.resetBalance.nHov.branches.hngScale,
    });
  }

  private isCellUnlocked(index: number): boolean {
    return this.hovCells[index]?.unlocked ?? false;
  }

  private isFirstHovCellGainUpgradeAvailable(): boolean {
    return this.hovGridTier >= HOV_GRID_INCREASE_1_TIER;
  }

  private isHovCellGainUpgradeAvailable(cellIndex: number): boolean {
    return cellIndex === 0
      ? this.isFirstHovCellGainUpgradeAvailable()
      : this.isCellVisibleForTier(cellIndex, this.hovGridTier);
  }

  private isHovCellUnlockBlockedByActiveWall(
    cellIndex: number,
    alreadyUnlocked: boolean,
  ): boolean {
    if (alreadyUnlocked) return false;
    return isHovCellUnlockBlockedByActiveWall(this.wallState, cellIndex);
  }

  private isCellVisibleForTier(
    index: number,
    tier: HoverIdleGridTier,
  ): boolean {
    return index >= 0 && index < tier;
  }

  setHoveringTim(isHovering: boolean): void {
    this.isHoveringTim = isHovering;
    if (!isHovering) {
      this.timHoveringCellIndex = null;
    }
  }

  setTimHoveringCellIndex(index: number | null): boolean {
    if (index === null) {
      this.timHoveringCellIndex = null;
      this.isHoveringTim = false;
      return true;
    }
    if (!Number.isInteger(index) || index < 0 || index >= TIM_CELL_COUNT) {
      throw new Error(`invalid cell index: ${index}`);
    }
    if (!this.isCellVisibleForTier(index, this.timGridTier)) {
      return false;
    }
    if (!this.isTimCellUnlocked(index)) {
      return false;
    }
    this.timHoveringCellIndex = index;
    this.isHoveringTim = true;
    return true;
  }

  getTimGridTier(): HoverIdleGridTier {
    return this.timGridTier;
  }

  setTimGridTier(tier: HoverIdleGridTier): boolean {
    if (tier === this.timGridTier) return false;
    if (tier < this.timGridTier) return false;
    this.timGridTier = tier;
    if (
      this.timHoveringCellIndex !== null &&
      !this.isCellVisibleForTier(this.timHoveringCellIndex, tier)
    ) {
      this.timHoveringCellIndex = null;
      this.isHoveringTim = false;
    }
    return true;
  }

  purchaseTimCellUpgrade(cellIndex: number): TimUpgradePurchaseResult {
    this.syncThresholdUnlocks();

    if (cellIndex === 0) {
      return this.purchaseTimGainUpgrade();
    }
    if (
      !Number.isInteger(cellIndex) ||
      cellIndex < 1 ||
      cellIndex >= TIM_CELL_COUNT
    ) {
      throw new Error(`invalid cell index: ${cellIndex}`);
    }
    if (!this.isCellVisibleForTier(cellIndex, this.timGridTier)) {
      return {
        ok: false,
        reason: "insufficient-tim",
        snapshot: this.getSnapshot(),
      };
    }

    const cell = this.timCells[cellIndex];
    if (compareHuge(this.tim, cell.upgradeCost) < 0) {
      return {
        ok: false,
        reason: "insufficient-tim",
        snapshot: this.getSnapshot(),
      };
    }

    const spent = cell.upgradeCost;
    this.tim = this.tim.sub(spent);
    if (!cell.unlocked) {
      cell.unlocked = true;
      cell.timGainMultiplier = huge(TIM_CELL_UNLOCK_INITIAL_MULTIPLIER);
    } else {
      cell.timGainMultiplier = cell.timGainMultiplier.add(
        TIM_CELL_GAIN_MULTIPLIER_INCREMENT,
      );
    }
    cell.purchasedUpgrades += 1;
    cell.upgradeCost = ceilHugeAtLeast(
      cell.upgradeCost.mul(TIM_CELL_UPGRADE_COST_MULTIPLIER),
      1,
    );
    this.cumulativeStats = {
      ...this.cumulativeStats,
      totalTimSpent: this.cumulativeStats.totalTimSpent.add(spent),
      upgradesPurchased: this.cumulativeStats.upgradesPurchased + 1,
    };

    return { ok: true, snapshot: this.getSnapshot() };
  }

  private resolveHoveringTimCellGainPerSecond(
    baseGainPerSecond: HugeNumber = this.timGainPerSecond,
  ): HugeNumber {
    const index = this.timHoveringCellIndex;
    if (index === null) {
      return baseGainPerSecond;
    }
    return this.resolveTimCellGainPerSecond(index, baseGainPerSecond);
  }

  private resolveTimCellGainPerSecond(
    index: number,
    baseGainPerSecond: HugeNumber = this.timGainPerSecond,
  ): HugeNumber {
    const cell = this.timCells[index];
    if (!cell?.unlocked) return hugeZero();
    if (index === 0) {
      return baseGainPerSecond.mul(cell.timGainMultiplier);
    }
    return cell.timGainMultiplier;
  }

  private isTimCellUnlocked(index: number): boolean {
    return this.timCells[index]?.unlocked ?? false;
  }

  private syncTimRootCellState(): void {
    const cell = this.timCells[0];
    cell.unlocked = true;
    cell.timGainMultiplier = hugeOne();
    cell.upgradeCost = this.timUpgradeCost;
    cell.purchasedUpgrades = this.purchasedTimGainUpgrades;
  }

  getSettings(): HoverIdleSettingsState {
    return cloneSettingsState(this.settings);
  }

  getPaymentState(): HoverIdlePaymentState {
    return clonePaymentState(this.payment);
  }

  getEntitlementSnapshot(): EntitlementSnapshot {
    return cloneEntitlementSnapshot(this.getEffectiveEntitlementSnapshot());
  }

  setOwnerEntitlementSnapshot(snapshot: EntitlementSnapshot): boolean {
    if (areEntitlementSnapshotsEqual(this.ownerEntitlementSnapshot, snapshot)) {
      return false;
    }
    this.ownerEntitlementSnapshot = cloneEntitlementSnapshot(snapshot);
    this.syncMainResourceGains();
    return true;
  }

  hasEntitlement(key: BundleEntitlementKey): boolean {
    return this.payment.entitlements.includes(key);
  }

  isEntitlementClaimed(key: BundleEntitlementKey): boolean {
    return this.payment.claimedEntitlements.includes(key);
  }

  grantEntitlement(key: BundleEntitlementKey): EntitlementGrantResult {
    const decision = resolveEntitlementGrant(this.payment, key);
    if (!decision.ok) {
      return {
        ok: false,
        reason: decision.reason,
        payment: this.getPaymentState(),
      };
    }
    this.payment = decision.nextPayment;
    return {
      ok: true,
      payment: this.getPaymentState(),
    };
  }

  markEntitlementClaimed(key: BundleEntitlementKey): boolean {
    const nextPayment = resolveEntitlementClaim(this.payment, key);
    if (nextPayment === null) {
      return false;
    }
    this.payment = nextPayment;
    return true;
  }

  grantToken(amount: number): void {
    assertFinitePositive(amount, "amount");
    this.payment = withUpdatedPaymentState(this.payment, {
      token: this.payment.token + amount,
    });
  }

  grantHov(amount: HugeNumberInput): void {
    const grant = assertFinitePositiveHugeInput(amount, "amount");
    this.hov = this.hov.add(grant);
    this.syncThresholdUnlocks();
  }

  setTimHovActivationUnlocked(unlocked: boolean): boolean {
    if (this.isTimHovActivationUnlocked === unlocked) {
      return false;
    }
    this.isTimHovActivationUnlocked = unlocked;
    this.clearTimHovConsumptionObservation();
    return true;
  }

  setTimHovActivationEnabled(enabled: boolean): boolean {
    if (this.isTimHovActivationEnabled === enabled) {
      return false;
    }
    this.isTimHovActivationEnabled = enabled;
    this.clearTimHovConsumptionObservation();
    return true;
  }

  isTimHovActivationControlUnlocked(): boolean {
    return this.isTimHovActivationUnlocked;
  }

  isTimHovActivationActive(): boolean {
    return this.shouldRefineTimToHov();
  }

  private patchSettingsValue<K extends keyof HoverIdleSettingsState>(
    key: K,
    value: HoverIdleSettingsState[K],
  ): boolean {
    if (this.settings[key] === value) {
      return false;
    }
    this.settings = { ...this.settings, [key]: value };
    return true;
  }

  setReduceMotion(reduceMotion: boolean): boolean {
    return this.patchSettingsValue("reduceMotion", reduceMotion);
  }

  setLowPowerMode(lowPowerMode: boolean): boolean {
    return this.patchSettingsValue("lowPowerMode", lowPowerMode);
  }

  setOutsideResetBurstAnimationEnabled(enabled: boolean): boolean {
    return this.patchSettingsValue(
      "outsideResetBurstAnimationEnabled",
      enabled,
    );
  }

  setLanguage(language: SupportedLocale): boolean {
    return this.patchSettingsValue("language", language);
  }

  setNotation(notation: Notation): boolean {
    return this.patchSettingsValue("notation", notation);
  }

  setAchievementNotification(mode: AchievementNotificationMode): boolean {
    return this.patchSettingsValue("achievementNotification", mode);
  }

  setWallNotificationMode(mode: WallNotificationMode): boolean {
    return this.patchSettingsValue("wallNotificationMode", mode);
  }

  setHelpNotificationsEnabled(enabled: boolean): boolean {
    return this.patchSettingsValue("helpNotificationsEnabled", enabled);
  }

  markHelpTopicRead(topicId: HelpTopicId): boolean {
    if (this.settings.readHelpTopicIds.includes(topicId)) {
      return false;
    }
    return this.patchSettingsValue("readHelpTopicIds", [
      ...this.settings.readHelpTopicIds,
      topicId,
    ]);
  }

  setHovSkillHoldDurationMs(ms: HovSkillHoldMs): boolean {
    if (!isHovSkillHoldMs(ms)) {
      throw new Error(
        "settings.hovSkillHoldDurationMs must be a supported hold duration",
      );
    }
    return this.patchSettingsValue("hovSkillHoldDurationMs", ms);
  }

  setWallStartChargeDurationMs(ms: WallStartChargeMs): boolean {
    if (!isWallStartChargeMs(ms)) {
      throw new Error(
        "settings.wallStartChargeDurationMs must be a supported wall start charge duration",
      );
    }
    return this.patchSettingsValue("wallStartChargeDurationMs", ms);
  }

  setFirstAccessTutorialCompleted(completed: boolean): boolean {
    return this.patchSettingsValue("firstAccessTutorialCompleted", completed);
  }

  redeemRewardCode(code: string): RewardCodeRedeemResult {
    const decision = resolveRewardCodeRedeem(this.payment, code);
    if (!decision.ok) {
      return {
        ok: false,
        reason: decision.reason,
        payment: this.getPaymentState(),
      };
    }
    this.payment = decision.nextPayment;
    return {
      ok: true,
      payment: this.getPaymentState(),
    };
  }

  purchasePermanentItem(id: PermanentPurchaseId): PermanentPurchaseResult {
    const decision = resolvePermanentItemPurchase(this.payment, id);
    if (!decision.ok) {
      return {
        ok: false,
        reason: decision.reason,
        payment: this.getPaymentState(),
      };
    }
    this.payment = decision.nextPayment;
    this.syncLocalEntitlementSnapshot();
    return {
      ok: true,
      payment: this.getPaymentState(),
    };
  }

  grantTim(amount: HugeNumberInput): void {
    const grant = assertFinitePositiveHugeInput(amount, "amount");
    this.tim = this.tim.add(grant);
    this.syncThresholdUnlocks();
  }

  grantPermanentEffectFromBundle(id: PermanentPurchaseId): void {
    this.payment = withPermanentItemGrantedFromBundle(this.payment, id);
    this.syncLocalEntitlementSnapshot();
  }

  purchaseTimGainUpgrade(): TimUpgradePurchaseResult {
    this.syncThresholdUnlocks();

    if (!this.canPurchaseTimUpgrade()) {
      return {
        ok: false,
        reason: "insufficient-tim",
        snapshot: this.getSnapshot(),
      };
    }

    const spent = this.timUpgradeCost;
    this.tim = this.tim.sub(spent);
    this.purchasedTimGainUpgrades += 1;
    this.syncMainResourceGains();
    this.cumulativeStats = {
      ...this.cumulativeStats,
      totalTimSpent: this.cumulativeStats.totalTimSpent.add(spent),
      upgradesPurchased: this.cumulativeStats.upgradesPurchased + 1,
    };
    this.timUpgradeCost = ceilHugeAtLeast(
      this.timUpgradeCost.mul(this.timUpgradeCostMultiplier),
      1,
    );

    return {
      ok: true,
      snapshot: this.getSnapshot(),
    };
  }

  addFai(amount: number): void {
    assertFinitePositive(amount, "amount");
    this.fai += amount;
    this.cumulativeStats = {
      ...this.cumulativeStats,
      totalFaiEarned: this.cumulativeStats.totalFaiEarned + amount,
    };
    this.syncThresholdUnlocks();
  }

  /**
   * Lightweight pre-reset readout for the PlayLog MHov payload. Reads
   * `currentLoopHovEarned`, `hgMultiplier`, and `count` from `mHovState`
   * directly, and only runs the gain pipeline once for `hovGainPerSecond`.
   * Avoids the 9-cell snapshot build and other derivations that the MHov
   * reset would discard one frame later.
   */
  getMHovExecuteBeforeSnapshot(): MHovExecuteBeforeSnapshot {
    return {
      hov: this.hov,
      hovGainPerSecond:
        this.resolveCurrentHovGainPipeline().finalHovGainPerSecond,
      mHov: {
        currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
        hgMultiplier: this.mHovState.hgMultiplier,
        count: this.mHovState.count,
      },
    };
  }

  getMHovSnapshot(): MHovSnapshot {
    return buildHoverIdleMHovSnapshot(this.mHovState, {
      exponent: this.resolveEffectiveMHovRewardExponent(),
      balance: this.resetBalance.mHov,
      freshnessStaling: isFreshnessWallActive(this.wallState)
        ? {
            elapsedSinceHovRunMs: getFreshnessElapsedSinceHovRunMs(
              this.wallState,
            ),
          }
        : undefined,
      mhovRewardUncapped: this.achievementMhovRewardUncapped,
    });
  }

  getMHovAnimationSpeed(): MHovAnimationSpeed {
    return this.settings.mHovAnimationSpeed;
  }

  setMHovAnimationSpeed(value: MHovAnimationSpeed): boolean {
    return this.patchSettingsValue("mHovAnimationSpeed", value);
  }

  getMHovChargeDurationMs(): MHovChargeDurationMs {
    return this.settings.mHovChargeDurationMs;
  }

  setMHovChargeDurationMs(value: MHovChargeDurationMs): boolean {
    if (!isMHovChargeDurationMs(value)) {
      throw new Error("settings.mHovChargeDurationMs must be 0, 2000, or 5000");
    }
    return this.patchSettingsValue("mHovChargeDurationMs", value);
  }

  getNHovAnimationSpeed(): NHovAnimationSpeed {
    return this.settings.nHovAnimationSpeed;
  }

  setNHovAnimationSpeed(value: NHovAnimationSpeed): boolean {
    if (!isNHovAnimationSpeed(value)) {
      throw new Error(
        'settings.nHovAnimationSpeed must be "normal", "fast", or "instant"',
      );
    }
    return this.patchSettingsValue("nHovAnimationSpeed", value);
  }

  getNHovChargeDurationMs(): NHovChargeDurationMs {
    return this.settings.nHovChargeDurationMs;
  }

  setNHovChargeDurationMs(value: NHovChargeDurationMs): boolean {
    if (!isNHovChargeDurationMs(value)) {
      throw new Error("settings.nHovChargeDurationMs must be 0, 2000, or 5000");
    }
    return this.patchSettingsValue("nHovChargeDurationMs", value);
  }

  getOHovChargeDurationMs(): OHovChargeDurationMs {
    return this.settings.oHovChargeDurationMs;
  }

  setOHovChargeDurationMs(value: OHovChargeDurationMs): boolean {
    if (!isOHovChargeDurationMs(value)) {
      throw new Error("settings.oHovChargeDurationMs must be 0, 2000, or 5000");
    }
    return this.patchSettingsValue("oHovChargeDurationMs", value);
  }

  getHoverToggleDuration(): HoverToggleDuration {
    return this.settings.hoverToggleDuration;
  }

  // Resolved ms for small-square hover toggles (MP slot assign/unassign,
  // MPM section enabled toggle, TimHov activation). View callers read this
  // via a getter so each new charge picks up the latest setting.
  getHoverToggleDurationMs(): number {
    return resolveHoverToggleDurationMs(this.settings.hoverToggleDuration);
  }

  setHoverToggleDuration(value: HoverToggleDuration): boolean {
    return this.patchSettingsValue("hoverToggleDuration", value);
  }

  getHoverChargeDuration(): HoverChargeDuration {
    return this.settings.hoverChargeDuration;
  }

  getHoverChargeDurationMs(): number {
    return resolveHoverChargeDurationMs(this.settings.hoverChargeDuration);
  }

  setHoverChargeDuration(value: HoverChargeDuration): boolean {
    return this.patchSettingsValue("hoverChargeDuration", value);
  }

  getAmplifierUpgradeGuardMs(): AmplifierUpgradeGuardMs {
    return this.settings.amplifierUpgradeGuardMs;
  }

  setAmplifierUpgradeGuardMs(value: AmplifierUpgradeGuardMs): boolean {
    if (!isAmplifierUpgradeGuardMs(value)) {
      throw new Error(
        "settings.amplifierUpgradeGuardMs must be 0, 1000, or 3000",
      );
    }
    return this.patchSettingsValue("amplifierUpgradeGuardMs", value);
  }

  markSeenAvailableAmplifierUpgradeGuard(): boolean {
    return this.patchSettingsValue(
      "hasSeenAvailableAmplifierUpgradeGuard",
      true,
    );
  }

  // Persistent latches set the first time the MHov / NHov reset button is
  // displayable on the Hov screen. Settings / Help read them for reveal so the
  // reveal cannot disappear when a deeper reset resets the per-run MHov / NHov
  // count.
  markSeenMHovButton(): boolean {
    return this.patchSettingsValue("hasSeenMHovButton", true);
  }

  markSeenNHovButton(): boolean {
    return this.patchSettingsValue("hasSeenNHovButton", true);
  }

  executeMHov(): {
    readonly ok: boolean;
    readonly snapshot: HoverIdleSnapshot;
  } {
    const ok = this.executeMHovMutation();
    return { ok, snapshot: this.getSnapshot() };
  }

  private executeMHovMutation(): boolean {
    const rewardBaseHov = this.mHovState.currentLoopHovEarned;
    if (!this.canExecuteMHovReset(rewardBaseHov)) {
      return false;
    }

    const reward = this.calculateMHovResetReward(rewardBaseHov);
    const plan = this.selectMHovResetPlan(rewardBaseHov, reward);

    this.recordMHovPreResetMilestones(reward);
    this.applyMHovResetPlanToState(plan);
    this.recordAnyResetNow();

    return true;
  }

  private canExecuteMHovReset(rewardBaseHov: HugeNumber): boolean {
    return this.canExecuteMHovResetAtRunBaseHov(rewardBaseHov, 0);
  }

  private canExecuteMHovResetAtRunBaseHov(
    rewardBaseHov: HugeNumber,
    elapsedOffsetMs: number,
  ): boolean {
    const currentMultiplier = this.mHovState.hgMultiplier;
    const rewardResolution = resolveMHovRewardMultiplier(
      rewardBaseHov,
      this.resolveMHovRewardMultiplierOptionsAtElapsed(elapsedOffsetMs),
    );
    return canExecuteMHov(
      {
        currentLoopHovEarned: rewardBaseHov,
        currentMultiplier,
        freshRewardMultiplier: rewardResolution.effectiveFreshRewardMultiplier,
        effectiveRewardMultiplier: rewardResolution.effectiveRewardMultiplier,
      },
      this.resetBalance.mHov,
    );
  }

  private calculateMHovResetReward(rewardBaseHov: HugeNumber): MHovResetReward {
    return {
      multiplierBefore: this.mHovState.hgMultiplier,
      outcome: computeMHovExecuteOutcome(
        this.mHovState,
        rewardBaseHov,
        this.resolveMHovRewardMultiplierOptions(),
      ),
    };
  }

  private selectMHovResetPlan(
    rewardBaseHov: HugeNumber,
    reward: MHovResetReward,
  ): MHovResetPlan {
    return resolveMHovResetPlan({ rewardBaseHov, outcome: reward.outcome });
  }

  private recordMHovPreResetMilestones(reward: MHovResetReward): void {
    // Record the pre-reset fact before the reset clears the Hov target cells:
    // this MHov ran while exactly one Hov target cell was unlocked.
    if (this.countUnlockedHovTargetCells() === 1) {
      this.recordMergeHovWithOnlyOneHovTargetCell();
    }

    // Latch the massive-merge record off the before/after ratio of the MHov
    // multiplier, so the achievement reflects how much a single MHov grew the
    // multiplier rather than the absolute post-execution value. Independent of
    // `pop` / `bomb`, which judge the Hov/HG scale at MHov execution time.
    if (
      isMassiveMergeHovRatioMet({
        multiplierBefore: reward.multiplierBefore,
        multiplierAfter: reward.outcome.newHgMultiplier,
      })
    ) {
      this.recordMassiveMergeHov();
    }

    // Latch the outside-mhov record off the raw reward multiplier: reaching
    // the Number-limit boundary unlocks the achievement even though this
    // execution's applied reward is still clamped at the boundary.
    if (
      compareHuge(reward.outcome.rawRewardMultiplier, NUMBER_LIMIT_BOUNDARY) >=
      0
    ) {
      this.recordOutsideMhov();
    }
  }

  private countUnlockedHovTargetCells(): number {
    let count = 0;
    for (const cell of this.hovCells) {
      if (cell.unlocked) count += 1;
    }
    return count;
  }

  // Stored on cumulativeStats so the flag survives MHov / NHov / OHov resets.
  private recordMergeHovWithOnlyOneHovTargetCell(): void {
    if (this.cumulativeStats.hasExecutedMergeHovWithOnlyOneHovTargetCell) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      hasExecutedMergeHovWithOnlyOneHovTargetCell: true,
    };
  }

  // Stored on cumulativeStats so the flag survives MHov / NHov / OHov resets.
  private recordMassiveMergeHov(): void {
    if (this.cumulativeStats.hasExecutedMassiveMergeHov) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      hasExecutedMassiveMergeHov: true,
    };
  }

  // Stored on cumulativeStats so the flag survives MHov / NHov / OHov resets.
  private recordOutsideMhov(): void {
    if (this.cumulativeStats.hasExecutedOutsideMhov) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      hasExecutedOutsideMhov: true,
    };
  }

  // Stored on cumulativeStats so the flag survives MHov / NHov / OHov resets.
  private recordOutsideNhov(): void {
    if (this.cumulativeStats.hasExecutedOutsideNhov) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      hasExecutedOutsideNhov: true,
    };
  }

  // Stored on cumulativeStats so the flag survives MHov / NHov / OHov resets.
  private recordRedAmplifierFullChargeReached(): void {
    if (this.cumulativeStats.redAmplifierFullChargeReached) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      redAmplifierFullChargeReached: true,
    };
  }

  executeNHov(branchId: NHovBranchId): boolean {
    const runBaseHov = this.mHovState.currentLoopHovEarned;
    const prospective = this.resolveProspectiveNHovReset(branchId, runBaseHov);
    if (!this.canExecuteNHovReset(branchId, runBaseHov, 0, prospective)) {
      return false;
    }

    this.recordNHovPreResetMilestones();
    const reward = this.calculateNHovResetReward(
      branchId,
      runBaseHov,
      prospective,
    );
    const plan = this.selectNHovResetPlan(branchId, reward);

    this.applyNHovResetPlanToState(plan);
    this.wallState = incrementStockWallNhovUsedCountInState(this.wallState);
    this.recordNHovPostResetMilestones();
    this.recordAnyResetNow();

    return true;
  }

  private canExecuteNHovReset(
    branchId: NHovBranchId,
    runBaseHov: HugeNumber,
    elapsedOffsetMs = 0,
    resolvedProspectiveEffects?: ReturnType<
      typeof resolveProspectiveNHovBranchEffects
    >,
  ): boolean {
    return canExecuteNHovBranch({
      branchId,
      nHovState: this.nHovState,
      runBaseHov,
      isBlockedByActiveWall: isNHovBranchBlockedByActiveWall(
        this.wallState,
        branchId,
      ),
      firstExecutionBranchSelectionUnlocked:
        hasStockWallFirstNhovBranchSelectionReward(this.wallState),
      hngRankScaleBaseBonus: this.hngRankScaleBaseBonus,
      growthExponentBonus: this.resolveNHovGrowthExponentBonus(),
      uncapped: this.achievementNhovBranchEffectUncapped,
      freshnessExponent: isFreshnessWallActive(this.wallState)
        ? this.resolveNHovFreshnessExponent(elapsedOffsetMs)
        : undefined,
      resolvedProspectiveEffects,
      balance: this.resetBalance.nHov,
    });
  }

  private resolveProspectiveNHovReset(
    branchId: NHovBranchId,
    runBaseHov: HugeNumber,
    elapsedOffsetMs = 0,
  ): ReturnType<typeof resolveProspectiveNHovBranchEffects> {
    return resolveProspectiveNHovBranchEffects({
      branchId,
      nHovState: this.nHovState,
      runBaseHov,
      hngRankScaleBaseBonus: this.hngRankScaleBaseBonus,
      growthExponentBonus: this.resolveNHovGrowthExponentBonus(),
      uncapped: this.achievementNhovBranchEffectUncapped,
      freshnessExponent: isFreshnessWallActive(this.wallState)
        ? this.resolveNHovFreshnessExponent(elapsedOffsetMs)
        : undefined,
      balance: this.resetBalance.nHov,
    });
  }

  private calculateNHovResetReward(
    branchId: NHovBranchId,
    runBaseHov: HugeNumber,
    resolvedProspectiveEffects?: ReturnType<
      typeof resolveProspectiveNHovBranchEffects
    >,
  ): NHovResetReward {
    const currentEffectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
      this.nHovState.effectBoundaryCaps,
    );
    const boundaryReexecutionState =
      branchId === "hgDiscount" &&
      currentEffectBoundaryCaps.hgDiscountCostDivisor
        ? resolveHgDiscountBoundaryReexecutionState({
            runBaseHov,
            balance: this.resetBalance.nHov,
          })
        : null;
    const prospective =
      resolvedProspectiveEffects ??
      this.resolveProspectiveNHovReset(branchId, runBaseHov);
    const nextBranchExperience = prospective.experienceAfter;
    const nextBranchExperienceByBranch = {
      ...this.nHovState.branchExperienceByBranch,
      [branchId]: nextBranchExperience,
    };
    const nextEffectBoundaryCaps =
      boundaryReexecutionState !== null
        ? {
            ...currentEffectBoundaryCaps,
            hgDiscountCostDivisor:
              !boundaryReexecutionState.shouldClearBoundaryMetadata,
          }
        : this.resolveNHovEffectBoundaryCapsAfterExecution({
            branchId,
            nextBranchExperienceByBranch,
          });
    const nextContinuousEffectProgressByBranch: Partial<
      Record<NHovBranchId, true>
    > = {
      ...normalizeNHovContinuousEffectProgressByBranch(
        this.nHovState.continuousEffectProgressByBranch,
      ),
    };
    if (isFreshnessWallActive(this.wallState)) {
      nextContinuousEffectProgressByBranch[branchId] = true;
    } else {
      delete nextContinuousEffectProgressByBranch[branchId];
    }

    return {
      nextCount: this.nHovState.count + 1,
      nextBranchExperienceByBranch,
      nextEffectBoundaryCaps,
      nextContinuousEffectProgressByBranch,
    };
  }

  private resolveNHovFreshnessExponent(elapsedOffsetMs = 0): number {
    if (!isFreshnessWallActive(this.wallState)) return 1;
    return resolveFreshnessWallNHovStalingExponent(
      getFreshnessElapsedSinceHovRunMs(this.wallState) +
        Math.max(0, elapsedOffsetMs),
    );
  }

  private resolveNHovEffectBoundaryCapsAfterExecution(options: {
    readonly branchId: NHovBranchId;
    readonly nextBranchExperienceByBranch: NHovBranchExperienceByBranch;
  }): NHovEffectBoundaryCaps {
    const current = normalizeNHovEffectBoundaryCaps(
      this.nHovState.effectBoundaryCaps,
    );
    if (options.branchId !== "hgDiscount") {
      return current;
    }
    if (current.hgDiscountCostDivisor) {
      return {
        ...current,
        hgDiscountCostDivisor: false,
      };
    }
    if (
      !this.cumulativeStats.hasExecutedOutsideNhov &&
      this.doesNHovHgDiscountCandidateReachNumberLimit(
        options.nextBranchExperienceByBranch,
      )
    ) {
      return {
        ...current,
        hgDiscountCostDivisor: true,
      };
    }
    return {
      ...current,
      hgDiscountCostDivisor: false,
    };
  }

  private selectNHovResetPlan(
    branchId: NHovBranchId,
    reward: NHovResetReward,
  ): NHovResetPlan {
    const offer = resolveNHovCandidateOffer(this.nHovState.count);
    return resolveNHovResetPlan({
      branchId,
      selectedThemeHue: offer.themesByBranchId[branchId].hue,
      nextCount: reward.nextCount,
      nextBranchExperienceByBranch: reward.nextBranchExperienceByBranch,
      nextEffectBoundaryCaps: reward.nextEffectBoundaryCaps,
      nextContinuousEffectProgressByBranch:
        reward.nextContinuousEffectProgressByBranch,
    });
  }

  private recordNHovPreResetMilestones(): void {
    // Record the current-NHov-loop fact before the reset clears mHov state:
    // this NHov ran while the loop had performed zero MHov.
    if (this.mHovState.count === 0) {
      this.recordNewHovWithoutMergeHov();
    }
  }

  private recordNHovPostResetMilestones(): void {
    // Latch the not-fully-free record off the post-grant divisor, so a single
    // NHov execution that crosses the threshold unlocks the achievement.
    this.recordNHovExceedingNotFullyFreeThresholdIfApplicable();
    // Latch outside-nhov only from the /n HG cost branch's uncapped candidate,
    // matching the achievement condition text.
    this.recordOutsideNhovIfApplicable();
  }

  private recordOutsideNhovIfApplicable(): void {
    if (this.cumulativeStats.hasExecutedOutsideNhov) {
      return;
    }
    if (
      this.doesNHovHgDiscountCandidateReachNumberLimit(
        this.nHovState.branchExperienceByBranch,
      )
    ) {
      this.recordOutsideNhov();
    }
  }

  private doesNHovHgDiscountCandidateReachNumberLimit(
    branchExperienceByBranch: NHovBranchExperienceByBranch,
  ): boolean {
    const candidateLevels = resolveNHovBranchContinuousLevelsFromExperience(
      branchExperienceByBranch,
    );
    const hgDiscountCandidate = calculateNHovHgCostDivisorHuge(
      candidateLevels.hgDiscount,
      this.resetBalance.nHov.branches.hgDiscount,
    );
    return doesNHovHgDiscountCostDivisorReachNumberLimit(hgDiscountCandidate);
  }

  private recordNewHovWithoutMergeHov(): void {
    if (this.cumulativeStats.hasExecutedNewHovWithoutMergeHov) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      hasExecutedNewHovWithoutMergeHov: true,
    };
  }

  private recordNHovExceedingNotFullyFreeThresholdIfApplicable(): void {
    if (this.cumulativeStats.hasExecutedNHovExceedingNotFullyFreeThreshold) {
      return;
    }
    const divisor = this.getNHovBranchEffects().hgDiscountCostDivisor;
    if (compareHuge(divisor, NOT_FULLY_FREE_HG_COST_DIVISOR_THRESHOLD) <= 0) {
      return;
    }
    this.cumulativeStats = {
      ...this.cumulativeStats,
      hasExecutedNHovExceedingNotFullyFreeThreshold: true,
    };
  }

  executeOHov(source: OHovExecutionSource = "manual"): boolean {
    if (!this.canExecuteOHovReset()) {
      return false;
    }

    const reward = this.calculateOHovResetReward();
    const plan = this.selectOHovResetPlan(reward);

    this.applyOHovResetPlanToState(plan);
    this.finishActiveWallOnOHovClear();
    this.recordAnyResetNow();
    this.lastOHovExecutionSource = source;

    return true;
  }

  consumeLastOHovExecutionSource(): OHovExecutionSource {
    const source = this.lastOHovExecutionSource;
    this.lastOHovExecutionSource = "unknown";
    return source;
  }

  private recordAnyResetNow(): number {
    const nowMs = Math.max(0, this.nowMs());
    this.lastAnyResetAtMs = nowMs;
    this.gameplayElapsedSinceAnyResetMs = 0;
    // MHov / NHov / OHov / wall-start all funnel through this hook and all
    // count as Hov-run boundaries for the Freshness Wall. Reset the freshness
    // elapsed counter so the same wall-active modifier applies to each fresh
    // sub-run. No-op when freshness staling is not currently armed.
    this.wallState = resetFreshnessElapsedInState(this.wallState);
    return nowMs;
  }

  private canExecuteOHovReset(): boolean {
    return this.canExecuteOHovResetAtHov(this.hov);
  }

  private canExecuteOHovResetAtHov(hov: HugeNumber): boolean {
    return canExecuteOHov({
      hov,
      targetHov: this.getOHovTargetCache().executionTargetHovHuge,
    });
  }

  private resolveOHovExecutionTargetHov(): string {
    return this.getOHovTargetCache().executionTargetHov;
  }

  private getOHovTargetCache(): NonNullable<HoverIdleModel["oHovTargetCache"]> {
    const cached = this.oHovTargetCache;
    if (
      cached !== null &&
      cached.wallState === this.wallState &&
      cached.wallRewardCapLevel === this.wallRewardCapLevel
    ) {
      return cached;
    }
    const executionTargetHov = resolveActiveWallClearTargetHov(this.wallState);
    const builtInTarget = resolveBuiltInOHovAutoExecutionTarget({
      activeWallTargetHov:
        this.wallState.activeWallId === null ? null : executionTargetHov,
      outerWallInfluenceTargetHov: resolveOuterWallBuiltInOHovAutoTargetHov(
        this.wallState,
        this.wallRewardCapLevel,
      ),
    });
    const next = {
      wallState: this.wallState,
      wallRewardCapLevel: this.wallRewardCapLevel,
      builtInTarget,
      builtInTargetHov: huge(builtInTarget.targetHov),
      executionTargetHov,
      executionTargetHovHuge: huge(executionTargetHov),
    };
    this.oHovTargetCache = next;
    return next;
  }

  private calculateOHovResetReward(): OHovResetReward {
    return {
      powerGain: this.computePowerGainForOHov(),
      hovCoreGain: this.computeHovCoreGainForOHov(),
    };
  }

  private selectOHovResetPlan(reward: OHovResetReward): OHovResetPlan {
    return resolveOHovResetPlan({
      currentOHovState: this.oHovState,
      nowMs: this.nowMs(),
      currentRunGameElapsedMs: this.oHovState.records.currentRunGameElapsedMs,
      powerGain: reward.powerGain,
      hovCoreGain: reward.hovCoreGain,
    });
  }

  private computePowerGainForOHov(): HugeNumber {
    return hugeOne();
  }

  private computeHovCoreGainMultiplierForOHov(): HugeNumber {
    const cached = this.oHovHovCoreGainMultiplierCache;
    if (
      cached !== null &&
      cached.oHovPower === this.oHovState.power &&
      cached.mpmState === this.mpmState &&
      cached.mpCapacity === this.mpCapacity &&
      cached.skillAcquired === this.skillOHovHovCoreGainMultiplierUnlocked &&
      cached.achievementScalingUnlocked ===
        this.achievementOHovHovCoreGainScalingUnlocked &&
      cached.mpCapacityCoreAcquired ===
        this.skillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked &&
      cached.freeMpCoreAcquired ===
        this.skillFreeMpCoreOHovHovCoreGainMultiplierUnlocked
    ) {
      return cached.multiplier;
    }
    const multiplier = resolveOHovHovCoreGainMultiplier({
      skillAcquired: this.skillOHovHovCoreGainMultiplierUnlocked,
      achievementScalingUnlocked:
        this.achievementOHovHovCoreGainScalingUnlocked,
      oHovPower: this.oHovState.power,
      mpCapacityCoreAcquired:
        this.skillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked,
      freeMpCoreAcquired: this.skillFreeMpCoreOHovHovCoreGainMultiplierUnlocked,
      mpCapacity: this.mpCapacity,
      freeMp: computeMpmAvailableMpFromOccupancy({
        mpCapacity: this.mpCapacity,
        state: this.mpmState,
      }),
    });
    this.oHovHovCoreGainMultiplierCache = {
      oHovPower: this.oHovState.power,
      mpmState: this.mpmState,
      mpCapacity: this.mpCapacity,
      skillAcquired: this.skillOHovHovCoreGainMultiplierUnlocked,
      achievementScalingUnlocked:
        this.achievementOHovHovCoreGainScalingUnlocked,
      mpCapacityCoreAcquired:
        this.skillMpCapacityCoreOHovHovCoreGainMultiplierUnlocked,
      freeMpCoreAcquired: this.skillFreeMpCoreOHovHovCoreGainMultiplierUnlocked,
      multiplier,
    };
    return multiplier;
  }

  private computeHovCoreGainForOHov(
    multiplier: HugeNumber = this.computeHovCoreGainMultiplierForOHov(),
  ): HugeNumber {
    return this.computeHovCoreGainForOHovAtHov(this.hov, multiplier);
  }

  private computeHovCoreGainForOHovAtHov(
    hov: HugeNumber,
    multiplier: HugeNumber = this.computeHovCoreGainMultiplierForOHov(),
  ): HugeNumber {
    // Preview, manual, MP, wall, and built-in auto execution all finalize the
    // same continuous base only after every HovCore multiplier is applied.
    const baseGain =
      this.mpmState.ohovExecution.unlocked ||
      this.wallState.activeWallId !== null
        ? resolveOHovHovCoreBaseGainForHov(hov)
        : hugeOne();
    return resolveOHovHovCoreAwardedGain({
      baseGain: compareHuge(baseGain, hugeOne()) > 0 ? baseGain : hugeOne(),
      multiplier,
    });
  }

  private applyMHovResetPlanToState(plan: MHovResetPlan): void {
    // Preserve the historical order: MHov grant happens before the shared
    // progress scope, while the scope currently treats `mHovState: "grant"` as
    // a layer-specific mutation owned by this grant helper.
    this.applyMHovResetGrant(plan);
    this.applyHoverResetProgressScope(plan.progress);
    this.applyHoverResetDerivedSync(plan);
  }

  private applyNHovResetPlanToState(plan: NHovResetPlan): void {
    this.applyHoverResetProgressScope(plan.progress);
    this.applyNHovResetGrant(plan);
    this.applyHoverResetDerivedSync(plan);
  }

  private applyOHovResetPlanToState(plan: OHovResetPlan): void {
    this.applyHoverResetProgressScope(plan.progress);
    this.applyOHovResetGrant(plan);
    this.applyHoverResetRuntimeEffects(plan);
    this.applyHoverResetDerivedSync(plan);
  }

  private applyHoverResetProgressScope(scope: HoverResetProgressScope): void {
    const next = applyHoverResetProgressScopeToGroups(
      scope,
      this.collectHoverResetProgressStateGroups(),
      {
        baseUpgradeCost: this.baseUpgradeCost,
        baseTimUpgradeCost: this.baseTimUpgradeCost,
      },
    );
    this.adoptHoverResetProgressStateGroups(next);
    if (scope.hovTargetGrid === "reset-to-default") {
      this.clearTimHovConsumptionObservation();
    }
  }

  private collectHoverResetProgressStateGroups(): HoverResetProgressStateGroups {
    return {
      hov: this.hov,
      hiddenHovGainBonus: this.hiddenHovGainBonus,
      purchasedHovGainUpgrades: this.purchasedHovGainUpgrades,
      hovUpgradeCost: this.upgradeCost,
      timUpgradeCost: this.timUpgradeCost,
      hovTargetGrid: {
        hovGridTier: this.hovGridTier,
        hovHoveringCellIndex: this.hovHoveringCellIndex,
        isHoveringHov: this.isHoveringHov,
        hoverIdleEffectUnlocks: this.hoverIdleEffectUnlocks,
        hovCells: this.hovCells,
      },
      hovGridThemeHue: this.hovGridThemeHue,
      mHovState: this.mHovState,
      nHovState: this.nHovState,
      secretProgress: {
        continuousPointerSeconds: this.continuousPointerSeconds,
        observerSeconds: this.observerSeconds,
        xPatternSeconds: this.xPatternSeconds,
        hPatternSeconds: this.hPatternSeconds,
        hovOLabelHoverSeconds: this.hovOLabelHoverSeconds,
        hovCellClickTimestamps: this.hovCellClickTimestamps,
      },
      hovAmplifier: {
        hovAmplifierState: this.hovAmplifierState,
        hovAmplifierRecentChargeSources: this.hovAmplifierRecentChargeSources,
        hovAmplifierRecentDirectChargeAtMs:
          this.hovAmplifierRecentDirectChargeAtMs,
        hovAmplifierGaugeVisualSegments: this.hovAmplifierGaugeVisualSegments,
        hovAmplifierVisualChargeBatch: this.hovAmplifierVisualChargeBatch,
      },
    };
  }

  private adoptHoverResetProgressStateGroups(
    next: HoverResetProgressStateGroups,
  ): void {
    this.hov = next.hov;
    this.hiddenHovGainBonus = next.hiddenHovGainBonus;
    this.purchasedHovGainUpgrades = next.purchasedHovGainUpgrades;
    this.upgradeCost = next.hovUpgradeCost;
    this.timUpgradeCost = next.timUpgradeCost;
    this.hovGridTier = next.hovTargetGrid.hovGridTier;
    this.hovHoveringCellIndex = next.hovTargetGrid.hovHoveringCellIndex;
    this.isHoveringHov = next.hovTargetGrid.isHoveringHov;
    this.hoverIdleEffectUnlocks = next.hovTargetGrid.hoverIdleEffectUnlocks;
    this.hovCells = next.hovTargetGrid.hovCells;
    this.hovGridThemeHue = next.hovGridThemeHue;
    this.mHovState = next.mHovState;
    this.nHovState = next.nHovState;
    this.continuousPointerSeconds =
      next.secretProgress.continuousPointerSeconds;
    this.observerSeconds = next.secretProgress.observerSeconds;
    this.xPatternSeconds = next.secretProgress.xPatternSeconds;
    this.hPatternSeconds = next.secretProgress.hPatternSeconds;
    this.hovOLabelHoverSeconds = next.secretProgress.hovOLabelHoverSeconds;
    this.hovCellClickTimestamps = next.secretProgress.hovCellClickTimestamps;
    this.hovAmplifierState = next.hovAmplifier.hovAmplifierState;
    this.hovAmplifierRecentChargeSources =
      next.hovAmplifier.hovAmplifierRecentChargeSources;
    this.hovAmplifierRecentDirectChargeAtMs =
      next.hovAmplifier.hovAmplifierRecentDirectChargeAtMs;
    this.hovAmplifierGaugeVisualSegments =
      next.hovAmplifier.hovAmplifierGaugeVisualSegments;
    this.hovAmplifierVisualChargeBatch =
      next.hovAmplifier.hovAmplifierVisualChargeBatch;
  }

  private applyMHovResetGrant(plan: MHovResetPlan): void {
    const patch = applyMHovResetGrantToPatch(plan);
    this.mHovState = patch.mHovState;
  }

  private applyNHovResetGrant(plan: NHovResetPlan): void {
    const patch = applyNHovResetGrantToPatch(plan, {
      nHovState: this.nHovState,
    });
    this.hovGridThemeHue = patch.hovGridThemeHue;
    this.nHovState = patch.nHovState;
  }

  private applyOHovResetGrant(plan: OHovResetPlan): void {
    const patch = applyOHovResetGrantToPatch(plan, {
      oHovState: this.oHovState,
    });
    this.oHovState = patch.oHovState;
  }

  private applyHoverResetRuntimeEffects(plan: HoverResetPlan): void {
    if (plan.runtimeEffects.outsideResetSerial !== "advance") {
      return;
    }
    // Outside-family reset event marker. Any future outside-family reset must
    // bump this same field so the Outside Burst effect covers it too.
    this.outsideResetSerial += 1;
  }

  private applyHoverResetDerivedSync(plan: HoverResetPlan): void {
    if (plan.derivedSync.mainResourceGains) {
      this.syncMainResourceGains();
    }
    if (plan.derivedSync.thresholdUnlocks) {
      this.syncThresholdUnlocks();
    }
  }

  spendHov(amount: HugeNumberInput): boolean {
    const spend = assertFinitePositiveHugeInput(amount, "amount");
    this.syncThresholdUnlocks();
    if (compareHuge(this.hov, spend) < 0) return false;
    this.hov = this.hov.sub(spend);
    return true;
  }

  spendHovCore(amount: HugeNumberInput): boolean {
    const spend = assertFinitePositiveHugeInput(amount, "amount");
    if (compareHuge(this.oHovState.hovCore, spend) < 0) return false;
    this.oHovState = {
      ...this.oHovState,
      hovCore: this.oHovState.hovCore.sub(spend),
    };
    this.cumulativeStats = {
      ...this.cumulativeStats,
      totalHovCoreSpent: this.cumulativeStats.totalHovCoreSpent.add(spend),
    };
    return true;
  }

  purchaseHovAmplifierGainUpgrade(
    entryId: HovAmplifierEntryId,
  ): HovAmplifierUpgradePurchaseResult {
    if (
      !isHovAmplifierEntryPurchasable(
        entryId,
        this.resolveHovAmplifierEntryAvailabilityContext(),
      )
    ) {
      return {
        ok: false,
        reason: "locked",
        snapshot: this.getSnapshot(),
      };
    }
    const cost = resolveHovAmplifierUpgradeCostForEntry(
      this.hovAmplifierState,
      entryId,
    );
    if (cost === null) {
      return {
        ok: false,
        reason: "unknown-entry",
        snapshot: this.getSnapshot(),
      };
    }
    if (!this.spendHovCore(cost)) {
      return {
        ok: false,
        reason: "insufficient-hov-core",
        snapshot: this.getSnapshot(),
      };
    }
    this.hovAmplifierState = applyHovAmplifierEnhancementPurchase(
      this.hovAmplifierState,
      entryId,
    );
    this.cumulativeStats = {
      ...this.cumulativeStats,
      amplifierUpgradesPurchased:
        this.cumulativeStats.amplifierUpgradesPurchased + 1,
    };
    return {
      ok: true,
      snapshot: this.getSnapshot(),
    };
  }

  getResetAutomationMinimumGameplayIntervalCarryMs(): HoverIdleResetAutomationState["minimumGameplayIntervalCarryMs"] {
    return {
      ...this.resetAutomationState.minimumGameplayIntervalCarryMs,
    };
  }

  exportState(): HoverIdleModelState {
    const unlocks = this.syncThresholdUnlocks();
    this.syncTimRootCellState();

    return {
      economy: {
        hov: this.hov,
        tim: this.tim,
        fai: this.fai,
        hovGainPerSecond: this.hovGainPerSecond,
        timGainPerSecond: this.timGainPerSecond,
        hiddenGrowth: {
          hovGainBonus: this.hiddenHovGainBonus,
          timGainBonus: this.hiddenTimGainBonus,
          hovTimProgress: this.hiddenHovTimProgress,
        },
        upgradeCost: this.upgradeCost,
        timUpgradeCost: this.timUpgradeCost,
        purchasedUpgrades: {
          hovGain: this.purchasedHovGainUpgrades,
          timGain: this.purchasedTimGainUpgrades,
        },
        unlocks: { ...unlocks },
        prestige: { ...this.prestige },
        targetGrid: {
          gridTier: this.hovGridTier,
          themeHue: this.hovGridThemeHue,
          hovCells: cloneHovCells(this.hovCells),
        },
        minHovGainUnlockCostsByTargetIndex: [
          ...this.minHovGainUnlockCostsByTargetIndex,
        ],
        minHovGainMilestoneTriggerCostsByMilestoneIndex: [
          ...this.minHovGainMilestoneTriggerCostsByMilestoneIndex,
        ],
        timTargetGrid: {
          gridTier: this.timGridTier,
          timCells: cloneTimCells(this.timCells),
        },
        hovTimBalance: { ...this.hovTimBalance },
        isTimHovActivationEnabled: this.isTimHovActivationEnabled,
        mHov: { ...this.mHovState },
        nHov: cloneNHovState(this.nHovState),
        oHov: cloneOHovState(this.oHovState),
        resetAutomation: cloneResetAutomationState(this.resetAutomationState),
        lastAnyResetAtMs: this.lastAnyResetAtMs,
        mpm: this.createMpmStateForExport(),
        hovAmplifier: this.hovAmplifierState,
        wall: cloneWallState(this.wallState),
        exploration: cloneExplorationState(this.explorationState),
      },
      settings: cloneSettingsState(this.settings),
      cumulativeStats: { ...this.cumulativeStats },
      payment: clonePaymentState(this.payment),
    };
  }

  restoreState(state: HoverIdleModelState): void {
    const mpmBefore = this.mpmState;
    this.lastOHovAutoExecutePreSnapshot = null;
    this.lastOHovMpExecuteBeforeSnapshot = null;
    this.lastMHovAutoExecuteBeforeSnapshot = null;
    this.lastNHovAutoExecute = null;
    this.lastAutoResetExecuteBeforeSnapshots = {
      mHov: null,
      nHov: null,
      oHov: null,
    };
    this.lastAutoResetExecutionEvents = [];
    this.lastHovGainUpgradeMpAutomationEvents = [];
    this.lastResetAutomationDrainDiagnostic = null;
    this.gameplayAutomationEventSequence = 0;
    this.hovGainUpgradeMpAutomationCadences.clear();
    const restore = normalizeHoverIdleModelRestoreState({
      state,
      fallbackNowMs: this.nowMs(),
      existingMpmHovGainUpgradesUnlocked:
        this.mpmState.hovGainUpgrades.unlocked,
      existingMpmAmplifierPanelsUnlocked:
        this.mpmState.amplifierPanels.unlocked,
      existingMpmAmplifierUpgradesUnlocked:
        this.mpmState.amplifierUpgrades.unlocked,
      existingMpmMHovExecutionUnlocked: this.mpmState.mhovExecution.unlocked,
      existingMpmNHovExecutionUnlocked: this.mpmState.nhovExecution.unlocked,
      existingMpmOHovExecutionUnlocked: this.mpmState.ohovExecution.unlocked,
      mpCapacityInitialized: this.mpCapacityInitialized,
    });
    const normalizedState = restore.state;
    const syncPlan = restore.postRestoreSyncPlan;

    assertHoverIdleEconomyState(normalizedState.economy);
    assertHoverIdleSettingsState(normalizedState.settings);
    assertHoverIdleCumulativeStatsState(normalizedState.cumulativeStats);
    assertHoverIdlePaymentState(restore.paymentState);
    this.payment = clonePaymentState(restore.paymentState);
    this.localEntitlementSnapshot = createEntitlementSnapshotFromPaymentState(
      this.payment,
    );

    this.hov = normalizedState.economy.hov;
    this.tim = normalizedState.economy.tim;
    this.fai = normalizedState.economy.fai;
    this.upgradeCost = normalizedState.economy.upgradeCost;
    this.timUpgradeCost = normalizedState.economy.timUpgradeCost;
    this.purchasedHovGainUpgrades =
      normalizedState.economy.purchasedUpgrades.hovGain;
    this.purchasedTimGainUpgrades =
      normalizedState.economy.purchasedUpgrades.timGain;
    this.hiddenHovGainBonus = normalizedState.economy.hiddenGrowth.hovGainBonus;
    this.hiddenTimGainBonus = normalizedState.economy.hiddenGrowth.timGainBonus;
    this.hiddenHovTimProgress =
      normalizedState.economy.hiddenGrowth.hovTimProgress;
    this.mHovState = { ...normalizedState.economy.mHov };
    this.nHovState = cloneNHovState(normalizedState.economy.nHov);
    this.oHovState = cloneOHovState(normalizedState.economy.oHov);
    this.resetAutomationState = normalizeResetAutomationState(
      normalizedState.economy.resetAutomation,
    );
    this.lastAnyResetAtMs = normalizedState.economy.lastAnyResetAtMs;
    this.gameplayElapsedSinceAnyResetMs = Math.max(
      0,
      this.nowMs() - this.lastAnyResetAtMs,
    );
    this.hovAmplifierState = normalizeHovAmplifierState(
      normalizedState.economy.hovAmplifier,
    );
    this.clearHovAmplifierRecentChargeSources();
    this.clearHovAmplifierRecentDirectCharges();
    this.clearHovAmplifierGaugeVisualSegments();
    this.mpmState = cloneMpmState(
      normalizedState.economy.mpm ?? createDefaultMpmState(),
    );
    const restoredExplorationState = cloneExplorationState(
      normalizedState.economy.exploration ?? createDefaultExplorationState(),
    );
    this.explorationState =
      this.explorationTimAccelerationUnlocked ||
      !restoredExplorationState.timAccelerationEnabled
        ? restoredExplorationState
        : { ...restoredExplorationState, timAccelerationEnabled: false };
    this.explorationPointerBiomeId = null;
    this.mpmState = reconcileExplorationAssignmentsToPurchasedSlots({
      state: this.explorationState,
      definition: this.explorationDefinition,
      mpmState: this.mpmState,
    });
    this.achievementMpmUnlockIntent = createNeutralMpmUnlockIntent();
    this.hovSkillMpmUnlockIntent = createNeutralMpmUnlockIntent();
    this.wallState = normalizeWallState(normalizedState.economy.wall);
    // Compose the restored wall `mp-capacity` addend into the effective cap
    // before latchWallMpmUnlocks() runs its capacity trim. Otherwise the trim
    // sees only the pre-restore base capacity and strips wall-addend-funded
    // assignments (OHov / Amp Panel / Amp Upgrade). No-op until mp capacity is
    // initialized, so the uninitialized restore path is unchanged.
    this.recomputeEffectiveMpCapacity();
    this.latchWallMpmUnlocks();
    const hoverIncomeMultiplier = this.resolveHovIncomeMultiplier();
    const timIncomeMultiplier = this.resolveTimIncomeMultiplier();
    const hovGainMultiplier = this.mHovState.hgMultiplier
      .mul(hoverIncomeMultiplier)
      .mul(this.hovGainCountMultiplier)
      .mul(this.hovGainMultiplier);
    const restoredBaseHovGain =
      normalizedState.economy.hovGainPerSecond.div(hovGainMultiplier);
    const knownHovGain = this.resolveKnownBaseHovGainPerSecond();
    const hovUpgradeMilestoneMultiplier =
      this.resolveHovUpgradeMilestoneMultiplier();
    this.skillHovGainPerSecond = maxHuge(
      hugeZero(),
      restoredBaseHovGain.sub(knownHovGain).div(hovUpgradeMilestoneMultiplier),
    );
    const restoredBaseTimGain =
      normalizedState.economy.timGainPerSecond.div(timIncomeMultiplier);
    const knownTimGain = this.baseTimGainPerSecond
      .add(this.timUpgradeGainIncrease.mul(this.purchasedTimGainUpgrades))
      .add(this.hiddenTimGainBonus.mul(this.achievementTimGainCountMultiplier));
    this.skillTimGainPerSecond = maxHuge(
      hugeZero(),
      restoredBaseTimGain.sub(knownTimGain),
    );
    if (syncPlan.recomputeMainResourceGains) {
      this.syncMainResourceGains();
    }
    if (syncPlan.resolveUnlockState) {
      this.hovTabUnlocks = resolveHovTabUnlockState({
        existing: normalizedState.economy.unlocks.tabs,
        hov: this.hov,
        tim: this.tim,
        hovCore: this.oHovState.hovCore,
        currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
      });
      this.hoverIdleEffectUnlocks = resolveHoverIdleEffectUnlockState({
        existing: normalizedState.economy.unlocks.effects,
        currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
      });
    }
    this.prestige = { ...normalizedState.economy.prestige };
    this.settings = cloneSettingsState(normalizedState.settings);
    this.cumulativeStats = { ...normalizedState.cumulativeStats };
    this.isHoveringHov = false;
    this.isHoveringTim = false;
    this.hovAmplifierHoveringEntryId = null;
    this.hovGridTier = normalizedState.economy.targetGrid.gridTier;
    this.hovGridThemeHue = normalizeNHovThemeHue(
      normalizedState.economy.targetGrid.themeHue,
    );
    this.hovHoveringCellIndex = null;
    this.clearTimHovConsumptionObservation();
    this.hovCells = cloneHovCells(normalizedState.economy.targetGrid.hovCells);
    this.minHovGainUnlockCostsByTargetIndex = [
      ...normalizedState.economy.minHovGainUnlockCostsByTargetIndex,
    ];
    this.minHovGainMilestoneTriggerCostsByMilestoneIndex = [
      ...normalizedState.economy
        .minHovGainMilestoneTriggerCostsByMilestoneIndex,
    ];
    this.timGridTier = normalizedState.economy.timTargetGrid.gridTier;
    this.timHoveringCellIndex = null;
    const restoredTimCells = cloneTimCells(
      normalizedState.economy.timTargetGrid.timCells,
    );
    for (let i = 0; i < TIM_CELL_COUNT; i += 1) {
      this.timCells[i] = restoredTimCells[i];
    }
    if (syncPlan.syncTimRootCellState) {
      this.syncTimRootCellState();
    }
    this.hovTimBalance = { ...normalizedState.economy.hovTimBalance };
    this.isTimHovActivationEnabled =
      normalizedState.economy.isTimHovActivationEnabled;
    if (syncPlan.trimMpAssignmentsToCapacity) {
      // Wall addend on `mp-capacity` may have shifted with the restored
      // wallState, so recompose the effective capacity before trimming so the
      // trim reflects the post-restore authoritative cap.
      this.recomputeEffectiveMpCapacity();
      this.trimMpAssignmentsToCapacity();
    }
    this.mpmState = this.createMpmStateForExport();
    this.continuousPointerSeconds = 0;
    this.observerSeconds = 0;
    this.xPatternSeconds = 0;
    this.hPatternSeconds = 0;
    this.hovOLabelHoverSeconds = 0;
    this.hovCellClickTimestamps = [];
    this.isHoveringHovOLabel = false;
    if (syncPlan.syncThresholdUnlocks) {
      this.syncThresholdUnlocks();
    }
    this.notifyMpmNumericSettingsChanges(mpmBefore);
  }

  private createMpmStateForExport(): MpmState {
    return cloneMpmState(this.mpmState);
  }

  getHovEarnedThisTick(): HugeNumber {
    return this.hovEarnedThisTick;
  }

  getSnapshot(): HoverIdleSnapshot {
    // Two-phase: prepareSnapshotState() is the only mutating step; everything
    // passed into buildHoverIdleSnapshot(...) below is a read-only view.
    const prepared = this.prepareSnapshotState();
    return buildHoverIdleSnapshot({
      state: this.createSnapshotBuildState(),
      synced: prepared.synced,
      transient: prepared.transient,
      lightweight: this.resolveSnapshotLightweightDerivations(),
      read: this.snapshotReadHelpers,
      exploration: this.getExplorationSnapshot(),
    });
  }

  /**
   * Authoritative mutation boundary for `getSnapshot()`.
   *
   * Latches threshold unlocks, syncs the Tim root cell, and prunes transient
   * hover history so the DTO build below sees a consistent state. This is the
   * one place where reading a snapshot is allowed to mutate owned state.
   */
  private prepareSnapshotState(): HoverIdleSnapshotPreparation {
    const synced = this.syncStateBeforeSnapshot();
    const transient = this.pruneTransientHistoryBeforeSnapshot();
    return { synced, transient };
  }

  private syncStateBeforeSnapshot(): HoverIdleSnapshotSyncedState {
    const unlocks = this.syncThresholdUnlocks();
    this.syncTimRootCellState();
    return { unlocks };
  }

  private resolveSnapshotLightweightDerivations(): HoverIdleSnapshotLightweightDerivations {
    // Keep regular snapshots to cheap previews and availability checks.
    const hovGainPipeline = this.resolveCurrentHovGainPipeline();
    const effectiveHovGainPerSecond = hovGainPipeline.finalHovGainPerSecond;
    const effectiveHovGainPerSecondAfterUpgrade =
      this.resolveDisplayedHovGainPerSecond(this.purchasedHovGainUpgrades + 1);
    const hovUpgradeCost = this.getEffectiveUpgradeCost();
    return {
      effectiveHovGainPerSecond,
      effectiveHovGainPerSecondAfterUpgrade,
      hovUpgradeCost,
      hovUpgradeOneCostBatchPreview:
        this.createGlobalHovGainUpgradeOneCostBatchSummary({
          effectiveCost: hovUpgradeCost,
        }),
      hovUpgradeSimultaneousPurchasePreview:
        this.createGlobalHovGainUpgradeSimultaneousPurchasePreview({
          effectiveCost: hovUpgradeCost,
        }),
      canPurchaseHovUpgrade: compareHuge(this.hov, hovUpgradeCost) >= 0,
      canPurchaseTimUpgrade: this.canPurchaseTimUpgrade(),
    };
  }

  private createSnapshotBuildState(): HoverIdleSnapshotBuildState {
    return {
      hov: this.hov,
      tim: this.tim,
      fai: this.fai,
      timGainPerSecond: this.timGainPerSecond,
      timUpgradeCost: this.timUpgradeCost,
      timUpgradeGainIncrease: this.timUpgradeGainIncrease,
      purchasedHovGainUpgrades: this.purchasedHovGainUpgrades,
      isHoveringHov: this.isHoveringHov,
      isHoveringTim: this.isHoveringTim,
      hovTimBalance: this.hovTimBalance,
      cumulativeStats: this.cumulativeStats,
      hovGridTier: this.hovGridTier,
      hovGridThemeHue: this.hovGridThemeHue,
      hovHoveringCellIndex: this.hovHoveringCellIndex,
      timHovConsumingCellIndex: this.timHovConsumingCellIndex,
      timHovConsumptionRatePerSecond: this.timHovConsumptionRatePerSecond,
      hovCells: this.hovCells,
      minHovGainUnlockCostsByTargetIndex:
        this.minHovGainUnlockCostsByTargetIndex,
      minHovGainMilestoneTriggerCostsByMilestoneIndex:
        this.minHovGainMilestoneTriggerCostsByMilestoneIndex,
      timGridTier: this.timGridTier,
      timHoveringCellIndex: this.timHoveringCellIndex,
      timCells: this.timCells,
      mpCapacity: this.mpCapacity,
      mpmState: this.mpmState,
      continuousPointerSeconds: this.continuousPointerSeconds,
      observerSeconds: this.observerSeconds,
      xPatternSeconds: this.xPatternSeconds,
      hPatternSeconds: this.hPatternSeconds,
      hovOLabelHoverSeconds: this.hovOLabelHoverSeconds,
      isTimHovActivationEnabled: this.isTimHovActivationEnabled,
      resetBalance: this.resetBalance,
      mHovState: this.mHovState,
      nHovState: this.nHovState,
      oHovState: this.oHovState,
      oHovHovCoreGainPreview: this.computeHovCoreGainForOHov(),
      outsideResetSerial: this.outsideResetSerial,
      hovAmplifierState: this.hovAmplifierState,
      hovAmplifierRecentChargeSources: this.hovAmplifierRecentChargeSources,
      hovAmplifierRecentDirectChargeAtMs:
        this.hovAmplifierRecentDirectChargeAtMs,
      hovAmplifierGaugeVisualSegments: this.hovAmplifierGaugeVisualSegments,
      hovAmplifierGainMultipliersByEntry:
        this.resolveAmplifierGainMultipliersByEntry(),
      hovAmplifierApExponentBreakdown:
        this.resolveAmplifierApExponentBonusBreakdown(),
      bhgToHgPowerExponentBonus: this.bhgToHgPowerExponentBonus,
      hngRankScaleBaseBonus: this.hngRankScaleBaseBonus,
      mhovGrowthExponentBonus: this.growthExponentBonus.mhov,
      nHovGrowthExponentBonus: this.resolveNHovGrowthExponentBonus(),
      mhovRewardUncapped: this.achievementMhovRewardUncapped,
      nhovBranchEffectUncapped: this.achievementNhovBranchEffectUncapped,
      wallState: this.wallState,
      wallUnlocked: this.skillWallUnlocked,
      wallRewardCapLevel: this.wallRewardCapLevel,
      nowMs: () => this.nowMs(),
    };
  }

  assignHovTimBalance(target: HovTimBalanceTarget): boolean {
    const nextBalance = assignHovTimBalancePoint(this.hovTimBalance, target);
    if (nextBalance === null) return false;
    this.hovTimBalance = nextBalance;
    return true;
  }

  unassignHovTimBalance(target: HovTimBalanceTarget): boolean {
    const nextBalance = unassignHovTimBalancePoint(this.hovTimBalance, target);
    if (nextBalance === null) return false;
    this.hovTimBalance = nextBalance;
    return true;
  }

  setHovTimBalanceRatio(pair: HovTimBalancePair, ratio: number): boolean {
    const nextBalance = setHovTimBalancePairRatio(
      this.hovTimBalance,
      pair,
      ratio,
    );
    if (nextBalance === null) return false;
    this.hovTimBalance = nextBalance;
    return true;
  }

  spendTim(amount: HugeNumberInput): boolean {
    const spend = assertFinitePositiveHugeInput(amount, "amount");
    this.syncThresholdUnlocks();
    if (compareHuge(this.tim, spend) < 0) return false;
    this.tim = this.tim.sub(spend);
    return true;
  }

  private canPurchaseTimUpgrade(): boolean {
    return compareHuge(this.tim, this.timUpgradeCost) >= 0;
  }

  private shouldRefineTimToHov(): boolean {
    return !this.isTimHovActivationUnlocked || this.isTimHovActivationEnabled;
  }

  // The control gate is cheap, so it runs before the grid scan.
  // `precomputedEligibility` lets a projection probe reuse the elapsed-invariant
  // resolution already stored in its inputs.
  private shouldActivateTimHov(
    baseGainPerSecond: HugeNumber,
    precomputedEligibility: boolean | null = null,
  ): boolean {
    if (!this.shouldRefineTimToHov()) return false;
    return (
      precomputedEligibility ??
      this.resolveTimHovActivationEligibility(baseGainPerSecond)
    );
  }

  /**
   * TimHov activation stays on the hovered target and never transfers, so it is
   * allowed only while that target's HnG ties the maximum among the targets the
   * player could hover (visible for the current tier and unlocked, matching
   * `setHoveringCellIndex`). The comparison reads pre-activation cell gain, so
   * the decision cannot depend on its own result. A hover without a specific
   * target index keeps the whole-board fallback `resolveHoveringCellGainPerSecond`
   * already applies.
   */
  private resolveTimHovActivationEligibility(
    baseGainPerSecond: HugeNumber,
    grid: {
      readonly hoveringCellIndex: number | null;
      readonly hovGridTier: HoverIdleGridTier;
      readonly cells: readonly HovCellState[];
    } = {
      hoveringCellIndex: this.hovHoveringCellIndex,
      hovGridTier: this.hovGridTier,
      cells: this.hovCells,
    },
  ): boolean {
    const hoveringCellIndex = grid.hoveringCellIndex;
    if (hoveringCellIndex === null) return true;
    const hoveredGainPerSecond = this.resolveCellGainPerSecond(
      hoveringCellIndex,
      baseGainPerSecond,
      grid.cells,
    );
    for (let index = 0; index < grid.cells.length; index += 1) {
      if (index === hoveringCellIndex) continue;
      if (!this.isCellVisibleForTier(index, grid.hovGridTier)) continue;
      if (!grid.cells[index]?.unlocked) continue;
      const gainPerSecond = this.resolveCellGainPerSecond(
        index,
        baseGainPerSecond,
        grid.cells,
      );
      if (compareHuge(gainPerSecond, hoveredGainPerSecond) > 0) {
        return false;
      }
    }
    return true;
  }

  private resolveHiddenHovGainGrowthPerSecond(): HugeNumber {
    return computeHiddenGainGrowthPerSecond({
      allocation: this.hovTimBalance.hovGainAllocation,
      growthPerAllocationPerSecond:
        HIDDEN_HOV_GAIN_GROWTH_PER_ALLOCATION_PER_SECOND,
    });
  }

  private resolveHiddenTimGainGrowthPerSecond(): HugeNumber {
    return computeHiddenGainGrowthPerSecond({
      allocation: this.hovTimBalance.timGainAllocation,
      growthPerAllocationPerSecond:
        HIDDEN_TIM_GAIN_GROWTH_PER_ALLOCATION_PER_SECOND,
    });
  }

  private getHovAmplifierHgMultiplier(): HugeNumber {
    return resolveHovAmplifierHgMultiplier(
      this.hovAmplifierState.ap,
      this.resolveEffectiveAmplifierApExponent(),
    );
  }

  private countUnlockedAmplifierKinds(): number {
    let count = 0;
    for (const definition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
      if (isHovAmplifierEntryUnlocked(this.hovAmplifierState, definition.id)) {
        count += 1;
      }
    }
    return count;
  }

  resolveAmplifierApExponentBonusBreakdown(): {
    readonly base: number;
    readonly apBoarExponentBonus: number;
    readonly apDeerExponentBonus: number;
    readonly apButterflyExponentBonus: number;
    readonly effective: number;
  } {
    const breakdown = resolveApExponentBonusBreakdown({
      apBoarExponentBonus: this.skillApBoarExponentBonus,
      apDeerExponentBonusAcquired: this.skillApDeerExponentBonusAcquired,
      apButterflyExponentBonusAcquired:
        this.skillApButterflyExponentBonusAcquired,
      unlockedAmplifierKindCount: this.countUnlockedAmplifierKinds(),
      ap: this.hovAmplifierState.ap,
    });
    const base = this.hovAmplifierState.apExponent;
    return {
      base,
      apBoarExponentBonus: breakdown.apBoarExponentBonus,
      apDeerExponentBonus: breakdown.apDeerExponentBonus,
      apButterflyExponentBonus: breakdown.apButterflyExponentBonus,
      effective: base + breakdown.total,
    };
  }

  private resolveEffectiveAmplifierApExponent(): number {
    return this.resolveAmplifierApExponentBonusBreakdown().effective;
  }

  private clearHovAmplifierRecentChargeSources(): void {
    this.hovAmplifierRecentChargeSources =
      createEmptyHovAmplifierRecentChargeSources();
  }

  private clearHovAmplifierRecentDirectCharges(): void {
    this.hovAmplifierRecentDirectChargeAtMs =
      createEmptyHovAmplifierRecentDirectChargeAtMs();
  }

  private clearHovAmplifierGaugeVisualSegments(): void {
    this.hovAmplifierGaugeVisualSegments =
      createEmptyHovAmplifierGaugeVisualSegments();
    this.hovAmplifierVisualChargeBatch = {};
  }

  private getEffectiveBhgToHgPowerExponent(): number {
    return (
      BHG_TO_HG_POWER_EXPONENT_BASE +
      this.bhgToHgPowerExponentBonus +
      getHgWallRewardExponentBonus(this.wallState)
    );
  }

  private resolveDisplayedHovGainPerSecond(
    purchasedUpgrades = this.purchasedHovGainUpgrades,
  ): HugeNumber {
    return this.resolveCurrentHovGainPipeline(purchasedUpgrades)
      .finalHovGainPerSecond;
  }

  private applyHiddenGainGrowth(dtSeconds: number): void {
    const hovGainBonus =
      this.resolveHiddenHovGainGrowthPerSecond().mul(dtSeconds);
    const timGainBonus =
      this.resolveHiddenTimGainGrowthPerSecond().mul(dtSeconds);
    if (!isPositiveHuge(hovGainBonus) && !isPositiveHuge(timGainBonus)) {
      return;
    }
    this.hiddenHovGainBonus = this.hiddenHovGainBonus.add(hovGainBonus);
    this.hiddenTimGainBonus = this.hiddenTimGainBonus.add(timGainBonus);
    this.syncMainResourceGains();
  }

  private applyHovTimGrowth(dtSeconds: number): void {
    if (!this.hovTabUnlocks.hovTimBalance) {
      return;
    }
    const growth = advanceHovTimGrowthProgress({
      currentProgress: this.hiddenHovTimProgress,
      gainPerSecond: HOV_TIM_GAIN_PER_SECOND,
      dtSeconds,
    });
    this.hiddenHovTimProgress = growth.nextProgress;
    if (growth.gainedPoints <= 0) {
      return;
    }
    this.hovTimBalance = addUnassignedHovTimBalancePoints(
      this.hovTimBalance,
      growth.gainedPoints,
    );
  }

  private syncMainResourceGains(): void {
    const hovGainPipeline = this.resolveCurrentHovGainPipeline();
    this.hovGainPerSecond = hovGainPipeline.bhgHovGainPerSecond;
    this.timGainPerSecond = this.computeTimGainPerSecond();
  }

  private computeTimGainPerSecond(): HugeNumber {
    const baseTim = this.baseTimGainPerSecond
      .add(this.timUpgradeGainIncrease.mul(this.purchasedTimGainUpgrades))
      .add(this.skillTimGainPerSecond)
      .add(this.hiddenTimGainBonus);
    return baseTim.mul(this.resolveTimIncomeMultiplier());
  }

  private getShopGainPercents(): {
    readonly hovPercent: number;
    readonly timPercent: number;
  } {
    const summary = this.localEntitlementSnapshot;
    return {
      hovPercent: Math.max(0, (summary.hoverIncomeMultiplier - 1) * 100),
      timPercent: Math.max(0, (summary.timIncomeMultiplier - 1) * 100),
    };
  }

  getShopGainPercentSummary(): {
    readonly hovPercent: number;
    readonly timPercent: number;
  } {
    return this.getShopGainPercents();
  }

  private syncLocalEntitlementSnapshot(): void {
    this.localEntitlementSnapshot = createEntitlementSnapshotFromPaymentState(
      this.payment,
    );
    this.syncMainResourceGains();
  }

  private getEffectiveEntitlementSnapshot(): EntitlementSnapshot {
    return mergeEntitlementSnapshots(
      this.localEntitlementSnapshot,
      this.ownerEntitlementSnapshot,
    );
  }

  private resolveHovIncomeMultiplier(): number {
    const entitlements = this.getEffectiveEntitlementSnapshot();
    return (
      entitlements.scoreIncomeMultiplier * entitlements.hoverIncomeMultiplier
    );
  }

  private resolveTimIncomeMultiplier(): number {
    const entitlements = this.getEffectiveEntitlementSnapshot();
    return (
      entitlements.scoreIncomeMultiplier * entitlements.timIncomeMultiplier
    );
  }

  private syncThresholdUnlocks(): HoverIdleUnlockState {
    if (!this.oHovState.isUnlocked && isOHovUnlocked(this.hov)) {
      this.oHovState = {
        ...this.oHovState,
        isUnlocked: true,
      };
    }
    this.hovTabUnlocks = resolveHovTabUnlockState({
      existing: this.hovTabUnlocks,
      hov: this.hov,
      tim: this.tim,
      hovCore: this.oHovState.hovCore,
      currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
    });
    this.hoverIdleEffectUnlocks = resolveHoverIdleEffectUnlockState({
      existing: this.hoverIdleEffectUnlocks,
      currentLoopHovEarned: this.mHovState.currentLoopHovEarned,
    });
    applyHoverIdleEffectUnlocks(
      this.hoverIdleEffectUnlocks,
      {
        setHovGridTier: (tier) => {
          this.setHovGridTier(tier);
        },
      },
      {
        suppressSecondGridIncrease: isSecondHovGridIncreaseBlockedByActiveWall(
          this.wallState,
        ),
      },
    );
    // The grid latch is monotonic; once both flags are latched the call below
    // can never change state, and it re-normalizes the whole MPM state, which
    // is too expensive for this per-tick path.
    const latchedGridUnlock = this.mpmState.gridUnlock.hov;
    if (!latchedGridUnlock.gridIncrease1 || !latchedGridUnlock.gridIncrease2) {
      this.mpmState = withMpmHovGridUnlockLatched(this.mpmState, {
        gridIncrease1:
          this.hoverIdleEffectUnlocks.hov.gridIncrease1 ||
          compareHuge(
            this.cumulativeStats.maxHov,
            HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
          ) >= 0,
        gridIncrease2:
          this.hoverIdleEffectUnlocks.hov.gridIncrease2 ||
          compareHuge(
            this.cumulativeStats.maxHov,
            HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
          ) >= 0,
      });
    }
    this.refreshMpmAssignAllLatch();
    return {
      tabs: this.hovTabUnlocks,
      effects: this.hoverIdleEffectUnlocks,
    };
  }
}
